import {getPosProvider, normalizeProvider, validatePosSnapshot} from "../../pos/providers";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import McTransferIntelligence from "../components/McTransferIntelligence";
import { formatTransferMetric } from "../utils/mcTransferIntelligence";
import { movementLabel } from "../utils/mcTransferRecommendation";
import { resolveDeadStockCost } from "../utils/mcDeadStockReport";
import "./mcImportEngine.css";
import {
  buildDefaultState,
  buildPreparedData,
  detectBestMapping,
  detectUniversalImportProfile,
  loadEngineState,
  parseCsv,
  saveEngineState,
  savePreparedRowsToSessionSeed,
  saveSessionDetailFromPayload,
  buildSessionPayloadFromEngine,
  buildDuplicateMergeSummary,
  upsertSessionRecord,
  archiveSessionRecord,
  loadSessionRecords,
  loadArchivedSessionRecords,
} from "../utils/mcEngineData";
import { buildProfileDisplayName, formatStoreLabel, normalizeStoreValue, getActiveStoreScope, isAdminProfile, canAccessAdminTabs, setAdminStoreView, useAdminStoreView, useCurrentMcProfile, getProfileAllowedSectionIds } from "../utils/profileAccess";
import { getMcActiveOrgId, mcGetItem, mcSetItem, mcSetCloudItem, pruneMorpheusImportDocsForScope } from "../utils/mcCloudSync";
import { recordMcTrafficCallableRequest, recordMcTrafficCallableResponse } from "../utils/mcTrafficProbe";
import { MC_SITE_EVENT, detectSiteHeaders, ensureSitesFromHeaders, loadSites, resolveSite } from "../utils/mcOrgData";
import { commitActiveInventorySnapshot, commitActiveInventoryAlias, loadActiveInventorySnapshot, loadActiveInventorySnapshotsForScopes, peekActiveInventorySnapshot, subscribeActiveInventorySnapshot, MC_ACTIVE_INVENTORY_EVENT } from "../utils/mcActiveInventory";
import { loadStorageMovementLedger, loadStorageSyncActivity, recordInventoryImportMovement } from "../utils/mcStorageMovementLedger";
import { withInventoryDeadline, runInventorySiteDistribution, yieldInventoryUi } from "../utils/mcInventoryOperation";
import { activeSections, formatSectionLabel, normalizeSectionId, activeCycles, formatCycleLabel, normalizeCycleId, MC_CYCLE_EVENT, MC_SECTION_EVENT } from "../utils/mcSections";
import {
  buildEngineFromPreparedInventory,
  loadPosSettings,
  loadPreparedInventory,
  normalizeInventoryRows,
  savePosSettings,
  savePreparedInventory,
} from "../utils/mcPosIntegration";

const MC_ACTIVITY_LOG_KEY = "mc-activity-log";
const MC_INTAKE_TRAFFIC_PROBE_ID = "workflow:intake";
const MC_DISTRIBUTION_HISTORY_KEY = "mc-global-distribution-history";

// An in-page navigation unmounts Intake, not the underlying organisation's work.
// This registry survives React route changes for the lifetime of the browser page.
// In-progress work cannot survive a full reload/browser close: the audit retains
// inventory_ready entries so the operator can resume safely, without re-upload.
const activeDistributionJobs = new Map();
function beginDistributionJob(orgId, batchId) {
  if (!orgId || activeDistributionJobs.has(orgId)) return false;
  activeDistributionJobs.set(orgId, batchId);
  return true;
}
function distributionJobIsCurrent(orgId, batchId) {
  return activeDistributionJobs.get(orgId) === batchId && getMcActiveOrgId() === orgId;
}
function releaseDistributionJob(orgId, batchId) {
  if (activeDistributionJobs.get(orgId) === batchId) activeDistributionJobs.delete(orgId);
}
function publicDistributionResult(result = {}) {
  const { revisionWork, ...safe } = result;
  return safe;
}
function revisionPending(result = {}) {
  return result.status === "inventory_ready" || result.status === "revision_pending";
}
function revisionRetryable(result = {}) {
  return ["failed", "revision_failed", "skipped", "queued", "inventory_ready", "revision_pending"].includes(result.status);
}

// Separate the slow revision-ledger commit from the inventory pointer commits.
// Never report a site's revision as ready until its head + sync history are confirmed.
function startBackgroundDistributionRevisions({ orgId, batch, inventoryResults, previousResults = null, onFinish = () => {} }) {
  const batchId = batch.batchId;
  const pending = inventoryResults.filter((item) => revisionPending(item) && typeof item.revisionWork === "function");
  const inventoryPublic = inventoryResults.map(publicDistributionResult);
  const original = Array.isArray(previousResults) ? previousResults : [];
  const compose = (updates) => {
    const bySite = new Map(updates.map((item) => [item.site, publicDistributionResult(item)]));
    const latest = inventoryPublic.map((item) => bySite.get(item.site) || item);
    if (!original.length) return latest;
    const processed = new Map(latest.map((item) => [item.site, item]));
    return original.map((item) => processed.get(item.site) || item);
  };
  const setAudit = (updates, final = false) => {
    if (!distributionJobIsCurrent(orgId, batchId)) return null;
    const results = compose(updates);
    const summary = summarizeDistribution(results);
    const status = !final ? "revisions_processing" : summary.failed ? "partial" : summary.skipped ? "completed_with_skips" : "completed";
    upsertDistributionBatch({ ...batch, results, ...summary, status, ...(final ? { completedAt: new Date().toISOString() } : {}) });
    return { results, summary, status };
  };
  // Persist recovery state before running any work. A refresh later cannot
  // silently turn these unfinished revisions into successes.
  setAudit([], false);
  const task = (async () => {
    let settled = [];
    try {
      if (pending.length) {
        settled = await runInventorySiteDistribution(pending, async (target, assertDeadline, reportStage) => {
          const assertRevisionActive = () => {
            assertDeadline();
            if (!distributionJobIsCurrent(orgId, batchId)) throw new Error("Workspace changed during revision processing.");
          };
          assertRevisionActive();
          const result = await target.revisionWork(assertRevisionActive, reportStage);
          assertRevisionActive();
          if (!result?.ok) throw new Error(result?.reason || "Site revision was not confirmed.");
          return { ...publicDistributionResult(target), status: "ready", ok: true, inventorySaved: true, revisionReady: true, reason: "" };
        }, {
          timeoutMs: 120000, concurrency: 2,
          onProgress: (progress) => {
            // Failed revision writes do not roll back already-committed inventory.
            const updates = progress.results.map((item) => item.status === "failed"
              ? { ...publicDistributionResult(item), status: "revision_failed", inventorySaved: true, revisionReady: false, ok: false }
              : item);
            setAudit(updates, false);
          },
        });
      }
      const updates = settled.map((item) => item.status === "failed"
        ? { ...publicDistributionResult(item), status: "revision_failed", inventorySaved: true, revisionReady: false, ok: false }
        : item);
      const final = setAudit(updates, true);
      onFinish(final);
      return final;
    } catch (error) {
      console.error("Morpheus background distribution revision error", { batchId, error });
      const failed = pending.map((item) => ({ ...publicDistributionResult(item), status: "revision_failed", ok: false, inventorySaved: true, revisionReady: false, reason: error?.message || "Revision not confirmed" }));
      onFinish(setAudit(failed, true));
      return null;
    } finally {
      releaseDistributionJob(orgId, batchId);
    }
  })();
  return task;
}

function loadDistributionHistory() {
  try {
    const raw = mcGetItem(MC_DISTRIBUTION_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function persistDistributionHistory(history = []) {
  const next = (Array.isArray(history) ? history : []).slice(0, 20);
  const serialized = JSON.stringify(next);
  try { mcSetItem(MC_DISTRIBUTION_HISTORY_KEY, serialized); } catch (_) {}
  mcSetCloudItem(MC_DISTRIBUTION_HISTORY_KEY, serialized);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("mc-distribution-history-updated", { detail: next }));
  }
  return next;
}

function upsertDistributionBatch(batch = {}) {
  const current = loadDistributionHistory();
  const id = String(batch?.batchId || "").trim();
  if (!id) return current;
  const existing = current.find((item) => item.batchId === id) || {};
  const merged = { ...existing, ...batch, batchId: id, updatedAt: new Date().toISOString() };
  return persistDistributionHistory([merged, ...current.filter((item) => item.batchId !== id)]);
}

const OPERATIONAL_COLUMNS = [
  { label: "Product", key: "name", fallback: "product_name" },
  { label: "Category", key: "product_category", fallback: "category" },
  { label: "Cost", key: "supply_price", fallback: "cost" },
  { label: "System SOH", key: "system_soh", fallback: "inventory" },
  { label: "SKU", key: "sku", fallback: "variant_sku" },
];

function normalizeCountGroups(groups = []) {
  return (Array.isArray(groups) ? groups : [])
    .map((group) => ({
      name: String(group?.name || '').trim(),
      outputs: Array.from(new Set((Array.isArray(group?.outputs) ? group.outputs : [])
        .map((output) => String(output || '').trim())
        .filter(Boolean))),
    }))
    .filter((group) => group.name);
}

function resolveCountGroupSource(scope = 'ALL', engineFallback = null) {
  // Categories → Count Groups is the single source of truth. loadEngineState(scope)
  // already inherits the All Sites category configuration when no site-specific
  // override exists, so Import must read that resolved engine instead of building
  // a second group model from the current CSV/POS rows.
  const scopedEngine = loadEngineState(scope) || engineFallback || null;
  const scopedGroups = normalizeCountGroups(scopedEngine?.config?.categoryGroups);
  if (scopedGroups.length) return { engine: scopedEngine, groups: scopedGroups };

  // Defensive fallback for older site records created before category inheritance.
  const globalEngine = loadEngineState('ALL');
  const globalGroups = normalizeCountGroups(globalEngine?.config?.categoryGroups);
  if (globalGroups.length) return { engine: globalEngine, groups: globalGroups };

  return { engine: scopedEngine || globalEngine || engineFallback || null, groups: [] };
}

function resolveConfiguredCountGroups(scope = 'ALL', engineFallback = null) {
  return resolveCountGroupSource(scope, engineFallback).groups;
}


function scopedImportStorageKey(scope = "ALL") {
  const normalized = normalizeStoreValue(scope || "ALL");
  return !normalized || normalized === "ALL" ? "mc-import-active" : `mc-import-active:${normalized}`;
}

function loadAuthoritativeImportEngine(scope = "ALL") {
  const normalized = normalizeStoreValue(scope || "ALL") || "ALL";
  const stored = loadEngineState(normalized);
  let importPayload = null;
  try {
    const raw = mcGetItem(scopedImportStorageKey(normalized));
    importPayload = raw ? JSON.parse(raw) : null;
  } catch (_) {
    importPayload = null;
  }

  const importEngine = importPayload?.engine && typeof importPayload.engine === "object"
    ? importPayload.engine
    : null;
  const storedRows = Array.isArray(stored?.rawRows) ? stored.rawRows.length : 0;
  const importRows = Array.isArray(importEngine?.rawRows) ? importEngine.rawRows.length : 0;
  const storedTime = Date.parse(stored?.uploadedAt || stored?.lastUploadedAt || stored?.updatedAt || "") || 0;
  const importTime = Date.parse(importEngine?.uploadedAt || importEngine?.lastUploadedAt || importPayload?.uploadedAt || importPayload?.updatedAt || "") || 0;

  // The active-import record is authoritative for inventory rows. A recovered
  // category-settings backup deliberately has no rows and must not appear as the
  // current import while the real CSV document is still hydrating.
  if (importEngine && importRows > 0 && (storedRows === 0 || importTime >= storedTime)) return importEngine;
  return stored;
}

async function loadAuthoritativeImportEngineAsync(scope = "ALL", { refreshCloud = false } = {}) {
  const normalized = normalizeStoreValue(scope || "ALL") || "ALL";
  const stored = loadEngineState(normalized) || buildDefaultState(normalized);
  const snapshot = peekActiveInventorySnapshot(normalized) || await loadActiveInventorySnapshot(normalized, { refreshCloud });
  if (!snapshot) {
    return { ...stored, rawRows: [], preparedRows: [], parsedHeaders: [], isActiveImportHydrating: true };
  }
  return {
    ...stored,
    ...snapshot,
    config: stored?.config || snapshot?.config,
    rules: stored?.rules || snapshot?.rules,
    mapping: snapshot?.mapping || stored?.mapping,
    isActiveImportHydrating: false,
  };
}

function importLockKeys(scope = "ALL") {
  const normalized = normalizeStoreValue(scope || "ALL") || "ALL";
  const label = normalized === "ALL" ? "ALL" : formatStoreLabel(normalized);
  return Array.from(new Set([
    normalized,
    String(scope || "ALL").trim() || "ALL",
    label,
    normalized.toLowerCase(),
    label.toLowerCase(),
  ])).flatMap((candidate) => [`mc-latest-import-lock:${candidate}`, `mc-active-import-lock:${candidate}`]);
}

function writeImportLock(scope = "ALL", payload = {}) {
  if (typeof window === "undefined") return;
  const value = JSON.stringify(payload);
  importLockKeys(scope).forEach((key) => {
    try { window.localStorage.setItem(key, value); } catch (_) {}
  });
}

function importEngineTimestamp(engine = null) {
  if (!engine || typeof engine !== "object") return 0;
  const candidates = [
    engine.uploadedAt,
    engine.lastUploadedAt,
    engine.lastGeneratedAt,
    engine.updatedAt,
    engine.config?.lastCsvUploadedAt,
  ]
    .filter(Boolean)
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value));
  return Math.max(...candidates, 0);
}

function importEngineRowCount(engine = null) {
  if (!engine || typeof engine !== "object") return 0;
  return Math.max(
    Array.isArray(engine.rawRows) ? engine.rawRows.length : 0,
    Array.isArray(engine.preparedRows) ? engine.preparedRows.length : 0
  );
}

function importEngineFingerprint(engine = null) {
  if (!engine || typeof engine !== "object") return "";
  return String(engine.importFingerprint || engine.activeImportId || engine.importSourceId || "");
}

function shouldKeepFreshManualImport(fresh = null, candidate = null) {
  if (!fresh || !candidate) return Boolean(fresh);
  const freshTime = importEngineTimestamp(fresh);
  const candidateTime = importEngineTimestamp(candidate);
  const freshRows = importEngineRowCount(fresh);
  const candidateRows = importEngineRowCount(candidate);
  const freshFingerprint = importEngineFingerprint(fresh);
  const candidateFingerprint = importEngineFingerprint(candidate);

  if (freshFingerprint && candidateFingerprint === freshFingerprint) return false;
  if (freshTime && (!candidateTime || candidateTime < freshTime)) return true;
  if (freshRows > 0 && candidateRows < freshRows && candidateTime <= freshTime) return true;
  if (freshFingerprint && !candidateFingerprint && candidateTime <= freshTime) return true;
  if (freshFingerprint && candidateFingerprint && candidateFingerprint !== freshFingerprint && candidateTime <= freshTime) return true;
  return false;
}

function validateInventoryUploadFile(file = null) {
  const name = String(file?.name || "").trim();
  const lowerName = name.toLowerCase();
  if (!file) return { ok: false, reason: "missing-file", message: "Choose a CSV inventory file first." };
  if (!lowerName.endsWith(".csv")) {
    return {
      ok: false,
      reason: "not-csv",
      message: `${name || "This file"} was blocked before import. Morpheus accepts .csv inventory files only.`,
    };
  }
  if (Number(file.size || 0) <= 0) {
    return { ok: false, reason: "empty-file", message: `${name || "This CSV"} is empty and was not imported.` };
  }
  return { ok: true, reason: "", message: "" };
}

function validateParsedInventoryCsv(parsed = null) {
  const headers = Array.isArray(parsed?.headers) ? parsed.headers.filter((header) => String(header || "").trim()) : [];
  const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
  if (!rows.length) {
    return { ok: false, reason: "no-rows", message: "This CSV contains no inventory rows, so the active snapshot was left unchanged." };
  }
  if (headers.length < 2) {
    return {
      ok: false,
      reason: "insufficient-columns",
      message: "This file does not look like an inventory CSV (fewer than 2 columns). The active snapshot was left unchanged.",
    };
  }
  return { ok: true, reason: "", message: "" };
}

function clearOldImportLocalAliases(scope = "ALL") {
  if (typeof window === "undefined") return;
  const normalized = normalizeStoreValue(scope || "ALL") || "ALL";
  const raw = String(scope || "ALL").trim() || "ALL";
  const label = normalized === "ALL" ? "ALL" : formatStoreLabel(normalized);
  const keep = new Set([
    normalized === "ALL" ? "mc-logic-pass" : `mc-logic-pass:${normalized}`,
    normalized === "ALL" ? "mc-import-active" : `mc-import-active:${normalized}`,
  ]);
  const suffixes = Array.from(new Set([normalized, raw, label, normalized.replace(/^inventory_/i, ""), label.replace(/\s+/g, "_")].filter(Boolean)));
  const keys = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i) || "";
    const isEngine = /^mc-logic-pass[:_]/i.test(key) || /^mc-engine[:_]/i.test(key);
    const isImport = /^mc-import-active[:_]/i.test(key);
    if (!isEngine && !isImport) continue;
    if (keep.has(key)) continue;
    const matchesScope = suffixes.some((suffix) => key.toLowerCase().includes(String(suffix).toLowerCase()));
    if (matchesScope) keys.push(key);
  }
  keys.forEach((key) => {
    try { window.localStorage.removeItem(key); } catch (_) {}
    try { window.sessionStorage?.removeItem(key); } catch (_) {}
  });
}


function safeParseActivityLog(raw) {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function appendMcActivity(entry = {}) {
  const current = safeParseActivityLog(mcGetItem(MC_ACTIVITY_LOG_KEY));
  const candidates = [100, 50, 20, 5, 1];
  for (const keep of candidates) {
    try {
      const next = [...current, entry].slice(-keep);
      mcSetItem(MC_ACTIVITY_LOG_KEY, JSON.stringify(next));
      return;
    } catch (err) {
      if (!String(err?.name || err?.message || "").toLowerCase().includes("quota")) {
        console.warn("Could not append MC activity log", err);
        return;
      }
    }
  }
  try { mcSetItem(MC_ACTIVITY_LOG_KEY, JSON.stringify([entry])); } catch (_) {}
}

function normalizeSiteMatchToken(value = "") {
  return String(value || "")
    .replace(/^inventory[_\s-]*/i, "")
    .replace(/[_\s-]*(store|site|outlet)$/i, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function findInventoryHeaderForSite(scope, headers = []) {
  const target = normalizeSiteMatchToken(formatStoreLabel(scope || ""));
  if (!target) return "";
  return (headers || []).find((header) => {
    if (!/^inventory_/i.test(String(header || ""))) return false;
    const candidate = normalizeSiteMatchToken(header);
    return candidate === target || candidate.includes(target) || target.includes(candidate);
  }) || "";
}

function buildWideCsvDistributionTargets(headers = [], registeredSites = []) {
  const inventoryHeaders = (headers || []).filter((header) => /^inventory_/i.test(String(header || "")));
  if (inventoryHeaders.length < 2) return [];

  const registry = (registeredSites || [])
    .filter((site) => site && site.active !== false)
    .map((site) => {
      const siteId = site.siteId || site.storeKey || site.name;
      const values = [siteId, site.storeKey, site.name, ...(site.aliases || [])].filter(Boolean);
      return {
        siteId,
        site,
        tokens: Array.from(new Set(values.map((value) => normalizeSiteMatchToken(value)).filter(Boolean))),
      };
    });

  const targets = [];
  const seen = new Set();
  inventoryHeaders.forEach((inventoryHeader) => {
    const headerToken = normalizeSiteMatchToken(inventoryHeader);
    const matched = registry.find((entry) => entry.tokens.some((token) => (
      token === headerToken || token.includes(headerToken) || headerToken.includes(token)
    )));

    // Prefer the organisation's canonical site id. If a newly introduced POS
    // outlet has not yet been registered, retain a stable label derived from the
    // inventory column so Mapping/Sites can resolve it without collapsing the
    // entire global upload to one site.
    const fallbackLabel = String(inventoryHeader || "")
      .replace(/^inventory_/i, "")
      .replace(/_/g, " ")
      .trim();
    const siteId = String(matched?.siteId || normalizeStoreValue(fallbackLabel) || fallbackLabel).trim();
    const key = normalizeSiteMatchToken(siteId);
    if (!siteId || !key || seen.has(key)) return;
    seen.add(key);
    targets.push({
      site: siteId,
      label: matched?.site?.name || formatStoreLabel(siteId),
      inventoryHeader,
      registered: Boolean(matched),
    });
  });

  return targets;
}

function resolveInventoryHeaderForScope(scope, headers = [], engine = {}, universalProfile = null) {
  if (String(engine?.config?.dataSourceMode || "csv").toLowerCase() === "pos" || (headers || []).includes("system_soh")) return "system_soh";
  // Universal row-based CSVs carry one SOH field plus a separate Site field.
  // Never replace that SOH mapping with a Lightspeed inventory_<site> column.
  if (universalProfile?.layout === "row_site" && universalProfile?.fields?.systemSoh?.header) {
    return universalProfile.fields.systemSoh.header;
  }
  const genericSoh = (headers || []).find((header) => /^(soh|stock on hand|stock|quantity on hand|qty on hand|quantity|qty)$/i.test(String(header || '').trim()));
  if (genericSoh) return genericSoh;
  const normalizedScope = normalizeStoreValue(scope || 'ALL');
  if (!normalizedScope || normalizedScope === 'ALL') {
    return engine?.mapping?.systemSoh || headers.find((header) => /^inventory_/i.test(String(header || ''))) || '';
  }
  return findInventoryHeaderForSite(normalizedScope, headers);
}



function getPosStatusMeta(status) {
  const normalized = String(status || "not_connected").toLowerCase();
  if (["ready_for_function", "ready", "connected"].includes(normalized)) {
    return { label: "Ready", tone: "ready" };
  }
  if (["synced_placeholder", "synced", "synced_live"].includes(normalized)) {
    return { label: "Synced", tone: "ready" };
  }
  if (["error", "failed", "token_error"].includes(normalized)) {
    return { label: "Error", tone: "danger" };
  }
  if (["testing", "pending"].includes(normalized)) {
    return { label: "Testing", tone: "warning" };
  }
  return { label: "Not Ready", tone: "muted" };
}

function isPosConnectionReady(status) {
  return ["ready_for_function", "ready", "connected", "synced_placeholder", "synced", "synced_live"].includes(String(status || "").toLowerCase());
}

function formatUploadTimestamp(value) {
  if (!value) return "No upload timestamp";
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return String(value);
  }
}

function normalizeCompareToken(value = '') {
  return normalizeStoreValue(formatStoreLabel(value || '') || value || '');
}

function readSessionDetailMeta(sessionId = '') {
  const cleanId = String(sessionId || '').trim();
  if (!cleanId || typeof window === 'undefined') return {};
  const keys = [`mc-session-engine:${cleanId}`, `mc-session-engine-${cleanId}`];
  for (const key of keys) {
    try {
      const raw = mcGetItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      const meta = parsed?.meta && typeof parsed.meta === 'object' ? parsed.meta : {};
      if (meta && Object.keys(meta).length) return meta;
    } catch (_) {}
  }
  return {};
}

function enrichRecordForRules(record = {}) {
  const meta = readSessionDetailMeta(record?.sessionId);
  return {
    ...record,
    storeName: record?.storeName || record?.store || record?.siteName || meta?.storeName || meta?.store || meta?.siteName || '',
    store: record?.store || record?.storeName || meta?.store || meta?.storeName || '',
    storeKey: record?.storeKey || meta?.storeKey || '',
    siteName: record?.siteName || meta?.siteName || meta?.storeName || '',
    siteId: record?.siteId || meta?.siteId || '',
    siteKey: record?.siteKey || meta?.siteKey || '',
    sectionId: record?.sectionId ?? meta?.sectionId ?? '',
    sectionName: record?.sectionName || meta?.sectionName || '',
    fullSite: record?.fullSite === true || meta?.fullSite === true,
    isFullSite: record?.isFullSite === true || meta?.isFullSite === true,
    fullCount: record?.fullCount === true || meta?.fullCount === true,
    cycleId: record?.cycleId || meta?.cycleId || '',
    cycleName: record?.cycleName || meta?.cycleName || '',
    countScope: record?.countScope || meta?.countScope || '',
    sessionType: record?.sessionType || meta?.sessionType || '',
    type: record?.type || meta?.type || '',
  };
}

function sameStore(record, engine, explicitScope = '') {
  const hydratedRecord = enrichRecordForRules(record);
  const cleanExplicitScope = String(explicitScope || '').trim();
  // A CSV inventory header (for example "SOH" or inventory_Bothasig) is a
  // source field, not the operational site identity. Conflict checks must use
  // the selected MatrixOS site so row-based imports cannot collide globally.
  const targetStore = engine?.config?.storeName || engine?.storeName || '';

  // When the operator has selected a concrete site, that site is the only valid
  // comparison scope. Do not mix in stale engine/config values from the previous
  // site, otherwise a matching cycle/section at another site can trigger the
  // overwrite prompt.
  const targetCandidates = cleanExplicitScope && cleanExplicitScope.toUpperCase() !== 'ALL'
    ? [cleanExplicitScope, formatStoreLabel(cleanExplicitScope)]
    : [
        cleanExplicitScope,
        formatStoreLabel(cleanExplicitScope),
        targetStore,
        engine?.config?.storeName,
        engine?.mapping?.systemSoh,
        engine?.storeName,
        engine?.storeKey,
      ];

  const targetTokens = targetCandidates.map(normalizeCompareToken).filter(Boolean);

  const recordTokens = [
    hydratedRecord?.storeName,
    hydratedRecord?.store,
    hydratedRecord?.storeKey,
    hydratedRecord?.siteName,
    hydratedRecord?.siteId,
    hydratedRecord?.siteKey,
    hydratedRecord?.mapping?.systemSoh,
  ].map(normalizeCompareToken).filter(Boolean);

  if (!targetTokens.length || targetTokens.includes('all')) return true;
  return recordTokens.some((token) => targetTokens.includes(token));
}

function recordBelongsToCycle(record = {}, cycleScope = null) {
  const hydratedRecord = enrichRecordForRules(record);
  const selectedTokens = [cycleScope?.cycleId, cycleScope?.cycleName]
    .map(normalizeCycleId)
    .filter(Boolean);
  if (!selectedTokens.length) return true;

  const recordTokens = [
    hydratedRecord?.cycleId,
    hydratedRecord?.cycleName,
    hydratedRecord?.cycle?.cycleId,
    hydratedRecord?.cycle?.cycleName,
  ]
    .map(normalizeCycleId)
    .filter(Boolean);

  // Older sessions may not have cycle metadata. Treat them as Cycle 1 only,
  // never as every cycle. This prevents Morning Count sessions blocking Handover/Closing.
  if (!recordTokens.length) return selectedTokens.some((token) => defaultCycleTokens().includes(token));
  return recordTokens.some((token) => selectedTokens.includes(token));
}

function isFullSiteSessionRecord(record = {}) {
  const hydratedRecord = enrichRecordForRules(record);
  const sectionId = normalizeSectionId(hydratedRecord?.sectionId || '');
  const sectionName = normalizeSectionId(hydratedRecord?.sectionName || hydratedRecord?.scopeName || hydratedRecord?.sessionName || '');
  const type = normalizeSectionId(hydratedRecord?.scopeType || hydratedRecord?.countScope || hydratedRecord?.sessionType || hydratedRecord?.type || '');
  return (
    hydratedRecord?.fullSite === true ||
    hydratedRecord?.isFullSite === true ||
    hydratedRecord?.fullCount === true ||
    ['full-site', 'full-count', 'full-site-count', 'all', 'all-sections'].includes(sectionId) ||
    ['full-site', 'full-count', 'full-site-count', 'all', 'all-sections'].includes(sectionName) ||
    ['full-site', 'full-count', 'full-site-count', 'all', 'all-sections'].includes(type)
  );
}

function activeLiveSessionRecordsForEngine(engine, sectionScope = null, cycleScope = null, explicitScope = '') {
  const rawTargetSection = String(sectionScope?.sectionId || '').trim();
  const targetSectionId = rawTargetSection ? normalizeSectionId(rawTargetSection) : '';
  const wantsFullSite = sectionScope?.fullSite === true || !targetSectionId;
  const targetCycleId = normalizeCycleId(cycleScope?.cycleId || '');
  const archivedIds = new Set(loadArchivedSessionRecords().map((item) => item?.sessionId).filter(Boolean));
  return loadSessionRecords().map(enrichRecordForRules).filter((hydratedRecord) => {
    if (!hydratedRecord?.sessionId || archivedIds.has(hydratedRecord.sessionId)) return false;
    if (hydratedRecord.archived === true || hydratedRecord.archivedAt || hydratedRecord.archiveReason || hydratedRecord.archiveType) return false;
    const status = String(hydratedRecord.status || "").trim().toLowerCase();
    // A completed section is still part of the live cycle until the cycle is finalized
    // and the session is moved to Archives. It must therefore be resumed/replaced,
    // not duplicated by another import for the same site, cycle and section.
    if (status && !["active", "section complete", "complete", "completed"].includes(status)) return false;
    if (!sameStore(hydratedRecord, engine, explicitScope)) return false;
    if (!recordBelongsToCycle(hydratedRecord, cycleScope)) return false;
    if (wantsFullSite) return isFullSiteSessionRecord(hydratedRecord);
    if (isFullSiteSessionRecord(hydratedRecord)) return false;
    const recordSectionId = normalizeSectionId(hydratedRecord?.sectionId || hydratedRecord?.sectionName || '');
    return recordSectionId === targetSectionId;
  });
}

function activeLiveSessionRecordsForCycle(engine, cycleScope = null, explicitScope = '') {
  const archivedIds = new Set(loadArchivedSessionRecords().map((item) => item?.sessionId).filter(Boolean));
  return loadSessionRecords().map(enrichRecordForRules).filter((hydratedRecord) => {
    if (!hydratedRecord?.sessionId || archivedIds.has(hydratedRecord.sessionId)) return false;
    if (hydratedRecord.archived === true || hydratedRecord.archivedAt || hydratedRecord.archiveReason || hydratedRecord.archiveType) return false;
    const status = String(hydratedRecord.status || "").trim().toLowerCase();
    // A completed section is still part of the live cycle until the cycle is finalized
    // and the session is moved to Archives. It must therefore be resumed/replaced,
    // not duplicated by another import for the same site, cycle and section.
    if (status && !["active", "section complete", "complete", "completed"].includes(status)) return false;
    if (!sameStore(hydratedRecord, engine, explicitScope)) return false;
    if (!recordBelongsToCycle(hydratedRecord, cycleScope)) return false;
    return true;
  });
}

function cycleScopeTokens(cycleScope = null) {
  return [cycleScope?.cycleId, cycleScope?.cycleName]
    .map(normalizeCycleId)
    .filter(Boolean);
}

function defaultCycleTokens() {
  const cycles = activeCycles();
  const first = cycles.find((cycle) => cycle?.active !== false) || cycles[0] || {};
  return Array.from(new Set([
    'cycle-1',
    first?.cycleId,
    first?.id,
    first?.name,
  ].map(normalizeCycleId).filter(Boolean)));
}

function recordCycleTokens(record = {}) {
  const hydratedRecord = enrichRecordForRules(record);
  return [
    hydratedRecord?.cycleId,
    hydratedRecord?.cycleName,
    hydratedRecord?.cycle?.cycleId,
    hydratedRecord?.cycle?.cycleName,
  ]
    .map(normalizeCycleId)
    .filter(Boolean);
}

function sameSelectedCycleOnly(record = {}, cycleScope = null) {
  const selected = cycleScopeTokens(cycleScope);
  if (!selected.length) return false;
  const tokens = recordCycleTokens(record);
  // Legacy sessions without cycle metadata belong only to the default first cycle.
  if (!tokens.length) return selected.some((token) => defaultCycleTokens().includes(token));
  return tokens.some((token) => selected.includes(token));
}

function activeRecordsForSelectedCycle(engine, cycleScope = null, explicitScope = '') {
  const archivedIds = new Set(loadArchivedSessionRecords().map((item) => item?.sessionId).filter(Boolean));
  return loadSessionRecords().map(enrichRecordForRules).filter((record) => {
    if (!record?.sessionId || archivedIds.has(record.sessionId)) return false;
    if (record.archived === true || record.archivedAt || record.archiveReason || record.archiveType) return false;
    const status = String(record.status || '').trim().toLowerCase();
    if (status && !['active', 'section complete', 'complete', 'completed'].includes(status)) return false;
    if (!sameStore(record, engine, explicitScope)) return false;
    return sameSelectedCycleOnly(record, cycleScope);
  });
}


const STORAGE_CHANGE_GROUPS = [
  { key: "added", label: "Added Products", short: "Added" },
  { key: "removed", label: "Removed Products", short: "Removed" },
  { key: "stock", label: "Stock Movement", short: "Stock" },
  { key: "cost", label: "Cost Updates", short: "Cost" },
  { key: "configuration", label: "Configuration Changes", short: "Configuration" },
  { key: "metadata", label: "Metadata Updates", short: "Metadata" },
];

function storageClassificationCount(delta = {}, key) {
  const direct = delta?.classifications?.[key];
  if (Number.isFinite(Number(direct))) return Number(direct);
  const fallback = {
    added: delta?.addedCount,
    removed: delta?.removedCount,
    stock: delta?.quantityChangedCount,
    cost: delta?.priceChangedCount,
    configuration: delta?.configurationChangedCount,
    metadata: delta?.metadataChangedCount,
  };
  return Number(fallback[key] || 0);
}

function formatStorageValue(value, field = "") {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") {
    if (/cost/i.test(field)) return `R${value.toFixed(2)}`;
    return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));
  }
  return String(value);
}

function buildStorageIntelligence(movement = null) {
  if (!movement) return null;
  const delta = movement.delta || {};
  const groups = Object.fromEntries(STORAGE_CHANGE_GROUPS.map((group) => [group.key, storageClassificationCount(delta, group.key)]));
  const stockRows = Array.isArray(delta?.changes?.stock) ? delta.changes.stock : [];
  const stockMovements = stockRows.map((row) => {
    const field = Array.isArray(row.fields) ? row.fields.find((item) => String(item?.field || '').toLowerCase() === 'soh') || row.fields[0] : null;
    const difference = Number(field?.difference ?? (Number(field?.after || 0) - Number(field?.before || 0)));
    return { ...row, field, difference: Number.isFinite(difference) ? difference : 0 };
  });
  const increases = stockMovements.filter((row) => row.difference > 0).sort((a, b) => b.difference - a.difference);
  const decreases = stockMovements.filter((row) => row.difference < 0).sort((a, b) => a.difference - b.difference);
  const total = Number(delta.currentProductCount ?? movement.productCount ?? 0);
  const changed = Number(delta.changedCount || 0);
  const stable = Number(delta.unchangedCount || 0);
  const stablePercent = total > 0 ? Math.round((stable / total) * 100) : 0;
  const changedPercent = total > 0 ? Math.round((changed / total) * 100) : 0;
  const largestIncrease = increases[0] || null;
  const largestDecrease = decreases[0] || null;
  const highlights = [];

  if (delta.initial) {
    highlights.push(`Initial inventory snapshot created with ${total} products.`);
  } else if (!changed && !groups.added && !groups.removed) {
    highlights.push('No inventory changes were detected in the latest revision.');
  } else {
    highlights.push(`${changed} product${changed === 1 ? '' : 's'} changed since the previous revision.`);
    if (largestIncrease) highlights.push(`Largest increase: ${largestIncrease.product || 'Unnamed product'} (${largestIncrease.difference > 0 ? '+' : ''}${formatStorageValue(largestIncrease.difference, 'SOH')}).`);
    if (largestDecrease) highlights.push(`Largest decrease: ${largestDecrease.product || 'Unnamed product'} (${formatStorageValue(largestDecrease.difference, 'SOH')}).`);
    highlights.push(groups.added ? `${groups.added} product${groups.added === 1 ? ' was' : 's were'} added.` : 'No products were added.');
    highlights.push(groups.removed ? `${groups.removed} product${groups.removed === 1 ? ' was' : 's were'} removed.` : 'No products were removed.');
  }

  return {
    movement,
    delta,
    groups,
    total,
    changed,
    stable,
    stablePercent,
    changedPercent,
    netSohChange: Number(delta.netSohChange || 0),
    largestIncrease,
    largestDecrease,
    highlights,
  };
}

function buildStoragePatterns(movements = []) {
  const windowRows = (Array.isArray(movements) ? movements : []).filter((movement) => !movement?.delta?.initial).slice(0, 5).reverse();
  if (windowRows.length < 2) {
    return { windowSize: windowRows.length, repeatedDecrease: [], repeatedIncrease: [], volatile: [], recurringRemoval: [], stableCount: Number(windowRows[windowRows.length - 1]?.delta?.unchangedCount || 0) };
  }

  const products = new Map();
  const touch = (item = {}) => {
    const key = item.key || `${item.sku || ''}:${item.product || ''}`;
    if (!key) return null;
    if (!products.has(key)) products.set(key, { key, product: item.product || 'Unnamed product', sku: item.sku || '', stock: [], lifecycle: [] });
    return products.get(key);
  };

  windowRows.forEach((movement) => {
    const revision = Number(movement.revision || 0);
    const changes = movement?.delta?.changes || {};
    (changes.stock || []).forEach((item) => {
      const record = touch(item);
      if (!record) return;
      const field = (item.fields || []).find((entry) => String(entry?.field || '').toLowerCase() === 'soh') || (item.fields || [])[0];
      const difference = Number(field?.difference ?? (Number(field?.after || 0) - Number(field?.before || 0)));
      record.stock.push({ revision, before: Number(field?.before || 0), after: Number(field?.after || 0), difference: Number.isFinite(difference) ? difference : 0 });
    });
    (changes.added || []).forEach((item) => { const record = touch(item); if (record) record.lifecycle.push({ revision, type: 'added' }); });
    (changes.removed || []).forEach((item) => { const record = touch(item); if (record) record.lifecycle.push({ revision, type: 'removed' }); });
  });

  const repeatedDecrease = [];
  const repeatedIncrease = [];
  const volatile = [];
  const recurringRemoval = [];

  products.forEach((record) => {
    const stock = record.stock.sort((a, b) => a.revision - b.revision);
    let decreaseRun = 0;
    let increaseRun = 0;
    for (let i = stock.length - 1; i >= 0; i -= 1) {
      const next = stock[i + 1];
      if (stock[i].difference < 0 && (!next || next.revision - stock[i].revision === 1)) decreaseRun += 1; else break;
    }
    for (let i = stock.length - 1; i >= 0; i -= 1) {
      const next = stock[i + 1];
      if (stock[i].difference > 0 && (!next || next.revision - stock[i].revision === 1)) increaseRun += 1; else break;
    }
    if (decreaseRun >= 2) repeatedDecrease.push({ ...record, run: decreaseRun, total: stock.slice(-decreaseRun).reduce((sum, row) => sum + row.difference, 0) });
    if (increaseRun >= 2) repeatedIncrease.push({ ...record, run: increaseRun, total: stock.slice(-increaseRun).reduce((sum, row) => sum + row.difference, 0) });

    const nonZero = stock.filter((row) => row.difference !== 0);
    let signChanges = 0;
    for (let i = 1; i < nonZero.length; i += 1) {
      if (Math.sign(nonZero[i - 1].difference) !== Math.sign(nonZero[i].difference)) signChanges += 1;
    }
    if (nonZero.length >= 3 && signChanges >= 2) volatile.push({ ...record, signChanges });

    const lifecycle = record.lifecycle.sort((a, b) => a.revision - b.revision);
    const removed = lifecycle.filter((row) => row.type === 'removed').length;
    const reintroduced = lifecycle.some((row, index) => row.type === 'added' && lifecycle.slice(0, index).some((prior) => prior.type === 'removed'));
    if (removed >= 2 || reintroduced) recurringRemoval.push({ ...record, removedCount: removed, reintroduced });
  });

  const latest = windowRows[windowRows.length - 1] || {};
  const total = Number(latest?.delta?.currentProductCount ?? latest?.productCount ?? 0);
  const touchedKeys = new Set();
  products.forEach((record) => { if (record.stock.length || record.lifecycle.length) touchedKeys.add(record.key); });

  return {
    windowSize: windowRows.length,
    repeatedDecrease: repeatedDecrease.sort((a, b) => a.total - b.total),
    repeatedIncrease: repeatedIncrease.sort((a, b) => b.total - a.total),
    volatile,
    recurringRemoval,
    stableCount: Math.max(0, total - touchedKeys.size),
  };
}

function deadStockProductKey(row = {}) {
  const sku = String(row.sku || row.SKU || row.variant_sku || row.barcode || row.code || '').trim().toLowerCase();
  if (sku) return `sku:${sku}`;
  const product = String(row.product || row.productName || row.name || row.description || row.product_name || '').trim().toLowerCase();
  const category = String(row.category || row.sourceCategory || row.product_category || '').trim().toLowerCase();
  return product ? `product:${product}::${category}` : '';
}

function deadStockNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

// DS1.14: Storage revisions created by Global Distribution are written against
// the canonical MatrixOS site id returned by the site registry. The admin selector
// can expose a display/store alias instead (for example "District Six"). Reading
// or writing the Storage ledger with that alias splits one site's history across
// different Firestore keys and can make Dead Stock fall back to unrelated/global
// evidence. Keep active inventory/session scope untouched; canonicalise only the
// Storage Movement/Sync authority key.
function resolveStorageAuthorityScope(scope = 'ALL') {
  const raw = String(scope || 'ALL').trim() || 'ALL';
  if (raw.toUpperCase() === 'ALL') return 'ALL';
  try {
    const site = resolveSite(raw);
    return String(site?.siteId || raw).trim() || raw;
  } catch (_) {
    return raw;
  }
}

function buildDeadStockIntelligence(engine = null, movements = [], syncActivity = [], thresholdDays = 7, scope = "ALL", options = {}) {
  // DS1.8: Dead Stock must inspect the source snapshot when it exists. Prepared
  // rows can contain a canonical `systemSoh: 0` when the site-specific inventory
  // column was not the column used during preparation. That zero is valid for
  // the prepared projection, but it is not evidence that the selected site has
  // no stock. The raw snapshot still contains the authoritative inventory_<site>
  // value, so prefer it for this read-only intelligence projection.
  const sourceRows = Array.isArray(engine?.rawRows) && engine.rawRows.length
    ? engine.rawRows
    : Array.isArray(engine?.preparedRows) ? engine.preparedRows : [];
  const preparedRows = Array.isArray(engine?.preparedRows) ? engine.preparedRows : [];
  const mapping = engine?.mapping || {};

  // DS1.10: the active snapshot's own SOH mapping is the first authority. The
  // previous Dead Stock patch could "helpfully" guess a similarly named wide
  // CSV inventory column before consulting the mapping that Intake/Review and
  // Sessions already use. On a real Lightspeed export this can select a sibling
  // outlet/online column and make every District Six SOH look blank/zero.
  // Gather headers across several rows because source-shaped rows need not all
  // expose identical optional fields.
  const rowHeaders = Array.from(new Set(
    sourceRows.slice(0, 12).flatMap((row) => Object.keys(row || {}))
  ));
  const headerByLower = new Map(rowHeaders.map((header) => [String(header).trim().toLowerCase(), header]));
  const existingHeader = (candidate = '') => headerByLower.get(String(candidate || '').trim().toLowerCase()) || '';

  const authoritativeMappedHeader = existingHeader(
    engine?.activeImportPointer?.inventoryHeader ||
    engine?.distributionContext?.inventoryHeader ||
    mapping.systemSoh ||
    ''
  ) || existingHeader(mapping.systemSoh || '');

  const siteCandidates = [
    scope,
    formatStoreLabel(scope),
    engine?.config?.storeName,
    engine?.storeName,
    engine?.storeKey,
    engine?.siteName,
    engine?.siteId,
    engine?.distributionContext?.scope,
    engine?.distributionContext?.siteName,
  ].filter(Boolean);
  const siteTokens = Array.from(new Set(siteCandidates.map(normalizeSiteMatchToken).filter(Boolean)));
  const inventoryHeaders = rowHeaders.filter((header) => /^inventory[_\s-]/i.test(String(header || "")));
  const inferredScopedHeader = String(scope || "ALL").toUpperCase() === "ALL" ? "" : (
    inventoryHeaders.find((header) => {
      const headerToken = normalizeSiteMatchToken(header);
      return siteTokens.some((token) => token === headerToken || token.includes(headerToken) || headerToken.includes(token));
    }) || resolveInventoryHeaderForScope(scope, rowHeaders, engine) || ""
  );
  const scopedInventoryHeader = authoritativeMappedHeader || inferredScopedHeader || '';

  // DS1.7/DS1.10: source-shaped site snapshots are projected read-only into the
  // canonical fields Dead Stock needs. A prepared-row lookup is retained only as
  // a missing-field fallback; an explicit source value of 0 remains a real zero
  // and is never replaced by another projection.
  const mappedValue = (row = {}, mappedKey = '', aliases = []) => {
    const candidates = [mappedKey, ...aliases].filter(Boolean);
    for (const key of candidates) {
      const actualKey = existingHeader(key) || key;
      if (Object.prototype.hasOwnProperty.call(row || {}, actualKey) && row[actualKey] !== null && row[actualKey] !== undefined && row[actualKey] !== '') return row[actualKey];
    }
    return '';
  };
  const preparedByKey = new Map();
  preparedRows.forEach((row) => {
    const key = deadStockProductKey(row);
    if (key) preparedByKey.set(key, row);
  });
  const rows = sourceRows.map((row = {}) => {
    const product = mappedValue(row, mapping.product, ['product', 'productName', 'name', 'description', 'product_name']);
    const sku = mappedValue(row, mapping.sku, ['sku', 'SKU', 'variant_sku', 'barcode', 'code']);
    const category = mappedValue(row, mapping.category, ['category', 'sourceCategory', 'product_category']);
    const projected = { ...row, product, sku, category };
    const key = deadStockProductKey(projected);
    const prepared = key ? preparedByKey.get(key) : null;
    const rawSoh = mappedValue(row, scopedInventoryHeader, [scopedInventoryHeader, mapping.systemSoh, 'systemSoh', 'system_soh', 'realSoh', 'real_soh', 'soh', 'quantity', 'stockOnHand', 'inventory']);
    const sohValue = rawSoh !== '' ? rawSoh : mappedValue(prepared || {}, 'systemSoh', ['systemSoh', 'system_soh', 'realSoh', 'real_soh', 'soh', 'quantity', 'stockOnHand', 'inventory']);
    const costMetadata = resolveDeadStockCost(row, mapping.cost || '', prepared, sourceRows === preparedRows);
    return {
      ...projected,
      ...costMetadata,
      systemSoh: sohValue,
    };
  });
  const ledger = (Array.isArray(movements) ? movements : [])
    .filter((movement) => Number.isFinite(Date.parse(movement?.timestamp || movement?.createdAt || '')))
    .slice()
    .sort((a, b) => Date.parse(a.timestamp || a.createdAt) - Date.parse(b.timestamp || b.createdAt));
  const observations = (Array.isArray(syncActivity) ? syncActivity : [])
    .filter((entry) => Number.isFinite(Date.parse(entry?.timestamp || entry?.createdAt || '')))
    .slice()
    .sort((a, b) => Date.parse(a.timestamp || a.createdAt) - Date.parse(b.timestamp || b.createdAt));
  const latestLedger = ledger[ledger.length - 1] || null;
  const latestObservation = observations[observations.length - 1] || null;

  // DS1.6: the revision ledger itself is the only row-level authority for Dead
  // Stock. Sync Activity proves that an import happened, but it does not prove
  // which SKU changed. Using old sync timestamps to extend the evidence window
  // can therefore manufacture dead-stock age from audit metadata alone.
  const trustedLedger = ledger.filter((movement) => String(movement?.readiness?.status || '').trim() !== 'sync_failed');
  const latestRevisionTrusted = !latestLedger || String(latestLedger?.readiness?.status || '').trim() !== 'sync_failed';

  const nowMs = Date.now();
  const newestEvidenceAtMs = Math.max(
    Date.parse(latestLedger?.timestamp || latestLedger?.createdAt || '') || 0,
    Date.parse(latestObservation?.timestamp || latestObservation?.createdAt || '') || 0
  );
  const latestAtMs = Math.max(nowMs, newestEvidenceAtMs);
  const thresholdMs = Math.max(1, Number(thresholdDays || 7)) * 86400000;
  const cutoffMs = latestAtMs - thresholdMs;
  const oldestTrustedLedgerAtMs = trustedLedger.length
    ? (Date.parse(trustedLedger[0].timestamp || trustedLedger[0].createdAt || '') || 0)
    : 0;
  const hasWindow = Boolean(latestRevisionTrusted && oldestTrustedLedgerAtMs && oldestTrustedLedgerAtMs <= cutoffMs);

  const canonicalMovementKey = (item = {}) => {
    const derived = deadStockProductKey(item);
    if (derived) return derived;
    const raw = String(item?.key || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw.startsWith('sku:')) return `sku:${raw.slice(4).trim()}`;
    return raw;
  };

  // DS1.20: Dead Stock tracks canonical per-SKU SOH continuity, not the raw
  // historical `before` value embedded in legacy revision deltas. Older revisions
  // were sometimes built from a stale sibling-site / filtered snapshot, producing
  // false transitions such as 48 -> 62 even though the same site's previous
  // trustworthy observation was already 62. Historical continuity is reconciled
  // read-only by comparing each revision's observed `after` SOH with the last
  // canonical SOH we actually observed for that SKU:
  //   - same canonical SOH => no movement, regardless of a bad legacy `before`;
  //   - different canonical SOH => genuine movement at this revision boundary;
  //   - omission/removal => presence only; never a movement to zero;
  //   - reappearance at the same SOH => preserve the old static clock;
  //   - reappearance at a different SOH => genuine movement at reappearance.
  // The first numeric observation establishes an evidence boundary, not an invented
  // movement. This lets old site-selected and Global Distribution revisions share
  // one continuous per-site SKU history without rewriting the immutable ledger.
  const lastMovement = new Map();
  const firstObserved = new Map();
  const continuityState = new Map();
  const historicalContinuityDiagnostics = [];
  const sohFieldFor = (item = {}) => (Array.isArray(item?.fields) ? item.fields : [])
    .find((field) => String(field?.field || '').trim().toLowerCase() === 'soh');
  const explicitNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  };
  const markObserved = (key, atMs, revision) => {
    const observed = firstObserved.get(key);
    if (!observed || atMs < observed.atMs) firstObserved.set(key, { atMs, revision });
  };
  const markMovement = (key, atMs, revision, type, before, after) => {
    if (before === null || after === null || Math.abs(after - before) <= 0.000001) return;
    const prior = lastMovement.get(key);
    if (!prior || atMs >= prior.atMs) lastMovement.set(key, { atMs, revision, type, before, after });
  };

  trustedLedger.forEach((movement) => {
    const atMs = Date.parse(movement.timestamp || movement.createdAt || '') || 0;
    if (!atMs) return;
    const revision = Number(movement.revision || 0);
    const changes = movement?.delta?.changes || {};
    const initialBaseline = Boolean(movement?.delta?.initial);

    (Array.isArray(changes.stock) ? changes.stock : []).forEach((item) => {
      const key = canonicalMovementKey(item);
      if (!key) return;
      const sohField = sohFieldFor(item);
      if (!sohField) return;
      const legacyBefore = explicitNumber(sohField.before);
      const after = explicitNumber(sohField.after);
      if (after === null) return;

      const prior = continuityState.get(key);
      markObserved(key, atMs, revision);

      if (prior && prior.soh !== null) {
        const changedFromCanonical = Math.abs(after - prior.soh) > 0.000001;
        if (changedFromCanonical) {
          markMovement(key, atMs, revision, 'stock', prior.soh, after);
        }
        historicalContinuityDiagnostics.push({
          key,
          revision,
          at: new Date(atMs).toISOString(),
          group: 'stock',
          legacyBefore,
          canonicalBefore: prior.soh,
          after,
          decision: changedFromCanonical ? 'GENUINE_CANONICAL_MOVEMENT' : (
            legacyBefore !== null && Math.abs(legacyBefore - after) > 0.000001
              ? 'IGNORED_FALSE_LEGACY_TRANSITION'
              : 'UNCHANGED_CANONICAL_SOH'
          ),
        });
      } else {
        historicalContinuityDiagnostics.push({
          key,
          revision,
          at: new Date(atMs).toISOString(),
          group: 'stock',
          legacyBefore,
          canonicalBefore: null,
          after,
          decision: 'FIRST_NUMERIC_OBSERVATION',
        });
      }

      continuityState.set(key, { soh: after, present: true, atMs, revision });
    });

    (Array.isArray(changes.removed) ? changes.removed : []).forEach((item) => {
      const key = canonicalMovementKey(item);
      if (!key) return;
      const prior = continuityState.get(key);
      // A filtered/minimum CSV can omit a product. Legacy `removed` entries are
      // therefore presence evidence only and must never overwrite canonical SOH
      // with a stale `before` value or zero.
      if (prior) continuityState.set(key, { ...prior, present: false, presenceAtMs: atMs, presenceRevision: revision });
      historicalContinuityDiagnostics.push({
        key,
        revision,
        at: new Date(atMs).toISOString(),
        group: 'removed',
        canonicalBefore: prior?.soh ?? null,
        after: null,
        decision: 'IGNORED_PRESENCE_ONLY_REMOVAL',
      });
    });

    (Array.isArray(changes.added) ? changes.added : []).forEach((item) => {
      const key = canonicalMovementKey(item);
      if (!key) return;
      const sohField = sohFieldFor(item);
      const after = explicitNumber(sohField?.after);
      const prior = continuityState.get(key);

      // Legacy added rows often contain no SOH at all. Without a numeric value they
      // are presence-only and cannot reset the movement clock. With a numeric value,
      // reconcile against the last canonical SOH exactly like a stock observation.
      if (after !== null) {
        markObserved(key, atMs, revision);
        if (!initialBaseline && prior && prior.soh !== null) {
          const changedFromCanonical = Math.abs(after - prior.soh) > 0.000001;
          if (changedFromCanonical) markMovement(key, atMs, revision, 'reappeared', prior.soh, after);
          historicalContinuityDiagnostics.push({
            key,
            revision,
            at: new Date(atMs).toISOString(),
            group: 'added',
            canonicalBefore: prior.soh,
            after,
            decision: changedFromCanonical ? 'GENUINE_REAPPEARANCE_MOVEMENT' : 'REAPPEARED_UNCHANGED',
          });
        } else {
          historicalContinuityDiagnostics.push({
            key,
            revision,
            at: new Date(atMs).toISOString(),
            group: 'added',
            canonicalBefore: prior?.soh ?? null,
            after,
            decision: 'FIRST_NUMERIC_OBSERVATION',
          });
        }
        continuityState.set(key, { soh: after, present: true, atMs, revision });
      } else {
        if (prior) continuityState.set(key, { ...prior, present: true, presenceAtMs: atMs, presenceRevision: revision });
        historicalContinuityDiagnostics.push({
          key,
          revision,
          at: new Date(atMs).toISOString(),
          group: 'added',
          canonicalBefore: prior?.soh ?? null,
          after: null,
          decision: 'IGNORED_PRESENCE_ONLY_ADD',
        });
      }
    });
  });

  const deadStockDebugProducts = [];
  const products = rows.map((row) => {
    const key = deadStockProductKey(row);
    if (!key) return null;
    const soh = deadStockNumber(row.systemSoh, row.system_soh, row.realSoh, row.real_soh, row.soh, row.quantity, row.stockOnHand, row.inventory);
    // Zero/negative inventory is not capital sitting in the business and can
    // never be classified as Dead Stock.
    if (!(soh > 0)) {
      deadStockDebugProducts.push({ key, product: String(row.product || row.productName || row.name || ''), sku: String(row.sku || row.SKU || ''), soh, decision: 'EXCLUDED_NON_POSITIVE_SOH' });
      return null;
    }

    const cost = deadStockNumber(row.cost, row.effectiveCost, row.sourceCost, row.unitCost, row.supply_price, row.price);
    const movement = lastMovement.get(key) || null;
    const observed = firstObserved.get(key) || null;

    // If no genuine quantity transition exists in the retained ledger, we may
    // still prove a SKU is static when the trustworthy revision window itself
    // reaches beyond the threshold. Use the oldest trusted revision as an
    // observation boundary, not as a fabricated "last movement" timestamp.
    const staticSinceMs = movement?.atMs || observed?.atMs || oldestTrustedLedgerAtMs;
    const ageMs = latestAtMs && staticSinceMs ? Math.max(0, latestAtMs - staticSinceMs) : 0;
    const daysStatic = Math.floor(ageMs / 86400000);
    const qualifies = Boolean(hasWindow && staticSinceMs && staticSinceMs <= cutoffMs);
    deadStockDebugProducts.push({
      key,
      product: String(row.product || row.productName || row.name || row.description || row.product_name || 'Unnamed product').trim(),
      sku: String(row.sku || row.SKU || row.variant_sku || row.barcode || row.code || '').trim(),
      soh,
      cost,
      costKnown: row.costKnown === true,
      costField: row.costField || '',
      movementRevision: movement?.revision || 0,
      movementAt: movement?.atMs ? new Date(movement.atMs).toISOString() : '',
      observedAt: observed?.atMs ? new Date(observed.atMs).toISOString() : '',
      staticSince: staticSinceMs ? new Date(staticSinceMs).toISOString() : '',
      cutoff: cutoffMs ? new Date(cutoffMs).toISOString() : '',
      daysStatic,
      hasWindow,
      decision: qualifies ? 'DEAD_STOCK_INCLUDED' : (!hasWindow ? 'EXCLUDED_NO_7_DAY_WINDOW' : 'EXCLUDED_MOVED_WITHIN_WINDOW'),
    });
    if (!qualifies) return null;

    return {
      key,
      product: String(row.product || row.productName || row.name || row.description || row.product_name || 'Unnamed product').trim(),
      sku: String(row.sku || row.SKU || row.variant_sku || row.barcode || row.code || '').trim(),
      category: String(row.category || row.sourceCategory || row.product_category || '').trim(),
      soh,
      cost,
      costKnown: row.costKnown === true,
      costField: row.costField || '',
      tiedValue: soh * cost,
      daysStatic: Math.max(thresholdDays, daysStatic),
      lastMovementAt: movement?.atMs ? new Date(movement.atMs).toISOString() : '',
      lastMovementRevision: movement?.revision || 0,
      observedSinceAt: !movement?.atMs && staticSinceMs ? new Date(staticSinceMs).toISOString() : '',
    };
  }).filter(Boolean).sort((a, b) => b.tiedValue - a.tiedValue || b.daysStatic - a.daysStatic || a.product.localeCompare(b.product));

  const debugPayload = {
    scope,
    thresholdDays,
    mapping: {
      product: mapping.product || '',
      sku: mapping.sku || '',
      category: mapping.category || '',
      systemSoh: mapping.systemSoh || '',
      cost: mapping.cost || '',
    },
    source: {
      sourceRows: sourceRows.length,
      preparedRows: preparedRows.length,
      rowHeaders,
      inventoryHeaders,
      authoritativeMappedHeader,
      inferredScopedHeader,
      scopedInventoryHeader,
      siteCandidates,
      siteTokens,
    },
    evidence: {
      movements: ledger.length,
      trustedMovements: trustedLedger.length,
      syncActivity: observations.length,
      latestRevision: latestLedger?.revision || 0,
      latestRevisionStatus: String(latestLedger?.readiness?.status || ''),
      latestRevisionTrusted,
      latestAt: latestAtMs ? new Date(latestAtMs).toISOString() : '',
      cutoff: cutoffMs ? new Date(cutoffMs).toISOString() : '',
      oldestTrustedLedgerAt: oldestTrustedLedgerAtMs ? new Date(oldestTrustedLedgerAtMs).toISOString() : '',
      hasWindow,
      coverageDays: latestAtMs && oldestTrustedLedgerAtMs ? Math.floor((latestAtMs - oldestTrustedLedgerAtMs) / 86400000) : 0,
      revisions: trustedLedger.map((movement) => ({
        revision: movement?.revision || 0,
        at: movement?.timestamp || movement?.createdAt || '',
        status: movement?.readiness?.status || '',
        initial: Boolean(movement?.delta?.initial),
        stock: Array.isArray(movement?.delta?.changes?.stock) ? movement.delta.changes.stock.length : 0,
        added: Array.isArray(movement?.delta?.changes?.added) ? movement.delta.changes.added.length : 0,
        removed: Array.isArray(movement?.delta?.changes?.removed) ? movement.delta.changes.removed.length : 0,
      })),
    },
    products: deadStockDebugProducts,
    historicalContinuityDiagnostics,
  };

  if (options?.debug !== false && typeof window !== 'undefined' && typeof console !== 'undefined') {
    try {
      const signature = JSON.stringify({
        scope,
        header: scopedInventoryHeader,
        ledger: ledger.length,
        latestRevision: latestLedger?.revision || 0,
        hasWindow,
        included: products.length,
        productDecisions: deadStockDebugProducts.map((item) => [item.sku, item.soh, item.decision, item.movementRevision]),
      });
      if (window.__MC_DS_DEBUG_SIGNATURE__ !== signature) {
        window.__MC_DS_DEBUG_SIGNATURE__ = signature;
        console.groupCollapsed(`%c[DS DEBUG] Dead Stock · ${formatStoreLabel(scope || 'ALL')} · ${products.length} included`, 'color:#35d7ff;font-weight:700');
        console.log('Summary', debugPayload);
        console.log('SOH source resolution', debugPayload.source);
        console.table(debugPayload.evidence.revisions);
        console.table(deadStockDebugProducts);
        const chronicRows = deadStockDebugProducts.filter((item) => /chronic/i.test(`${item.product} ${item.sku}`));
        if (chronicRows.length) console.log('Chronic diagnostic', chronicRows);

        // DS1.15 forensic probe: show the exact per-revision SOH transitions for
        // products we are validating against the POS. This is intentionally
        // read-only. It lets us distinguish a Dead Stock classification bug from
        // an upstream snapshot/revision-history gap or a wrong site SOH column.
        const forensicSkus = new Set(['10403', '10841', '10860', '10906']);
        const forensicRows = [];
        trustedLedger.forEach((movement) => {
          const at = movement?.timestamp || movement?.createdAt || '';
          const revision = Number(movement?.revision || 0);
          const groups = movement?.delta?.changes || {};
          ['stock', 'added', 'removed'].forEach((group) => {
            (Array.isArray(groups[group]) ? groups[group] : []).forEach((item) => {
              const sku = String(item?.sku || '').trim();
              if (!forensicSkus.has(sku)) return;
              const sohField = (Array.isArray(item?.fields) ? item.fields : []).find((field) => String(field?.field || '').trim().toLowerCase() === 'soh');
              forensicRows.push({
                revision,
                at,
                group,
                sku,
                product: item?.product || '',
                before: sohField?.before ?? '',
                after: sohField?.after ?? '',
                difference: sohField?.difference ?? '',
                readiness: movement?.readiness?.status || '',
                source: movement?.source || '',
                importId: movement?.importId || movement?.reference || '',
              });
            });
          });
        });
        const forensicCurrent = deadStockDebugProducts
          .filter((item) => forensicSkus.has(String(item.sku || '').trim()))
          .map((item) => ({
            sku: item.sku,
            product: item.product,
            currentSoh: item.soh,
            deadStockDecision: item.decision,
            lastMovementRevision: item.movementRevision,
            lastMovementAt: item.movementAt,
          }));
        console.groupCollapsed('%c[DS FORENSIC] POS validation · SKUs 10403 / 10841 / 10860 / 10906', 'color:#ffcc55;font-weight:700');
        console.log('Current snapshot / Dead Stock decision');
        console.table(forensicCurrent);
        console.log('Exact canonical revision transitions');
        console.table(forensicRows.sort((a, b) => (a.revision - b.revision)));
        console.log('Interpretation: if a POS movement is missing here, the gap is upstream of Dead Stock. If a false transition appears here, inspect that revision snapshot/site SOH mapping.');
        console.groupEnd();
        window.__MC_DS_FORENSIC__ = { current: forensicCurrent, transitions: forensicRows };

        console.log('Tip: run window.__MC_DS_DEBUG__ in Console to inspect the latest payload; run window.__MC_DS_FORENSIC__ for the four POS-audited SKUs.');
        console.groupEnd();
      }
      window.__MC_DS_DEBUG__ = debugPayload;
    } catch (error) {
      console.warn('[DS DEBUG] Diagnostic logger failed', error);
    }
  }

  return {
    thresholdDays,
    hasWindow,
    latestRevisionTrusted,
    products,
    count: products.length,
    totalUnits: products.reduce((sum, item) => sum + item.soh, 0),
    tiedValue: products.reduce((sum, item) => sum + item.tiedValue, 0),
    missingCostCount: products.filter((item) => !item.costKnown).length,
    valuedCount: products.filter((item) => item.costKnown).length,
    latestAt: latestAtMs ? new Date(latestAtMs).toISOString() : '',
    // Classification time includes wall-clock age. PDF stock freshness must use
    // the actual imported snapshot/revision time, never that assessment clock.
    latestSnapshotAt: engine?.uploadedAt || engine?.updatedAt || engine?.syncedAt || latestLedger?.timestamp || latestLedger?.createdAt || latestObservation?.timestamp || '',
    oldestAt: oldestTrustedLedgerAtMs ? new Date(oldestTrustedLedgerAtMs).toISOString() : '',
    coverageDays: latestAtMs && oldestTrustedLedgerAtMs ? Math.floor((latestAtMs - oldestTrustedLedgerAtMs) / 86400000) : 0,
    // DS2.1: expose the exact DS1 per-product evidence model for read-only
    // cross-site analysis. This is intentionally not rendered and does not
    // create a second movement algorithm.
    productEvidence: deadStockDebugProducts,
  };
}

function storageReadinessView(movement = null) {
  const status = String(movement?.readiness?.status || '').trim();
  if (!movement) return { status: 'waiting', label: 'Waiting for revision', tone: 'neutral', ready: false };
  if (status === 'ready') return { status, label: 'Ready for analysis', tone: 'ready', ready: true };
  if (status === 'sync_failed') return { status, label: 'Revision sync failed', tone: 'error', ready: false };
  if (status === 'incomplete') return { status, label: 'Revision incomplete', tone: 'warning', ready: false };
  return { status: status || 'syncing', label: 'Preparing analysis', tone: 'syncing', ready: false };
}

// MC285 compile baseline: removes out-of-scope rowBased/rowSiteHeader/site reference.
function summarizeDistribution(results = []) {
  const succeeded = results.filter((item) => item.status === "ready" || (item.ok && !revisionPending(item))).length;
  const inventoryReady = results.filter((item) => revisionPending(item)).length;
  const skipped = results.filter((item) => item.skipped || item.status === "skipped").length;
  return { succeeded, inventoryReady, skipped, failed: results.length - succeeded - inventoryReady - skipped };
}

async function distributeInventoryTarget(target, context, assertActive, reportStage = () => {}) {
  assertActive();
  const { sourceSnapshot, sourceManifestId, sourceImportId, parsed, rowSiteHeader, batchId, fileName, actor } = context;
  const site = target.site;
  const rowBased = Boolean(rowSiteHeader);
  const targetSite = rowBased ? resolveSite(site, parsed.headers)?.siteId : site;
  const skipped = (reason) => ({ site, label: target.label || site, ok: false, skipped: true, status: "skipped", reason });
  if (!targetSite) return skipped("Source site requires mapping");

  const inventoryHeader = rowBased
    ? sourceSnapshot.mapping?.systemSoh
    : target.inventoryHeader || findInventoryHeaderForSite(site, parsed.headers);
  if (!inventoryHeader) return skipped("System SOH mapping is unresolved");
  const sourceRows = sourceSnapshot.rawRows?.length ? sourceSnapshot.rawRows : parsed.rows;
  const rows = sourceRows.filter((row) => rowBased
    ? String(row?.[rowSiteHeader] || "").trim() === site
    : row?.[inventoryHeader] !== null && row?.[inventoryHeader] !== undefined && String(row[inventoryHeader]).trim() !== "");
  if (!rows.length) return skipped("No inventory rows for this site");

  reportStage("Loading previous site snapshot");
  const previousSnapshot = await loadActiveInventorySnapshot(targetSite, { refreshCloud: false }).catch(() => null);
  assertActive();
  const aliasImportId = `${sourceImportId}__${normalizeStoreValue(targetSite) || targetSite}`;
  reportStage("Saving site inventory");
  const aliasResult = await commitActiveInventoryAlias(targetSite, {
    sourceScope: "ALL", sourceManifestId, sourceImportId, aliasImportId, inventoryHeader,
    rowSiteHeader: rowBased ? rowSiteHeader : "", sourceSiteValue: rowBased ? site : "",
    fileName, uploadedAt: sourceSnapshot.uploadedAt || new Date().toISOString(),
    rowCount: rows.length, columnCount: parsed.headers.length, distributionBatchId: batchId,
  }, sourceSnapshot, { assertActive });
  assertActive();
  if (!aliasResult?.ok) throw new Error(aliasResult?.err?.message || aliasResult?.reason || "Site pointer could not be saved");

  // Site inventory is already authoritative at this point. Defer CPU-heavy
  // prepared-row projection, revision and sync-history confirmation until after
  // the operator has been released from the blocking distribution dialog.
  const revisionWork = async (assertRevisionActive = () => {}, reportRevisionStage = () => {}) => {
    assertRevisionActive();
    reportRevisionStage("Preparing site revision");
    const snapshot = {
      ...sourceSnapshot, scope: targetSite, siteName: formatStoreLabel(targetSite),
      importId: aliasImportId, activeImportId: aliasImportId, csvText: "", rawRows: rows,
      mapping: { ...(sourceSnapshot.mapping || {}), systemSoh: inventoryHeader },
      config: { ...(sourceSnapshot.config || {}), storeName: formatStoreLabel(targetSite) },
      rowCount: rows.length,
      distributionContext: {
        ...(sourceSnapshot.distributionContext || {}), mode: "SITE", scope: targetSite,
        inheritedFrom: "ALL", rowSiteHeader: rowBased ? rowSiteHeader : "",
        sourceSiteValue: rowBased ? site : "", distributionBatchId: batchId,
      },
    };
    snapshot.preparedRows = buildPreparedData(rows, snapshot.mapping, snapshot.config, sourceSnapshot.rules);
    assertRevisionActive();
    const revisionResult = await recordInventoryImportMovement(
      resolveStorageAuthorityScope(targetSite), snapshot, actor,
      previousSnapshot?.snapshot || previousSnapshot || null,
      { awaitCloud: true, assertActive: assertRevisionActive, onStage: reportRevisionStage },
    );
    assertRevisionActive();
    if (!revisionResult?.ok) {
      const reason = revisionResult?.error?.message || revisionResult?.reason || "Site revision could not be saved";
      console.warn('[MC DISTRIBUTION] site revision not confirmed', {
        site: targetSite, reason, code: revisionResult?.error?.code || '',
      });
      throw new Error(reason);
    }
    return { ok: true, revisionResult };
  };
  return {
    site, targetSite, label: target.label || formatStoreLabel(site), ok: true,
    inventorySaved: true, revisionReady: false, skipped: false,
    status: "inventory_ready", reason: "Revision processing in background", revisionWork,
  };
}

export default function MorpheusCountImports() {
  const [siteRegistryVersion, setSiteRegistryVersion] = useState(0);

  const currentProfile = useCurrentMcProfile();
  const adminStoreView = useAdminStoreView();
  // The Intake route must react to the shared admin site selector immediately.
  // Reading the scope back through getActiveStoreScope() alone can leave this
  // mounted route on the previous snapshot until a tab navigation remounts it.
  // For admins, the reactive hook value is the selected scope authority; staff
  // profiles still use the normal profile/site access resolver.
  const activeStoreScope = isAdminProfile(currentProfile)
    ? (adminStoreView || "ALL")
    : getActiveStoreScope(currentProfile);
  const storageAuthorityScope = resolveStorageAuthorityScope(activeStoreScope);
  const [engine, setEngine] = useState(() => {
    const stored = loadEngineState(activeStoreScope) || buildDefaultState(activeStoreScope);
    const active = peekActiveInventorySnapshot(activeStoreScope);
    return active ? { ...stored, ...active, config: stored.config || active.config, rules: stored.rules || active.rules, mapping: active.mapping || stored.mapping } : { ...stored, rawRows: [], preparedRows: [], parsedHeaders: [] };
  });
  const [fileName, setFileName] = useState("");
  const [uploadedAt, setUploadedAt] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [showAdvancedCsv, setShowAdvancedCsv] = useState(false);
  const [csvSearchTerm, setCsvSearchTerm] = useState("");
  const [csvGoToRow, setCsvGoToRow] = useState("");
  const csvTableRef = useRef(null);
  const [overwritePrompt, setOverwritePrompt] = useState(false);
  const [overwriteScope, setOverwriteScope] = useState("");
  const [overwriteMatches, setOverwriteMatches] = useState([]);
  const [cycleBlockPrompt, setCycleBlockPrompt] = useState(null);
  const [posSettings, setPosSettings] = useState(() => loadPosSettings(activeStoreScope));
  const [posInventory, setPosInventory] = useState(() => loadPreparedInventory(activeStoreScope));
  const [posNotice, setPosNotice] = useState("");
  const [posBusy, setPosBusy] = useState(false);
  const [prepareBusy, setPrepareBusy] = useState(false);
  const [prepareStage, setPrepareStage] = useState("building");
  const [siteChoicePrompt, setSiteChoicePrompt] = useState(null);
  const [siteChoiceValue, setSiteChoiceValue] = useState("");
  const [distributionContext, setDistributionContext] = useState(() => ({
    mode: String(activeStoreScope || "ALL").toUpperCase() === "ALL" ? "ALL_SITES" : "SITE",
    scope: activeStoreScope || "ALL",
    selectedAt: "",
  }));
  const [distributionProgress, setDistributionProgress] = useState(null);
  const distributionOperationRef = useRef(null);
  // Do not cancel an active Firestore job on Intake route unmount. The module-level
  // registry protects it across internal Morpheus tab navigation.
  const [distributionHistory, setDistributionHistory] = useState(() => loadDistributionHistory());
  const [expandedDistributionBatch, setExpandedDistributionBatch] = useState("");
  const [retryingDistributionBatch, setRetryingDistributionBatch] = useState("");
  const [sections, setSections] = useState(() => activeSections());
  const [cycles, setCycles] = useState(() => activeCycles());
  const [storageMovements, setStorageMovements] = useState([]);
  const [storageSyncActivity, setStorageSyncActivity] = useState([]);
  const [storageLedgerLoading, setStorageLedgerLoading] = useState(true);
  const [storageRevisionProcessing, setStorageRevisionProcessing] = useState(false);
  const [selectedStorageMovement, setSelectedStorageMovement] = useState(null);
  const [expandedStorageGroups, setExpandedStorageGroups] = useState({});
  const [selectedStoragePattern, setSelectedStoragePattern] = useState(null);
  const [networkMovementPanel, setNetworkMovementPanel] = useState(null);
  const [networkMovementLoading, setNetworkMovementLoading] = useState(false);
  const [networkMovementError, setNetworkMovementError] = useState("");
  const [networkMovementFilter, setNetworkMovementFilter] = useState("");
  const [deadStockPdfBusy, setDeadStockPdfBusy] = useState(false);
  const [deadStockPdfError, setDeadStockPdfError] = useState('');
  const networkMovementRequestRef = useRef(0);
  const networkMovementModalRef = useRef(null);
  const networkEvidenceRef = useRef(null);
  const [networkEvidenceJump, setNetworkEvidenceJump] = useState(0);
  const networkEvidenceRows = (networkMovementPanel?.rows || []).filter((row) => !networkMovementFilter || row.networkSignal === networkMovementFilter);
  const networkEvidenceFilterLabel = networkMovementFilter ? movementLabel(networkMovementFilter) : 'All sites';
  const storageIntelligence = useMemo(() => buildStorageIntelligence(storageMovements[0] || null), [storageMovements]);
  const storagePatterns = useMemo(() => buildStoragePatterns(storageMovements), [storageMovements]);
  const deadStock = useMemo(() => buildDeadStockIntelligence(engine, storageMovements, storageSyncActivity, 7, activeStoreScope), [engine, storageMovements, storageSyncActivity, activeStoreScope]);
  const storageReadiness = useMemo(() => storageReadinessView(storageMovements[0] || null), [storageMovements]);
  const ds2NetworkCacheRef = useRef({ orgId: '', loadedAt: 0, sites: [] });

  // MC490 — Development-only page traffic scope. This only tags Firestore
  // operations that already happen while Intake is mounted; it adds no reads,
  // writes, listeners or polling of its own.

  // Network evidence is operator-triggered. Opening the view hydrates shared
  // site snapshots once, then reuses the evidence for five minutes across SKUs.
  // Shared global manifests and concurrent opens are deduplicated; there are no
  // background snapshot listeners or polling for this network view.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const CACHE_MS = 5 * 60 * 1000;
    const buildSiteEvidenceCache = async ({ refresh = false } = {}) => {
      const activeOrgId = String(getMcActiveOrgId?.() || 'local').trim() || 'local';
      const registry = loadSites().filter((site) => site?.active !== false);
      const scopeMap = new Map();
      registry.forEach((site) => {
        const scope = String(site?.siteId || site?.storeKey || site?.name || '').trim();
        if (!scope) return;
        const key = normalizeStoreValue(scope) || scope;
        scopeMap.set(key, { scope, label: site?.name || formatStoreLabel(scope) });
      });
      if (activeStoreScope && String(activeStoreScope).toUpperCase() !== 'ALL') {
        const sourceScope = resolveStorageAuthorityScope(activeStoreScope);
        const key = normalizeStoreValue(sourceScope) || sourceScope;
        if (!scopeMap.has(key)) scopeMap.set(key, { scope: sourceScope, label: formatStoreLabel(activeStoreScope) });
      }

      const scopeSignature = Array.from(scopeMap.keys()).sort().join('|');
      const cached = ds2NetworkCacheRef.current || { orgId: '', loadedAt: 0, sites: [] };
      const sameScope = cached.orgId === activeOrgId && cached.scopeSignature === scopeSignature;
      const cacheFresh = !refresh && sameScope && Array.isArray(cached.sites) && cached.sites.length
        && (Date.now() - Number(cached.loadedAt || 0) < CACHE_MS);
      if (cacheFresh) return cached;
      if (sameScope && cached.loadingPromise) return cached.loadingPromise;

      // Fresh deployed browsers have no sibling-site inventory cache. Hydrate
      // the shared snapshots on demand; the batch loader reads each shared
      // All Sites manifest once and applies each site's own inventory mapping.
      const loadingPromise = (async () => {
        const snapshots = await loadActiveInventorySnapshotsForScopes(
          Array.from(scopeMap.values(), (target) => target.scope)
        );
        // Deliberately sequential: DS2.1 is operator-triggered forensic work, so
        // predictable Firestore pressure is preferable to a burst across 10 sites.
        const sites = [];
        for (const target of scopeMap.values()) {
          const scope = target.scope;
          try {
            const snapshot = snapshots[scope] || null;
            const ledger = await loadStorageMovementLedger(scope).catch(() => []);
            const sync = await loadStorageSyncActivity(scope).catch(() => []);
            const stored = loadEngineState(scope) || buildDefaultState(scope);
            const scopedEngine = snapshot
              ? { ...stored, ...snapshot, config: stored?.config || snapshot?.config, rules: stored?.rules || snapshot?.rules, mapping: snapshot?.mapping || stored?.mapping }
              : stored;
            const intelligence = buildDeadStockIntelligence(scopedEngine, ledger, sync, 7, scope, { debug: false });
            sites.push({
              scope,
              label: target.label || formatStoreLabel(scope),
              latestRevision: Number(ledger?.[0]?.revision || 0),
              latestRevisionAt: ledger?.[0]?.timestamp || ledger?.[0]?.createdAt || '',
              // Snapshot freshness is separate from SKU movement. Reopening the
              // panel or importing unchanged stock must not fabricate velocity.
              latestSnapshotAt: scopedEngine?.uploadedAt || scopedEngine?.updatedAt || scopedEngine?.syncedAt || '',
              latestRevisionTrusted: intelligence?.latestRevisionTrusted === true,
              hasWindow: Boolean(intelligence?.hasWindow),
              coverageDays: Number(intelligence?.coverageDays || 0),
              evidence: Array.isArray(intelligence?.productEvidence) ? intelligence.productEvidence : [],
              // DS2.5: retain the already-loaded canonical ledger in the short-lived
              // network cache so velocity is derived locally with zero extra reads.
              ledger: Array.isArray(ledger) ? ledger : [],
            });
          } catch (error) {
            sites.push({ scope, label: target.label || formatStoreLabel(scope), error: String(error?.message || error), evidence: [] });
          }
        }

        if ((String(getMcActiveOrgId?.() || 'local').trim() || 'local') !== activeOrgId) {
          throw new Error('Organisation changed. Please reopen network movement.');
        }
        const next = { orgId: activeOrgId, scopeSignature, loadedAt: Date.now(), sites };
        if (ds2NetworkCacheRef.current?.loadingPromise === loadingPromise) {
          ds2NetworkCacheRef.current = next;
          window.__MC_DS2_NETWORK_CACHE__ = next;
        }
        return next;
      })();

      // Concurrent product opens share one request, rather than repeating the
      // network reads before the first five-minute cache has finished loading.
      ds2NetworkCacheRef.current = { orgId: activeOrgId, scopeSignature, loadedAt: 0, sites: [], loadingPromise };
      try {
        return await loadingPromise;
      } finally {
        if (ds2NetworkCacheRef.current?.loadingPromise === loadingPromise) {
          ds2NetworkCacheRef.current = { orgId: activeOrgId, scopeSignature, loadedAt: 0, sites: [] };
        }
      }
    };

    const probeNetwork = async (skuOrKey = '', options = {}) => {
      const requested = String(skuOrKey || '').trim();
      if (!requested) {
        console.info("[DS2.1 NETWORK] Pass a SKU, for example: await window.__MC_DS2_NETWORK__('10032')");
        return [];
      }
      const wanted = requested.toLowerCase();
      const cache = await buildSiteEvidenceCache({ refresh: options?.refresh === true });
      const rows = (cache.sites || []).map((site) => {
        const evidence = (site.evidence || []).find((item) => {
          const sku = String(item?.sku || '').trim().toLowerCase();
          const key = String(item?.key || '').trim().toLowerCase();
          return sku === wanted || key === wanted || key === `sku:${wanted}`;
        }) || null;

        let signal = 'NO_EVIDENCE';
        const hasObservedMovement = Boolean(evidence?.movementAt) && Number(evidence?.movementRevision || 0) > 0;
        const daysStatic = Number(evidence?.daysStatic || 0);
        if (site.error) signal = 'ERROR';
        else if (!evidence) signal = 'NO_CURRENT_OBSERVATION';
        else if (!(Number(evidence.soh) > 0)) signal = 'OUT_OF_STOCK';
        else if (!site.hasWindow || !evidence.staticSince) signal = 'NO_EVIDENCE';
        // DS2.1.1: a recent observation is not proof of movement. Only a
        // canonical SOH transition can earn MOVING/SLOW. A positive-SOH SKU
        // with no transition yet stays OBSERVED until enough static evidence
        // exists to classify it DEAD.
        else if (!hasObservedMovement && daysStatic < 14) signal = 'OBSERVED';
        else if (hasObservedMovement && daysStatic < 7) signal = 'MOVING';
        else if (hasObservedMovement && daysStatic < 14) signal = 'SLOW';
        else signal = 'DEAD';

        // DS2.5 — Movement Velocity. Reconstruct this SKU's observed SOH path
        // from revision AFTER values only. We deliberately ignore legacy BEFORE
        // values because DS1 proved those can contain historical projection noise.
        // The first numeric observation is a baseline; subsequent distinct AFTER
        // values are canonical observation-to-observation movement boundaries.
        const velocityObservations = [];
        const chronologicalLedger = [...(Array.isArray(site.ledger) ? site.ledger : [])].sort((a, b) => {
          const atA = new Date(a?.timestamp || a?.createdAt || 0).getTime() || 0;
          const atB = new Date(b?.timestamp || b?.createdAt || 0).getTime() || 0;
          return atA - atB || Number(a?.revision || 0) - Number(b?.revision || 0);
        });
        chronologicalLedger.forEach((movement) => {
          if (String(movement?.readiness?.status || '') === 'sync_failed') return;
          const groups = movement?.delta?.changes || {};
          for (const group of ['stock', 'added']) {
            for (const change of (Array.isArray(groups[group]) ? groups[group] : [])) {
              const changeSku = String(change?.sku || '').trim().toLowerCase();
              if (changeSku !== wanted) continue;
              const sohField = (Array.isArray(change?.fields) ? change.fields : []).find((field) => String(field?.field || '').trim().toLowerCase() === 'soh');
              const rawAfter = sohField?.after;
              const after = Number(rawAfter);
              if (rawAfter === '' || rawAfter == null || !Number.isFinite(after)) continue;
              const at = movement?.timestamp || movement?.createdAt || '';
              const atMs = new Date(at).getTime();
              if (!atMs) continue;
              const prior = velocityObservations[velocityObservations.length - 1];
              if (!prior || prior.soh !== after) velocityObservations.push({ at, atMs, soh: after, revision: Number(movement?.revision || 0) });
            }
          }
        });
        // Ensure the current observation closes the path when the latest snapshot
        // differs from the last ledger observation. Its timestamp is the latest
        // site revision, not wall-clock time, so refreshes never invent velocity.
        if (evidence && Number.isFinite(Number(evidence.soh))) {
          const currentSoh = Number(evidence.soh);
          const prior = velocityObservations[velocityObservations.length - 1];
          const currentAt = site.latestRevisionAt || evidence?.movementAt || evidence?.observedAt || '';
          const currentAtMs = new Date(currentAt).getTime();
          if (currentAtMs && (!prior || prior.soh !== currentSoh)) velocityObservations.push({ at: currentAt, atMs: currentAtMs, soh: currentSoh, revision: Number(site.latestRevision || 0) });
        }
        let depletedUnits = 0;
        let replenishedUnits = 0;
        let lastDepletionAt = '';
        let lastReplenishmentAt = '';
        for (let i = 1; i < velocityObservations.length; i += 1) {
          const diff = velocityObservations[i].soh - velocityObservations[i - 1].soh;
          if (diff < 0) {
            depletedUnits += Math.abs(diff);
            lastDepletionAt = velocityObservations[i].at;
          } else if (diff > 0) {
            replenishedUnits += diff;
            lastReplenishmentAt = velocityObservations[i].at;
          }
        }
        const velocityFirst = velocityObservations[0] || null;
        const velocityLast = velocityObservations[velocityObservations.length - 1] || null;
        const velocityDays = velocityFirst && velocityLast ? Math.max(0, (velocityLast.atMs - velocityFirst.atMs) / 86400000) : 0;
        const depletionPerDay = velocityDays > 0 ? depletedUnits / velocityDays : 0;
        const velocityConfidence = velocityObservations.length >= 4 && velocityDays >= 3 ? 'ESTABLISHING' : velocityObservations.length >= 2 ? 'DEVELOPING' : 'INSUFFICIENT';

        return {
          site: site.label,
          scope: site.scope,
          sku: evidence?.sku || requested,
          product: evidence?.product || '',
          cost: evidence?.cost ?? null,
          costKnown: evidence?.costKnown === true,
          costField: evidence?.costField || '',
          soh: evidence ? Number(evidence.soh || 0) : '',
          daysStatic: evidence?.daysStatic ?? '',
          lastObservedMovement: evidence?.movementAt || '',
          movementRevision: Number(evidence?.movementRevision || 0),
          observedSince: evidence?.observedAt || '',
          hasObservedMovement,
          ds1Decision: evidence?.decision || '',
          networkSignal: signal,
          latestSiteRevision: Number(site.latestRevision || 0),
          latestSnapshotAt: site.latestSnapshotAt || '',
          latestRevisionTrusted: site.latestRevisionTrusted === true,
          coverageDays: Number(site.coverageDays || 0),
          velocity: {
            observations: velocityObservations.length,
            firstSoh: velocityFirst?.soh ?? '',
            latestSoh: velocityLast?.soh ?? '',
            observedDays: Number(velocityDays.toFixed(2)),
            // DS2.6A gates use the exact span, not a rounded 2.99 → 3.00.
            observedDaysExact: velocityDays,
            depletedUnits,
            replenishedUnits,
            lastDepletionAt,
            lastReplenishmentAt,
            depletionPerDay: Number(depletionPerDay.toFixed(2)),
            confidence: velocityConfidence,
          },
          error: site.error || '',
        };
      }).sort((a, b) => {
        const rank = { MOVING: 0, SLOW: 1, OBSERVED: 2, DEAD: 3, OUT_OF_STOCK: 4, NO_EVIDENCE: 5, NO_CURRENT_OBSERVATION: 6, ERROR: 7 };
        return (rank[a.networkSignal] ?? 99) - (rank[b.networkSignal] ?? 99) || String(a.site).localeCompare(String(b.site));
      });

      const productName = rows.find((row) => row.product)?.product || requested;
      const summary = {
        sku: requested,
        product: productName,
        generatedAt: new Date().toISOString(),
        cacheLoadedAt: new Date(cache.loadedAt).toISOString(),
        sites: rows.length,
        moving: rows.filter((row) => row.networkSignal === 'MOVING').length,
        slow: rows.filter((row) => row.networkSignal === 'SLOW').length,
        observed: rows.filter((row) => row.networkSignal === 'OBSERVED').length,
        dead: rows.filter((row) => row.networkSignal === 'DEAD').length,
        outOfStock: rows.filter((row) => row.networkSignal === 'OUT_OF_STOCK').length,
        noEvidence: rows.filter((row) => ['NO_EVIDENCE', 'NO_CURRENT_OBSERVATION'].includes(row.networkSignal)).length,
      };

      console.groupCollapsed(`%c[DS2.1 NETWORK] ${productName} · SKU ${requested} · ${rows.length} site(s)`, 'color:#62f5c8;font-weight:700');
      console.log('Summary', summary);
      console.table(rows);
      console.log('Evidence rule: MOVING/SLOW require a genuine canonical SOH transition · OBSERVED means positive SOH but no transition proven yet · DEAD requires 14+ days static evidence · OUT_OF_STOCK SOH<=0. Revision dates alone never count as movement.');
      console.log('Cost rule: on-demand only, cached for 5 minutes, no listeners/polling/writes. Use __MC_DS2_NETWORK_REFRESH__ only when a fresh cloud read is required.');
      console.groupEnd();
      window.__MC_DS2_NETWORK_LAST__ = { summary, rows };
      return rows;
    };

    window.__MC_DS2_NETWORK__ = probeNetwork;
    window.__MC_DS2_NETWORK_REFRESH__ = (sku = '') => probeNetwork(sku, { refresh: true });
    window.__MC_DS2_NETWORK_CLEAR_CACHE__ = () => {
      ds2NetworkCacheRef.current = { orgId: '', loadedAt: 0, sites: [] };
      window.__MC_DS2_NETWORK_CACHE__ = ds2NetworkCacheRef.current;
      console.info('[DS2.1 NETWORK] cache cleared');
    };

    return () => {
      if (window.__MC_DS2_NETWORK__ === probeNetwork) delete window.__MC_DS2_NETWORK__;
      delete window.__MC_DS2_NETWORK_REFRESH__;
      delete window.__MC_DS2_NETWORK_CLEAR_CACHE__;
    };
  }, [activeStoreScope, siteRegistryVersion]);

  const exportDeadStock = async (event) => {
    if (deadStockPdfBusy) return;
    setDeadStockPdfBusy(true);
    setDeadStockPdfError('');
    const reportThemeElement = event.currentTarget;
    // Capture the selected scope and snapshot before the on-demand module load.
    const payload = { deadStock, scope: activeStoreScope, site: formatStoreLabel(activeStoreScope), exportedAt: new Date().toISOString() };
    const selectedTheme = reportThemeElement.closest('.mc-theme-light') ? true : reportThemeElement.closest('.mc-theme-dark') ? false : undefined;
    try {
      const { exportDeadStockPdf } = await import('../utils/mcDeadStockPdfExport');
      exportDeadStockPdf(payload, { light: selectedTheme });
    } catch (_) { setDeadStockPdfError('The PDF could not be created. Please try again.'); }
    finally { setDeadStockPdfBusy(false); }
  };

  const openNetworkMovement = async (item) => {
    const sku = String(item?.sku || '').trim();
    if (!sku) return;
    const requestId = ++networkMovementRequestRef.current;
    const sourceScope = resolveStorageAuthorityScope(activeStoreScope);
    setNetworkMovementFilter("");
    setNetworkEvidenceJump(0);
    setNetworkMovementPanel({ item, sourceScope, summary: null, rows: [] });
    setNetworkMovementLoading(true);
    setNetworkMovementError('');
    try {
      const probe = typeof window !== 'undefined' ? window.__MC_DS2_NETWORK__ : null;
      if (typeof probe !== 'function') throw new Error('Network evidence engine is not ready yet.');
      const rows = await probe(sku);
      if (networkMovementRequestRef.current !== requestId) return;
      const last = typeof window !== 'undefined' ? window.__MC_DS2_NETWORK_LAST__ : null;
      setNetworkMovementPanel({
        item,
        sourceScope,
        summary: last?.summary?.sku === sku ? last.summary : { sku, generatedAt: new Date().toISOString() },
        rows: Array.isArray(rows) ? rows : [],
      });
    } catch (error) {
      if (networkMovementRequestRef.current === requestId) setNetworkMovementError(String(error?.message || error || 'Unable to load network movement.'));
    } finally {
      if (networkMovementRequestRef.current === requestId) setNetworkMovementLoading(false);
    }
  };

  const closeNetworkMovement = () => {
    networkMovementRequestRef.current += 1;
    setNetworkMovementPanel(null);
    setNetworkMovementFilter("");
    setNetworkEvidenceJump(0);
    setNetworkMovementLoading(false);
    setNetworkMovementError('');
  };

  const showMovingSiteEvidence = () => {
    setNetworkMovementFilter('MOVING');
    // A separate jump request works even when MOVING is already selected.
    // React commits the filtered rows before this effect moves focus/scroll.
    setNetworkEvidenceJump((request) => request + 1);
  };

  useEffect(() => {
    if (!networkEvidenceJump) return;
    const evidence = networkEvidenceRef.current;
    const modal = networkMovementModalRef.current;
    if (!evidence || !modal) return;
    const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    // Scroll only this modal; do not shift the Intake page behind the overlay.
    const top = Math.max(0, modal.scrollTop + evidence.getBoundingClientRect().top - modal.getBoundingClientRect().top - modal.clientTop - 16);
    evidence.focus({ preventScroll: true });
    modal.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [networkEvidenceJump]);

  useEffect(() => {
    networkMovementRequestRef.current += 1;
    setNetworkMovementPanel(null);
    setNetworkMovementFilter("");
    setNetworkEvidenceJump(0);
    setNetworkMovementError('');
    setNetworkMovementLoading(false);
    return () => { networkMovementRequestRef.current += 1; };
  }, [activeStoreScope]);

  useEffect(() => {
    const resolvedScope = activeStoreScope || "ALL";
    const storedContext = engine?.distributionContext;
    setDistributionContext({
      mode: storedContext?.mode || (String(resolvedScope).toUpperCase() === "ALL" ? "ALL_SITES" : "SITE"),
      scope: storedContext?.scope || resolvedScope,
      selectedAt: storedContext?.selectedAt || "",
    });
  }, [activeStoreScope, engine?.distributionContext?.mode, engine?.distributionContext?.scope, engine?.distributionContext?.selectedAt]);

  useEffect(() => {
    const syncHistory = (event) => {
      const next = Array.isArray(event?.detail) ? event.detail : loadDistributionHistory();
      setDistributionHistory(next);
    };
    syncHistory();
    window.addEventListener("mc-distribution-history-updated", syncHistory);
    window.addEventListener("mc-cloud-sync", syncHistory);
    return () => {
      window.removeEventListener("mc-distribution-history-updated", syncHistory);
      window.removeEventListener("mc-cloud-sync", syncHistory);
    };
  }, []);

  useEffect(() => {
    const syncCycles = () => setCycles(activeCycles());
    syncCycles();
    window.addEventListener(MC_CYCLE_EVENT, syncCycles);
    window.addEventListener("storage", syncCycles);
    window.addEventListener("mc-cloud-sync", syncCycles);
    return () => {
      window.removeEventListener(MC_CYCLE_EVENT, syncCycles);
      window.removeEventListener("storage", syncCycles);
      window.removeEventListener("mc-cloud-sync", syncCycles);
    };
  }, []);

  useEffect(() => {
    const syncSections = () => setSections(activeSections());
    syncSections();
    window.addEventListener(MC_SECTION_EVENT, syncSections);
    window.addEventListener("storage", syncSections);
    window.addEventListener("mc-cloud-sync", syncSections);
    return () => {
      window.removeEventListener(MC_SECTION_EVENT, syncSections);
      window.removeEventListener("storage", syncSections);
      window.removeEventListener("mc-cloud-sync", syncSections);
    };
  }, []);

  const cycleOptions = useMemo(() => cycles.filter((cycle) => cycle?.active !== false).map((cycle) => ({
    value: normalizeCycleId(cycle.cycleId || cycle.id || cycle.name),
    label: cycle.name || formatCycleLabel(cycle.cycleId || cycle.id),
    cycleId: normalizeCycleId(cycle.cycleId || cycle.id || cycle.name),
    cycleName: cycle.name || formatCycleLabel(cycle.cycleId || cycle.id),
  })), [cycles]);

  const sectionScopeOptions = useMemo(() => {
    const active = sections.filter((section) => section?.active !== false);
    const sectionOptions = active.map((section) => ({
      value: normalizeSectionId(section.sectionId || section.id || section.name),
      label: section.name || formatSectionLabel(section.sectionId || section.id),
      sectionId: normalizeSectionId(section.sectionId || section.id || section.name),
      sectionName: section.name || formatSectionLabel(section.sectionId || section.id),
      fullSite: false,
    }));

    if (isAdminProfile(currentProfile)) {
      return [
        { value: 'FULL_SITE', label: 'Full Site Count', sectionId: '', sectionName: '', fullSite: true },
        ...sectionOptions,
      ];
    }

    const allowed = new Set(getProfileAllowedSectionIds(currentProfile, active).map(normalizeSectionId).filter(Boolean));
    const assignedOptions = sectionOptions.filter((option) => allowed.has(normalizeSectionId(option.sectionId || option.value)));
    const hasEveryActiveSection = active.length > 0 && assignedOptions.length === active.length;

    return hasEveryActiveSection
      ? [{ value: 'FULL_SITE', label: 'Full Site Count', sectionId: '', sectionName: '', fullSite: true }, ...assignedOptions]
      : assignedOptions;
  }, [currentProfile, sections]);
  const [selectedCycleScope, setSelectedCycleScope] = useState('');
  const [selectedSectionScope, setSelectedSectionScope] = useState('');
  const [selectedCategoryScopes, setSelectedCategoryScopes] = useState([]);
  const forcedImportScopeRef = useRef("");
  // Keep the newest user-selected CSV authoritative in memory while Firestore
  // finishes replacing the previous import snapshot. A delayed cloud replay may
  // still arrive seconds after upload; it must never make the UI fall back to an
  // older, smaller import.
  const latestManualImportRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // MC491 — removed the old DS1.13 cloud alias probe. Dev Traffic now gives
  // authoritative diagnostics without reading every legacy Storage History key
  // each time Intake mounts or the selected site changes.

  useEffect(() => {
    let cancelled = false;
    setStorageLedgerLoading(true);
    setStorageMovements([]);
    setStorageSyncActivity([]);
    Promise.all([
      loadStorageMovementLedger(storageAuthorityScope),
      loadStorageSyncActivity(storageAuthorityScope),
    ]).then(([entries, syncEntries]) => {
      if (!cancelled) {
        setStorageMovements(entries);
        setStorageSyncActivity(syncEntries);
        setStorageLedgerLoading(false);
      }
    }).catch(() => { if (!cancelled) setStorageLedgerLoading(false); });
    // COST C3: storage history is on-demand. A different browser sees new
    // revisions when Intake is reopened/refreshed; no background listener.
    return () => { cancelled = true; };
  }, [storageAuthorityScope]);

  useEffect(() => {
    if (!selectedStorageMovement) { setExpandedStorageGroups({}); return; }
    const first = STORAGE_CHANGE_GROUPS.find((group) => (selectedStorageMovement.delta?.changes?.[group.key] || []).length);
    setExpandedStorageGroups(first ? { [first.key]: true } : {});
  }, [selectedStorageMovement]);

  useEffect(() => {
    let cancelled = false;
    const refreshVisibleImport = async () => {
      const pending = latestManualImportRef.current;
      const sameScope = pending && normalizeStoreValue(pending.scope || "ALL") === normalizeStoreValue(activeStoreScope || "ALL");
      // Render the durable IndexedDB snapshot immediately. Firestore refreshes
      // in the background through the live subscription below, so a slow network
      // can never leave the Import page empty for 15-20 seconds after refresh.
      const stored = await loadAuthoritativeImportEngineAsync(activeStoreScope, { refreshCloud: false });
      const next = sameScope && shouldKeepFreshManualImport(pending.engine, stored)
        ? pending.engine
        : (stored || buildDefaultState(activeStoreScope));

      // Hydration is read-only here. Never write the full engine from a render or
      // sync event; doing so can overwrite profiles, sections, cycles and archives
      // with a partial browser snapshot.
      if (cancelled) return;
      setEngine(next);
      setFileName(next.fileName || "");
      setUploadedAt(next.uploadedAt || next.lastUploadedAt || "");
      setPosSettings(loadPosSettings(activeStoreScope));
      setPosInventory(loadPreparedInventory(activeStoreScope));
      setPosNotice("");
    };

    let unsubscribeShared = () => {};
    const connectSharedSnapshot = () => {
      try { unsubscribeShared(); } catch (_) {}
      unsubscribeShared = subscribeActiveInventorySnapshot(
        activeStoreScope,
        () => refreshVisibleImport(),
        (err) => console.warn("Shared active inventory subscription failed", err),
      );
    };

    refreshVisibleImport();
    connectSharedSnapshot();

    // Stale-while-revalidate: local storage paints first, then the newest shared
    // Firestore snapshot is reconciled without blocking the visible inventory.
    loadAuthoritativeImportEngineAsync(activeStoreScope, { refreshCloud: true })
      .then((shared) => {
        if (!cancelled && shared && !shared.isActiveImportHydrating) {
          const pending = latestManualImportRef.current;
          const sameScope = pending && normalizeStoreValue(pending.scope || "ALL") === normalizeStoreValue(activeStoreScope || "ALL");
          const next = sameScope && shouldKeepFreshManualImport(pending.engine, shared) ? pending.engine : shared;
          setEngine(next);
          setFileName(next.fileName || "");
          setUploadedAt(next.uploadedAt || next.lastUploadedAt || "");
        }
      })
      .catch(() => {});

    const onSyncReady = () => { refreshVisibleImport(); connectSharedSnapshot(); };
    window.addEventListener(MC_ACTIVE_INVENTORY_EVENT, refreshVisibleImport);
    window.addEventListener("mc-cloud-sync-ready", onSyncReady);
    return () => {
      cancelled = true;
      try { unsubscribeShared(); } catch (_) {}
      window.removeEventListener(MC_ACTIVE_INVENTORY_EVENT, refreshVisibleImport);
      window.removeEventListener("mc-cloud-sync-ready", onSyncReady);
    };
  }, [activeStoreScope, currentProfile, adminStoreView]);


  const rawRows = (Array.isArray(engine.rawRows) ? engine.rawRows : []).filter((row) => row && typeof row === "object");
  const headers = Array.isArray(engine.parsedHeaders) && engine.parsedHeaders.length
    ? engine.parsedHeaders
    : Object.keys(rawRows[0] || {});
  const detectedColumns = useMemo(() => headers.length, [headers]);
  const previewRows = useMemo(() => rawRows.slice(0, 10), [rawRows]);
  const duplicateSummary = useMemo(() => buildDuplicateMergeSummary(rawRows, engine.mapping), [rawRows, engine.mapping]);
  const csvGridRows = useMemo(() => {
    const term = String(csvSearchTerm || "").trim().toLowerCase();
    if (!term) return rawRows;
    return rawRows.filter((row) => headers.some((header) => String(row?.[header] ?? "").toLowerCase().includes(term)));
  }, [rawRows, headers, csvSearchTerm]);

  const handleCsvGoToRow = () => {
    const requested = Number.parseInt(String(csvGoToRow || ""), 10);
    if (!Number.isFinite(requested) || requested < 1 || requested > rawRows.length) return;
    setCsvSearchTerm("");
    window.requestAnimationFrame(() => {
      const target = csvTableRef.current?.querySelector(`[data-source-row="${requested}"]`);
      target?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  };
  const detectedSiteColumn = useMemo(() => {
    const selectedScope = String(activeStoreScope || "ALL").trim();
    if (selectedScope && selectedScope !== "ALL") return selectedScope;

    const mapped = String(engine?.mapping?.systemSoh || "").trim();
    if (mapped) return mapped;

    const headerMatch = headers.find((header) => /^inventory_/i.test(String(header || "")));
    return headerMatch || "";
  }, [activeStoreScope, engine?.mapping?.systemSoh, headers]);

  const globalPosSettings = useMemo(() => loadPosSettings("ALL"), [posSettings?.updatedAt, activeStoreScope]);
  const dataSourceMode = String(posSettings?.dataSourceMode || globalPosSettings?.dataSourceMode || engine.config?.dataSourceMode || "csv").toLowerCase();
  const isPosMode = dataSourceMode === "pos";
  const providerInfo = getPosProvider(posSettings?.provider);
  const providerLabel = providerInfo.label;
  const effectivePosStatusValue = posSettings?.status;
  const posStatus = getPosStatusMeta(effectivePosStatusValue || posSettings?.status);
  const posReadyToSync = providerInfo.available && isPosConnectionReady(effectivePosStatusValue);
  const posHasSyncedInventory = Boolean((posInventory.rows?.length || 0) > 0 && (posSettings?.lastSyncedAt || posInventory.syncedAt));
  const isAllSitesScope = String(activeStoreScope || "ALL").toUpperCase() === "ALL";
  const mustChooseSite = isAdminProfile(currentProfile) && isAllSitesScope;
  useEffect(() => {
    const refreshSites = () => setSiteRegistryVersion((version) => version + 1);
    window.addEventListener(MC_SITE_EVENT, refreshSites);
    window.addEventListener(MC_ACTIVE_INVENTORY_EVENT, refreshSites);
    return () => {
      window.removeEventListener(MC_SITE_EVENT, refreshSites);
      window.removeEventListener(MC_ACTIVE_INVENTORY_EVENT, refreshSites);
    };
  }, []);

  const siteChoiceOptions = useMemo(() => {
    const addUnique = (map, value) => {
      const raw = String(value || "").trim();
      const normalized = normalizeStoreValue(raw);
      if (!raw || !normalized || normalized === "ALL") return;
      const key = formatStoreLabel(normalized).toLowerCase().replace(/[^a-z0-9]+/g, "");
      // Keep the modal aligned with the real MC site dropdown.
      // Do not add POS inventory headers here: Lightspeed can expose fallback
      // outlets like "Main Outlet" that are not active MC sites.
      if (!map.has(key)) map.set(key, raw);
    };

    const activeSessionSiteKeys = new Set(
      loadSessionRecords()
        .map((record) => record?.siteId || record?.storeKey || record?.storeName || record?.store || record?.siteName)
        .map((value) => formatStoreLabel(value).toLowerCase().replace(/[^a-z0-9]/g, ""))
        .filter(Boolean)
    );

    const unique = new Map();
    const allSnapshot = peekActiveInventorySnapshot("ALL");
    const allHeaders = Array.isArray(allSnapshot?.parsedHeaders) ? allSnapshot.parsedHeaders : [];
    if (detectSiteHeaders(allHeaders).length) ensureSitesFromHeaders(allHeaders);
    loadSites()
      .filter((site) => site?.active !== false)
      .filter((site) => {
        const labelKey = formatStoreLabel(site?.name || site?.storeKey || site?.siteId).toLowerCase().replace(/[^a-z0-9]/g, "");
        if (labelKey !== "mainoutlet") return true;
        return activeSessionSiteKeys.has("mainoutlet");
      })
      .forEach((site) => addUnique(unique, site.siteId || site.storeKey || site.name));

    return Array.from(unique.values()).sort((a, b) => formatStoreLabel(a).localeCompare(formatStoreLabel(b)));
  }, [activeStoreScope, currentProfile, adminStoreView, siteRegistryVersion]);

  const distributionChoiceOptions = useMemo(() => {
    if (!mustChooseSite) return siteChoiceOptions;
    return ["ALL", ...siteChoiceOptions];
  }, [mustChooseSite, siteChoiceOptions]);

  const requestSiteChoice = (reason, payload = null) => {
    const distribution = mustChooseSite && ["import", "picker", "pos-sync"].includes(reason);
    const options = distribution ? distributionChoiceOptions : siteChoiceOptions;
    const fallback = distribution ? "ALL" : (options[0] || "");
    setSiteChoiceValue((prev) => (prev && options.includes(prev) ? prev : fallback));
    setSiteChoicePrompt({ reason, payload, distribution });
  };

  const closeSiteChoicePrompt = () => {
    setSiteChoicePrompt(null);
    setSiteChoiceValue("");
  };

  useEffect(() => {
    setSelectedSectionScope((prev) => (prev && sectionScopeOptions.some((option) => option.value === prev) ? prev : ''));
  }, [sectionScopeOptions]);

  useEffect(() => {
    if (!isPosMode) return;
    if (!posInventory?.rows?.length) return;
    const hasEngineRows = Array.isArray(engine?.rawRows) && engine.rawRows.length > 0;
    if (hasEngineRows && String(engine?.config?.dataSourceMode || "").toLowerCase() === "pos") return;
    const latestPosSettings = loadPosSettings(activeStoreScope);
    const hydrated = buildEngineFromPreparedInventory(engine, posInventory.rows, {
      provider: posInventory.provider || latestPosSettings?.provider || "lightspeed",
      syncedAt: latestPosSettings?.lastSyncedAt || posInventory.syncedAt || new Date().toISOString(),
      scope: activeStoreScope,
      posFieldMapping: latestPosSettings?.fieldMapping || {},
    });
    setEngine(hydrated);
    setFileName(hydrated.fileName || `${providerLabel} inventory`);
    setUploadedAt(hydrated.uploadedAt || "");
    saveEngineState(hydrated, activeStoreScope);
  }, [isPosMode, activeStoreScope, posInventory?.rows?.length, posSettings?.updatedAt]);

  const getCallableOrgId = () => {
    const orgId = getMcActiveOrgId();
    if (!orgId) {
      setPosNotice("MC is still resolving this org. Refresh once, then click Test Connection again.");
      return "";
    }
    return orgId;
  };


  const getPosCallableSiteId = (scopeOverride = activeStoreScope) => {
    const scope = String(scopeOverride || "ALL").trim() || "ALL";
    if (!scope || scope.toUpperCase() === "ALL") return "ALL";
    const latestSettings = loadPosSettings(scope);
    const mappings = latestSettings?.outletMappings || {};
    const site = resolveSite(scope);
    const candidates = [
      mappings[scope],
      mappings[site?.siteId],
      mappings[site?.storeKey],
      mappings[site?.name],
    ].filter((value) => String(value || "").trim());
    // Lightspeed currently returns the correct org inventory when unfiltered.
    // If no explicit MC Site → POS Outlet mapping exists, fetch unfiltered and
    // save the returned normalized rows under the selected MC site scope.
    return String(candidates[0] || "ALL").trim();
  };

  const hydratePosEngine = async (rows = [], meta = {}) => {
    const updateVisibleState = meta.updateVisibleState !== false;
    const targetScope = String(meta.scope || activeStoreScope || "ALL").trim() || "ALL";
    const syncedAt = meta.syncedAt || new Date().toISOString();
    const activePosSettings = loadPosSettings(targetScope);
    const normalizedRows = normalizeInventoryRows(rows, activePosSettings?.fieldMapping || {}, {
      source: meta.source || `${getPosProvider(activePosSettings?.provider).id}-live`,
      provider: meta.provider || activePosSettings?.provider || posSettings?.provider || "lightspeed",
      syncedAt,
      scope: targetScope,
    });

    const inventoryPayload = savePreparedInventory(normalizedRows, targetScope, {
      source: meta.source || `${getPosProvider(activePosSettings?.provider).id}-live`,
      provider: meta.provider || activePosSettings?.provider || posSettings?.provider || "lightspeed",
      syncedAt,
    });
    const nextSettings = savePosSettings({
      dataSourceMode: "pos",
      status: meta.status || "synced_live",
      provider: meta.provider || activePosSettings?.provider || posSettings?.provider || "lightspeed",
      providerLabel: getPosProvider(meta.provider || activePosSettings?.provider).label,
      lastSyncedAt: syncedAt,
    }, targetScope);
    const nextEngine = buildEngineFromPreparedInventory(engine, inventoryPayload.rows, {
      provider: nextSettings.provider || "lightspeed",
      syncedAt,
      scope: targetScope,
      posFieldMapping: nextSettings.fieldMapping || activePosSettings?.fieldMapping || {},
    });

    appendMcActivity({
      id: `pos-sync-${syncedAt}-${Math.random().toString(36).slice(2, 8)}`,
      type: meta.type || "pos-sync-live",
      createdAt: syncedAt,
      uploadedAt: syncedAt,
      scope: targetScope,
      siteId: targetScope,
      siteKey: targetScope,
      siteName: formatStoreLabel(targetScope),
      provider: nextSettings.provider || "lightspeed",
      rows: normalizedRows.length,
    });

    setPosInventory(inventoryPayload);
    setPosSettings(nextSettings);
    setEngine(nextEngine);
    setFileName(nextEngine.fileName || `${providerLabel} inventory`);
    setUploadedAt(syncedAt);
    const posImportId = `pos-${syncedAt}-${Math.random().toString(36).slice(2, 8)}`;
    // Keep the operator informed while the new snapshot is converted into a
    // Storage Engine revision. Initial hydration already uses this state; a
    // live import/POS sync must use it too so the previous revision never looks
    // like the newest result while the ledger commit is still in progress.
    if (updateVisibleState) { setStorageLedgerLoading(true); setStorageRevisionProcessing(true); }
    // Give React one paint before snapshot/ledger work starts so operators see
    // that Morpheus is actively creating the next revision on slower devices.
    await yieldInventoryUi();
    const previousActiveSnapshot = peekActiveInventorySnapshot(targetScope)
      || await loadActiveInventorySnapshot(targetScope, { refreshCloud: false }).catch(() => null);
    const posSnapshotResult = await commitActiveInventorySnapshot(targetScope, {
      ...nextEngine,
      importId: posImportId,
      activeImportId: posImportId,
      importSourceId: posImportId,
      importFingerprint: `${targetScope}::pos::${syncedAt}::${normalizedRows.length}`,
      uploadedAt: syncedAt,
      updatedAt: syncedAt,
      rowCount: normalizedRows.length,
      columnCount: nextEngine.parsedHeaders?.length || Object.keys(normalizedRows[0] || {}).length,
      sourceType: "pos",
    });
    if (posSnapshotResult?.ok) {
      const actorName = buildProfileDisplayName(currentProfile) || currentProfile?.email || "Morpheus operator";
      const ledgerResult = await recordInventoryImportMovement(resolveStorageAuthorityScope(targetScope), posSnapshotResult.snapshot, {
        id: currentProfile?.profileId || currentProfile?.id || currentProfile?.email || "",
        name: actorName,
        email: currentProfile?.email || "",
      }, previousActiveSnapshot);
      if (ledgerResult?.entries) setStorageMovements(ledgerResult.entries);
      if (ledgerResult?.syncEntries) setStorageSyncActivity(ledgerResult.syncEntries);
    }
    if (updateVisibleState) {
      setStorageLedgerLoading(false);
      setStorageRevisionProcessing(false);
    }
    saveEngineState({ ...nextEngine, rawRows: [], preparedRows: [], parsedHeaders: [], importSnapshotRef: { importId: posImportId, scope: targetScope, rowCount: normalizedRows.length, status: "ready" } }, targetScope);
    return { inventoryPayload, nextSettings, nextEngine, normalizedRows };
  };

  const testConnection = async () => {
    const orgId = getCallableOrgId();
    if (!orgId) return;
    if (!providerInfo.available) { setPosNotice(providerInfo.setupMessage); return; }
    setPosBusy(true);
    setPosNotice(`Testing ${providerLabel} connection...`);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "testPosConnection");
      const requestPayload = { orgId, provider: providerInfo.id };
      recordMcTrafficCallableRequest(MC_INTAKE_TRAFFIC_PROBE_ID, "callable:testPosConnection", requestPayload);
      const result = await fn(requestPayload);
      recordMcTrafficCallableResponse(MC_INTAKE_TRAFFIC_PROBE_ID, "callable:testPosConnection", result?.data || {});
      if (result?.data?.ok !== true || result.data.provider !== providerInfo.id) throw new Error("The POS connection could not be verified.");
      const nextSettings = savePosSettings({dataSourceMode:"pos",status:"connected",provider:providerInfo.id,providerLabel,
        lastTestedAt:result.data.checkedAt || new Date().toISOString()}, activeStoreScope);
      setPosSettings(nextSettings);
      setPosNotice(`${providerLabel} connection verified. You can now sync inventory.`);
    } catch (err) {
      setPosSettings(savePosSettings({status:"error",lastTestedAt:new Date().toISOString()},activeStoreScope));
      setPosNotice(err?.message || "Connection test failed. Check Super Admin.");
    } finally { setPosBusy(false); }
  };

  const syncLatestSoh = async (scopeOverride = "") => {
    // React passes the click event when this is used directly as a handler; never
    // treat that event object as a store scope.
    const explicitScope = typeof scopeOverride === "string" ? scopeOverride : "";
    const targetScope = String(explicitScope || activeStoreScope || "ALL").trim() || "ALL";
    if (targetScope.toUpperCase() === "ALL" && siteChoiceOptions.length) {
      requestSiteChoice("pos-sync");
      return;
    }
    const orgId = getCallableOrgId();
    if (!orgId) return;
    const targetPosSettings = loadPosSettings(targetScope);
    const selectedProvider = getPosProvider(targetPosSettings.provider);
    if (!selectedProvider.available) { setPosNotice(selectedProvider.setupMessage); return; }
    setPosBusy(true);
    setPosNotice(`Syncing latest ${selectedProvider.label} SOH for ${formatStoreLabel(targetScope)}...`);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "syncPosInventory");
      const callableSiteId = getPosCallableSiteId(targetScope);
      const requestPayload = {
        orgId,
        provider: selectedProvider.id,
        siteId: callableSiteId,
        mcSiteId: targetScope,
        posFilters: targetPosSettings?.syncFilters || posSettings?.syncFilters || {},
        posFieldMapping: targetPosSettings?.fieldMapping || posSettings?.fieldMapping || {},
      };
      recordMcTrafficCallableRequest(MC_INTAKE_TRAFFIC_PROBE_ID, "callable:syncPosInventory", requestPayload);
      const result = await fn(requestPayload);
      recordMcTrafficCallableResponse(MC_INTAKE_TRAFFIC_PROBE_ID, "callable:syncPosInventory", result?.data || {});
      const data = result?.data || {};
      validatePosSnapshot(data, selectedProvider.id, targetScope);
      const syncedAt = data.syncedAt || new Date().toISOString();
      const rows = Array.isArray(data.rows) ? data.rows : [];

      if (!rows.length) {
        setPosNotice(`${selectedProvider.label} returned no matching products. Previous inventory was preserved; review your location and filters.`);
        return;
      }

      const { normalizedRows } = await hydratePosEngine(rows, {
        syncedAt,
        provider: selectedProvider.id,
        source: `${selectedProvider.id}-live`,
        status: data.status || "synced_live",
        type: "pos-sync-live",
        scope: targetScope,
      });
      const summary = data.filterSummary || data.meta || {};
      const filterText = summary.fetched !== undefined
        ? ` Products fetched: ${summary.fetched}. Included: ${summary.included ?? normalizedRows.length}. Excluded: ${summary.excluded ?? 0}.`
        : "";
      setPosNotice(data.message || `${selectedProvider.label} sync complete. ${normalizedRows.length} rows are now feeding Prepared Data.${filterText}`);
    } catch (err) {
      console.error("POS sync failed", err);
      const nextSettings = savePosSettings({ status: "error" }, targetScope);
      setPosSettings(nextSettings);
      setPosNotice(err?.message || "POS sync failed. Check Functions deploy and org connection.");
    } finally {
      setPosBusy(false);
    }
  };


  const persistCsvTextForScope = async (value, nextFileName = fileName, scopeOverride = activeStoreScope, options = {}) => {
    const assertActive = options.assertActive || (() => {});
    assertActive();
    const targetScope = scopeOverride || activeStoreScope || "ALL";
    const updateVisibleState = options.updateVisibleState !== false;
    const recordLedger = options.recordLedger !== false;
    const awaitLedger = options.awaitLedger !== false;
    const baseEngine = loadEngineState(targetScope) || buildDefaultState(targetScope);
    const uploadTimestamp = new Date().toISOString();
    const parsed = options.parsed || parseCsv(value);
    const parsedValidation = validateParsedInventoryCsv(parsed);
    if (!parsedValidation.ok) {
      if (updateVisibleState) setPosNotice(parsedValidation.message);
      return { ok: false, scope: targetScope, reason: parsedValidation.reason, error: parsedValidation.message };
    }

    // A manual CSV upload must always win over any previous POS/live sync or
    // older cloud snapshot for this scope. The bug we saw was: the new CSV
    // briefly loaded, then MC re-hydrated the previous import because the old
    // engine was still marked as POS/previous-source. Force this upload back
    // into CSV mode before mapping, preparing and saving.
    const csvConfig = {
      ...(baseEngine.config || {}),
      dataSourceMode: "csv",
      storeName: targetScope && String(targetScope).toUpperCase() !== "ALL"
        ? formatStoreLabel(targetScope)
        : (baseEngine.config?.storeName || "ALL"),
    };
    const csvBaseEngine = { ...baseEngine, config: csvConfig };

    const importSourceProfile = detectUniversalImportProfile(parsed.headers, parsed.rows, csvConfig.storeName || "");
    const autoMapping = detectBestMapping(parsed.headers, csvConfig.storeName || "", parsed.rows);
    const scopedSystemSoh = resolveInventoryHeaderForScope(targetScope, parsed.headers, csvBaseEngine, importSourceProfile);
    // Auto-detection is a default only. A confirmed operator mapping must survive
    // every later CSV/POS refresh as long as the selected header still exists.
    const persistedMapping = baseEngine.mapping || baseEngine.csvMapping || {};
    const validPersisted = Object.fromEntries(Object.entries(persistedMapping).filter(([key, header]) => {
      if (!header) return false;
      if (key === "site" && importSourceProfile.layout === "column_site") return false;
      return parsed.headers.includes(header);
    }));
    const nextMapping = {
      ...autoMapping,
      ...validPersisted,
      systemSoh: scopedSystemSoh || validPersisted.systemSoh || autoMapping.systemSoh || ''
    };
    const preparedRows = buildPreparedData(
      parsed.rows,
      nextMapping,
      csvConfig,
      baseEngine.rules
    );

    const importId = `import-${uploadTimestamp}-${Math.random().toString(36).slice(2, 8)}`;
    const importFingerprint = `${targetScope || "ALL"}::${nextFileName || "csv"}::${uploadTimestamp}::${parsed.rows.length}::${parsed.headers.length}`;
    const importEntry = {
      id: importId,
      type: "import",
      uploadedAt: uploadTimestamp,
      createdAt: uploadTimestamp,
      fileName: nextFileName,
      scope: targetScope || "ALL",
      siteId: targetScope || "ALL",
      siteKey: targetScope || "ALL",
      storeKey: targetScope || "ALL",
      siteName: formatStoreLabel(targetScope || detectedSiteColumn || "ALL"),
      systemSoh: scopedSystemSoh || nextMapping.systemSoh || "",
      rows: parsed.rows.length,
      headers: parsed.headers,
      importFingerprint,
      distributionScope: String(targetScope || "ALL").toUpperCase() === "ALL" ? "ALL_SITES" : "SITE",
      distributionTarget: targetScope || "ALL",
      distributionBatchId: options.distributionBatchId || "",
      distributionOrigin: options.distributionOrigin || (String(targetScope || "ALL").toUpperCase() === "ALL" ? "ALL_SITES" : "SITE"),
    };

    appendMcActivity(importEntry);

    const next = {
      ...baseEngine,
      csvText: value,
      parsedHeaders: parsed.headers,
      rawRows: parsed.rows,
      mapping: nextMapping,
      preparedRows,
      // Latest import only: each site keeps one shared CSV payload.
      // Old imports are not retained as fallbacks/backups.
      importHistory: [importEntry],
      uploadHistory: [importEntry],
      importCount: Number(baseEngine.importCount || 0) + 1,
      uploadCount: Number(baseEngine.uploadCount || 0) + 1,
      lastGeneratedAt: uploadTimestamp,
      uploadedAt: uploadTimestamp,
      lastUploadedAt: uploadTimestamp,
      updatedAt: uploadTimestamp,
      fileName: nextFileName,
      activeImportId: importId,
      importSourceId: importId,
      importFingerprint,
      importSourceProfile,
      sourceLayout: importSourceProfile.layout,
      sourceMappingConfidence: importSourceProfile.fields,
      normalizationStatus: importSourceProfile.readyForNormalization ? "ready" : "mapping_required",
      distributionContext: {
        mode: String(targetScope || "ALL").toUpperCase() === "ALL" ? "ALL_SITES" : "SITE",
        scope: targetScope || "ALL",
        selectedAt: uploadTimestamp,
      },
    };

    latestManualImportRef.current = {
      scope: targetScope,
      engine: next,
      fingerprint: importFingerprint,
      uploadedAt: uploadTimestamp,
    };

    // Save the scoped engine before switching the admin dropdown/view.
    // Without this order, the scope-change effect can reload the previous
    // cached/cloud import for that site, which made the new CSV flash briefly
    // and then fall back to the previous import.
    clearOldImportLocalAliases(targetScope);

    // Capture the prior active snapshot before the new revision replaces it.
    // Phase 3.2 uses this local baseline to calculate deterministic product deltas.
    // Show revision processing immediately; otherwise the old R-number remains
    // visible for several seconds on slower connections with no explanation.
    if (updateVisibleState) {
      setStorageLedgerLoading(true);
      setStorageRevisionProcessing(true);
    }
    // Yield once so the revision status is painted before the Storage Engine
    // compares and commits the new snapshot. This state is independent from
    // the live ledger listener, which may replay the previous revision first.
    await yieldInventoryUi();
    assertActive();
    const previousActiveSnapshot = peekActiveInventorySnapshot(targetScope)
      || await loadActiveInventorySnapshot(targetScope, { refreshCloud: false }).catch(() => null);

    // DS1.17 / Storage revision continuity guard:
    // A site snapshot may have been hydrated from a global alias or an older
    // browser projection whose preparedRows were built with a different site's
    // inventory_<site> column. Passing those stale preparedRows straight into
    // the revision diff poisons the `before` SOH (for example District Six can
    // inherit Bothasig values) and can manufacture false movement/removal.
    // Re-project the previous raw snapshot through the SAME site SOH mapping
    // being used by this new upload before calculating the delta. This changes
    // only the comparison view; the immutable prior snapshot itself is untouched.
    let previousRevisionSnapshot = previousActiveSnapshot;
    if (previousActiveSnapshot && Array.isArray(previousActiveSnapshot.rawRows) && previousActiveSnapshot.rawRows.length) {
      const comparisonMapping = {
        ...(previousActiveSnapshot.mapping || {}),
        ...nextMapping,
        systemSoh: scopedSystemSoh || nextMapping.systemSoh || previousActiveSnapshot.mapping?.systemSoh || '',
      };
      const previousPreparedRows = buildPreparedData(
        previousActiveSnapshot.rawRows,
        comparisonMapping,
        { ...(previousActiveSnapshot.config || {}), ...csvConfig },
        previousActiveSnapshot.rules || baseEngine.rules,
      );
      previousRevisionSnapshot = {
        ...previousActiveSnapshot,
        mapping: comparisonMapping,
        preparedRows: previousPreparedRows,
      };

      if (process.env.NODE_ENV !== 'production') {
        const beforeHeader = String(previousActiveSnapshot.mapping?.systemSoh || '');
        const compareHeader = String(comparisonMapping.systemSoh || '');
        if (beforeHeader && compareHeader && beforeHeader !== compareHeader) {
          console.warn('[STORAGE REVISION] Reprojected previous site snapshot before diff', {
            scope: targetScope,
            previousSystemSoh: beforeHeader,
            comparisonSystemSoh: compareHeader,
            previousRows: previousActiveSnapshot.rawRows.length,
            reprojectedRows: previousPreparedRows.length,
          });
        }
      }
    }

    assertActive();
    // Persist large import rows in a dedicated chunked snapshot. The shared
    // engine document remains lightweight and continues to own rules/config.
    const snapshotResult = await commitActiveInventorySnapshot(targetScope, {
      ...next,
      importId,
      activeImportId: importId,
      importSourceId: importId,
      importFingerprint,
      rowCount: parsed.rows.length,
      columnCount: parsed.headers.length,
    }, { assertActive });
    assertActive();
    if (!snapshotResult?.ok) {
      console.warn("Active inventory snapshot could not be committed to the shared source of truth.", snapshotResult);
      if (updateVisibleState) {
        setPosNotice("Inventory was read locally, but the shared snapshot could not be saved. Check your connection and try again.");
        setStorageLedgerLoading(false);
        setStorageRevisionProcessing(false);
      }
      return { ok: false, scope: targetScope, error: snapshotResult?.error || "snapshot-commit-failed" };
    }

    let ledgerResult = null;
    if (recordLedger) {
      const actorName = buildProfileDisplayName(currentProfile) || currentProfile?.email || "Morpheus operator";
      const ledgerPromise = recordInventoryImportMovement(resolveStorageAuthorityScope(targetScope), snapshotResult.snapshot || { ...next, importId }, {
        id: currentProfile?.profileId || currentProfile?.id || currentProfile?.email || "",
        name: actorName,
        email: currentProfile?.email || "",
      }, previousRevisionSnapshot);
      if (awaitLedger) {
        ledgerResult = await ledgerPromise;
        if (updateVisibleState && ledgerResult?.entries) setStorageMovements(ledgerResult.entries);
        if (updateVisibleState && ledgerResult?.syncEntries) setStorageSyncActivity(ledgerResult.syncEntries);
        if (!ledgerResult?.ok) console.warn("Storage movement ledger entry could not be created.", ledgerResult);
      } else {
        ledgerPromise
          .then((backgroundLedger) => {
            if (!backgroundLedger?.ok) console.warn("Background storage movement ledger entry could not be created.", backgroundLedger);
          })
          .catch((error) => console.warn("Background storage movement ledger failed.", error));
      }
    }
    if (updateVisibleState) { setStorageLedgerLoading(false); setStorageRevisionProcessing(false); }

    const lightweightEngine = {
      ...next,
      csvText: "",
      parsedHeaders: [],
      rawRows: [],
      preparedRows: [],
      importSnapshotRef: {
        importId,
        scope: targetScope || "ALL",
        rowCount: Array.isArray(preparedRows) ? preparedRows.length : parsed.rows.length,
        columnCount: parsed.headers.length,
        uploadedAt: uploadTimestamp,
        status: "ready",
      },
    };
    saveEngineState(lightweightEngine, targetScope);
    savePreparedInventory(preparedRows, targetScope, {
      source: 'csv-prepared-data',
      provider: posSettings?.provider || 'csv',
      syncedAt: uploadTimestamp,
    });

    const activeImportPayload = {
      scope: targetScope || "ALL",
      siteId: targetScope || "ALL",
      siteName: formatStoreLabel(targetScope || "ALL"),
      fileName: nextFileName,
      uploadedAt: uploadTimestamp,
      updatedAt: uploadTimestamp,
      rowCount: parsed.rows.length,
      columnCount: parsed.headers.length,
      importId,
      importFingerprint,
    };

    try {
      try { try { mcSetItem(scopedImportStorageKey(targetScope), JSON.stringify(activeImportPayload)); } catch (err) { console.warn("MC import local cache skipped due to storage quota", err); } } catch (err) { console.warn("MC import local cache skipped due to storage quota", err); }
    } catch (err) {
      console.warn("MC import local cache skipped due to storage quota", err);
    }
    writeImportLock(targetScope, activeImportPayload);
    mcSetCloudItem(scopedImportStorageKey(targetScope), JSON.stringify(activeImportPayload));
    pruneMorpheusImportDocsForScope(targetScope);

    // Also mark this scope as CSV mode so the POS hydration effect cannot
    // re-apply the previous Lightspeed/prepared inventory over the fresh CSV.
    const nextPosSettings = savePosSettings({
      dataSourceMode: "csv",
      status: "csv_upload",
      provider: posSettings?.provider || "lightspeed",
      providerLabel: posSettings?.providerLabel || "Lightspeed",
      lastCsvUploadedAt: uploadTimestamp,
    }, targetScope);

    if (updateVisibleState) {
      setDistributionContext({
        mode: String(targetScope || "ALL").toUpperCase() === "ALL" ? "ALL_SITES" : "SITE",
        scope: targetScope || "ALL",
        selectedAt: uploadTimestamp,
      });
      setAdminStoreView(targetScope);
      setPosSettings(nextPosSettings);
      setEngine(next);
      setFileName(nextFileName);
      setUploadedAt(uploadTimestamp);
    }

    // The fresh import was already written once above. Firestore/local cache
    // reconciliation must remain read-only after this point; repeated full-engine
    // writes caused unrelated organisation state to appear reset during hydration.
    return { ok: true, scope: targetScope, next, snapshotResult, ledgerResult, importEntry };
  };

  const distributeCsvToAllSites = async (value, nextFileName = fileName) => {
    if (distributionOperationRef.current || activeDistributionJobs.has(getMcActiveOrgId())) return { ok: false, reason: "distribution-running" };
    const parsed = parseCsv(value);
    const validation = validateParsedInventoryCsv(parsed);
    if (!validation.ok) { setPosNotice(validation.message); return { ok: false, results: [], reason: validation.reason }; }
    const profile = detectUniversalImportProfile(parsed.headers, parsed.rows, "");
    const wideTargets = buildWideCsvDistributionTargets(parsed.headers, loadSites().filter((site) => site?.active !== false));
    const isWide = wideTargets.length >= 2;
    const rowSiteHeader = !isWide && profile.layout === "row_site" ? profile.rowSiteHeader : "";
    const rowSites = rowSiteHeader
      ? Array.from(new Set(parsed.rows.map((row) => String(row?.[rowSiteHeader] || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))
      : [];
    if (!isWide && !rowSites.length) {
      setPosNotice("All Sites import stopped before saving. This CSV has no distributable site field or inventory_<site> columns. Choose a specific site, or upload a multi-site CSV.");
      return { ok: false, results: [], reason: "missing-site-distribution" };
    }
    const targets = isWide ? wideTargets : rowSites.map((site) => ({ site, label: site }));
    const batchId = `distribution-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const requestedAt = new Date().toISOString();
    const operationOrgId = getMcActiveOrgId();
    const actor = { id: currentProfile?.profileId || currentProfile?.id || currentProfile?.email || "", name: buildProfileDisplayName(currentProfile) || currentProfile?.email || "Morpheus operator", email: currentProfile?.email || "" };
    const initialBatch = {
      batchId, status: "securing", requestedAt, completedAt: "", fileName: nextFileName,
      sourceScope: "ALL", total: targets.length, succeeded: 0, skipped: 0, failed: 0, actor: actor.name,
      results: targets.map((target) => ({ site: target.site, label: target.label || formatStoreLabel(target.site), status: "queued", reason: "" })),
    };
    if (!beginDistributionJob(operationOrgId, batchId)) return { ok: false, reason: "distribution-running" };
    distributionOperationRef.current = batchId;
    let handoffToBackground = false;
    const assertBatchActive = () => {
      if (!distributionJobIsCurrent(operationOrgId, batchId)) throw new Error("Inventory distribution was stopped because the workspace changed.");
    };
    try {
      setDistributionHistory(upsertDistributionBatch(initialBatch));
      setDistributionProgress({ batchId, status: "running", current: 0, total: 1, site: "Securing global inventory", results: [] });
      const globalResult = await withInventoryDeadline((assertWithinDeadline) => persistCsvTextForScope(value, nextFileName, "ALL", {
        parsed, updateVisibleState: false, recordLedger: true, awaitLedger: false,
        distributionBatchId: batchId, distributionOrigin: "ALL_SITES",
        assertActive: () => { assertWithinDeadline(); assertBatchActive(); },
      }), "Saving global inventory", 120000);
      assertBatchActive();
      if (!globalResult?.ok || !globalResult.snapshotResult?.cloudResult?.manifestId) throw new Error(globalResult?.snapshotResult?.cloudResult?.err?.message || "The global inventory snapshot could not be confirmed. No site distribution was started.");

      const globalEngine = globalResult.next;
      const sourceSnapshot = globalResult.snapshotResult.snapshot || globalEngine;
      const sourceManifestId = globalResult.snapshotResult.cloudResult.manifestId;
      const sourceImportId = globalResult.snapshotResult.cloudResult.importId || sourceSnapshot.importId || sourceSnapshot.activeImportId;
      setDistributionHistory(upsertDistributionBatch({ ...initialBatch, status: "running", sourceManifestId, sourceImportId, uploadedAt: sourceSnapshot.uploadedAt || requestedAt }));
      setAdminStoreView("ALL");
      setDistributionContext({ mode: "ALL_SITES", scope: "ALL", selectedAt: requestedAt, batchId });
      setEngine(globalEngine);
      setFileName(nextFileName);
      setUploadedAt(globalEngine.uploadedAt || requestedAt);
      setPosSettings(loadPosSettings("ALL"));
      setPosInventory(loadPreparedInventory("ALL"));
      setPosNotice(`Inventory accepted. Completing ${targets.length} site distributions...`);
      setDistributionProgress({ batchId, status: "running", current: 0, total: targets.length, site: "Saving site inventory", results: [] });

      const context = { sourceSnapshot, sourceManifestId, sourceImportId, parsed, rowSiteHeader, batchId, fileName: nextFileName, actor };
      const results = await runInventorySiteDistribution(targets, (target, assertWithinDeadline, reportStage) => distributeInventoryTarget(target, context, () => { assertWithinDeadline(); assertBatchActive(); }, reportStage), {
        timeoutMs: 120000,
        concurrency: 2,
        onStage: (progress) => {
          if (distributionOperationRef.current !== batchId || getMcActiveOrgId() !== operationOrgId) return;
          setDistributionProgress((current) => current?.batchId === batchId ? { ...current, current: progress.current, total: progress.total, site: `${progress.site} · ${progress.stage}` } : current);
        },
        onProgress: (progress) => {
          if (distributionOperationRef.current !== batchId || getMcActiveOrgId() !== operationOrgId) return;
          setDistributionProgress({ batchId, status: "running", ...progress, ...summarizeDistribution(progress.results) });
        },
      });
      assertBatchActive();
      const publicResults = results.map(publicDistributionResult);
      const summary = summarizeDistribution(publicResults);
      const pending = summary.inventoryReady;
      const batch = {
        ...initialBatch, ...summary, results: publicResults, sourceManifestId, sourceImportId,
        uploadedAt: sourceSnapshot.uploadedAt || requestedAt,
        status: pending ? "revisions_processing" : summary.failed ? "partial" : summary.skipped ? "completed_with_skips" : "completed",
      };
      setDistributionHistory(upsertDistributionBatch(batch));
      setDistributionProgress({ batchId, status: "queued", current: targets.length, total: targets.length, results: publicResults, ...summary });
      setPosNotice(`Inventory saved for ${summary.succeeded + pending} of ${targets.length} sites. ${pending} revision${pending === 1 ? " is" : "s are"} processing in the background. You can open Review or Sessions.`);
      if (pending) {
        handoffToBackground = true;
        startBackgroundDistributionRevisions({ orgId: operationOrgId, batch, inventoryResults: results });
      } else {
        releaseDistributionJob(operationOrgId, batchId);
      }
      window.setTimeout(() => setDistributionProgress((current) => current?.batchId === batchId && current?.status === "queued" ? null : current), 2800);
      return { ok: summary.failed === 0, queued: pending > 0, results: publicResults, globalResult };
    } catch (error) {
      if (distributionOperationRef.current === batchId && getMcActiveOrgId() === operationOrgId) {
        const reason = error?.message || "Inventory distribution failed.";
        const results = initialBatch.results.map((item) => ({ ...item, ok: false, status: "failed", reason }));
        setDistributionHistory(upsertDistributionBatch({ ...initialBatch, status: "failed", failed: targets.length, completedAt: new Date().toISOString(), results }));
        setDistributionProgress({ batchId, status: "failed", current: 0, total: targets.length, results: [], failed: targets.length, error: reason });
        setPosNotice(reason);
      }
      return { ok: false, results: [], error };
    } finally {
      if (!handoffToBackground) releaseDistributionJob(operationOrgId, batchId);
      if (distributionOperationRef.current === batchId) distributionOperationRef.current = null;
    }
  };

  const retryDistributionFailures = async (batch) => {
    const batchId = String(batch?.batchId || "");
    const targets = (batch?.results || []).filter((item) => revisionRetryable(item) || item?.ok === false);
    if (!batchId || !targets.length || distributionOperationRef.current) return;
    const operationOrgId = getMcActiveOrgId();
    if (!beginDistributionJob(operationOrgId, batchId)) return;
    distributionOperationRef.current = batchId;
    let handoffToBackground = false;
    const assertBatchActive = () => {
      if (!distributionJobIsCurrent(operationOrgId, batchId)) throw new Error("Inventory distribution was stopped because the workspace changed.");
    };
    setRetryingDistributionBatch(batchId);
    try {
      const source = await withInventoryDeadline(() => loadActiveInventorySnapshot("ALL", { refreshCloud: true }), "Loading the distribution source");
      assertBatchActive();
      const sourceSnapshot = source?.snapshot || source;
      if (!sourceSnapshot?.rawRows?.length) throw new Error("The global source snapshot is unavailable. Upload the CSV again.");
      const sourceImportId = sourceSnapshot.importId || sourceSnapshot.activeImportId;
      if (!batch.sourceManifestId || !batch.sourceImportId || batch.sourceImportId !== sourceImportId) throw new Error("This distribution source is no longer active. Upload its CSV again to start a fresh distribution.");
      const parsed = { headers: sourceSnapshot.parsedHeaders || Object.keys(sourceSnapshot.rawRows[0]), rows: sourceSnapshot.rawRows };
      const profile = detectUniversalImportProfile(parsed.headers, parsed.rows, "");
      const actor = { id: currentProfile?.profileId || currentProfile?.id || currentProfile?.email || "", name: buildProfileDisplayName(currentProfile) || currentProfile?.email || "Morpheus operator", email: currentProfile?.email || "" };
      const context = { sourceSnapshot, sourceManifestId: batch.sourceManifestId, sourceImportId, parsed, rowSiteHeader: profile.layout === "row_site" ? profile.rowSiteHeader : "", batchId, fileName: batch.fileName || sourceSnapshot.fileName, actor };
      setDistributionProgress({ batchId, status: "running", current: 0, total: targets.length, site: "Retrying affected sites", results: [] });
      const retried = await runInventorySiteDistribution(targets, (target, assertWithinDeadline, reportStage) => distributeInventoryTarget(target, context, () => { assertWithinDeadline(); assertBatchActive(); }, reportStage), {
        timeoutMs: 120000,
        concurrency: 2,
        onStage: (progress) => {
          if (distributionOperationRef.current !== batchId || getMcActiveOrgId() !== operationOrgId) return;
          setDistributionProgress((current) => current?.batchId === batchId ? { ...current, current: progress.current, total: progress.total, site: `${progress.site} · ${progress.stage}` } : current);
        },
        onProgress: (progress) => {
          if (distributionOperationRef.current === batchId && getMcActiveOrgId() === operationOrgId) setDistributionProgress({ batchId, status: "running", ...progress });
        },
      });
      assertBatchActive();
      const results = (batch.results || []).map((item) => publicDistributionResult(retried.find((result) => result.site === item.site) || item));
      const summary = summarizeDistribution(results);
      const nextBatch = { ...batch, ...summary, results, status: summary.inventoryReady ? "revisions_processing" : summary.failed ? "partial" : summary.skipped ? "completed_with_skips" : "completed", lastRetryAt: new Date().toISOString() };
      setDistributionHistory(upsertDistributionBatch(nextBatch));
      setDistributionProgress({ batchId, status: "queued", current: targets.length, total: targets.length, results, ...summary });
      setPosNotice(`Inventory recovery finished. ${summary.inventoryReady} site revisions processing in the background; you can continue working.`);
      if (summary.inventoryReady) {
        handoffToBackground = true;
        startBackgroundDistributionRevisions({ orgId: operationOrgId, batch: nextBatch, inventoryResults: retried, previousResults: batch.results || [] });
      }
      window.setTimeout(() => setDistributionProgress((current) => current?.batchId === batchId && current?.status === "queued" ? null : current), 2800);
    } catch (error) {
      if (distributionOperationRef.current === batchId && getMcActiveOrgId() === operationOrgId) {
        const reason = error?.message || "Distribution retry failed.";
        setPosNotice(reason);
        setDistributionProgress({ batchId, status: "failed", current: 0, total: targets.length, error: reason, results: [] });
      }
    } finally {
      if (!handoffToBackground) releaseDistributionJob(operationOrgId, batchId);
      if (distributionOperationRef.current === batchId) distributionOperationRef.current = null;
      setRetryingDistributionBatch("");
    }
  };

  const updateCsvText = async (value, nextFileName = fileName, scopeOverride = activeStoreScope) => {
    const targetScope = String(scopeOverride || activeStoreScope || "ALL").trim() || "ALL";
    if (activeDistributionJobs.has(getMcActiveOrgId())) {
      setPosNotice("An inventory distribution is still confirming site revisions. You can use Review or Sessions now; wait for the audit to finish before starting another CSV import.");
      return { ok: false, reason: "background-revisions-running" };
    }
    if (targetScope.toUpperCase() === "ALL" && siteChoiceOptions.length) {
      return distributeCsvToAllSites(value, nextFileName);
    }
    return persistCsvTextForScope(value, nextFileName, targetScope, { updateVisibleState: true, recordLedger: true });
  };

  const handleFile = async (file) => {
    if (!file) return;
    const fileValidation = validateInventoryUploadFile(file);
    if (!fileValidation.ok) {
      forcedImportScopeRef.current = "";
      setPosNotice(fileValidation.message);
      return;
    }
    const forcedScope = forcedImportScopeRef.current;
    if (forcedScope) {
      forcedImportScopeRef.current = "";
      const text = await file.text();
      await updateCsvText(text, file.name, forcedScope);
      return;
    }
    if (mustChooseSite && siteChoiceOptions.length) {
      requestSiteChoice("import", file);
      return;
    }
    const text = await file.text();
    await updateCsvText(text, file.name);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    await handleFile(file);
    event.target.value = "";
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    await handleFile(file);
  };

  const openPicker = () => {
    if (mustChooseSite && siteChoiceOptions.length) {
      requestSiteChoice("picker");
      return;
    }
    fileInputRef.current?.click();
  };

  const resolveSelectedCycleScope = () => {
    const selected = cycleOptions.find((option) => option.value === selectedCycleScope);
    if (!selected) return null;
    return {
      cycleId: normalizeCycleId(selected.cycleId || selected.value),
      cycleName: selected.cycleName || selected.label || formatCycleLabel(selected.value),
    };
  };

  const categoryScopeOptions = useMemo(() => {
    if (!selectedSectionScope) return [];

    const selectedSection = sectionScopeOptions.find((option) => option.value === selectedSectionScope);
    if (!selectedSection) return [];

    const source = resolveCountGroupSource(activeStoreScope, engine);
    const configuredGroups = source.groups;
    if (!configuredGroups.length) return [];

    // A Full Count can choose from every configured Count Group.
    if (selectedSection.fullSite) return configuredGroups.map((group) => group.name);

    const sectionId = normalizeSectionId(selectedSection.sectionId || selectedSection.value || '');
    const ownership = source.engine?.config?.categoryOwnership || {};
    const hasOwnership = Object.keys(ownership).length > 0;

    const ownedGroups = configuredGroups.filter((group) => {
      const outputs = Array.isArray(group.outputs) ? group.outputs : [];
      return outputs.some((output) => {
        const normalizedOutput = String(output || '').trim().toLowerCase();
        const owner = normalizeSectionId(ownership?.[normalizedOutput] || ownership?.[output] || '');
        return owner === sectionId;
      });
    });

    if (ownedGroups.length || hasOwnership) return ownedGroups.map((group) => group.name);

    // Legacy fallback only: if ownership has not been configured yet, infer the
    // applicable groups from the current prepared rows. The group definitions
    // themselves still come exclusively from Categories → Count Groups.
    const baseEngine = loadEngineState(activeStoreScope) || engine;
    const rows = buildPreparedData(baseEngine?.rawRows || [], baseEngine?.mapping, baseEngine?.config, baseEngine?.rules);
    const availableOutputs = new Set(rows.map((row) => String(row?.category || '').trim().toLowerCase()).filter(Boolean));
    return configuredGroups
      .filter((group) => (group.outputs || []).some((output) => availableOutputs.has(String(output || '').trim().toLowerCase())))
      .map((group) => group.name);
  }, [activeStoreScope, engine, selectedSectionScope, sectionScopeOptions]);

  const singleApplicableCountGroup = selectedSectionScope && categoryScopeOptions.length === 1
    ? categoryScopeOptions[0]
    : '';


  useEffect(() => {
    setSelectedCategoryScopes([]);
  }, [selectedSectionScope, activeStoreScope]);

  const resolveSelectedCategoryScope = () => {
    const explicitSelection = Array.from(new Set((selectedCategoryScopes || []).map((item) => String(item || '').trim()).filter(Boolean)));
    const selected = Array.from(new Set(
      explicitSelection.length ? explicitSelection : (singleApplicableCountGroup ? [singleApplicableCountGroup] : [])
    )).sort((a, b) => a.localeCompare(b));
    const baseEngine = loadEngineState(activeStoreScope) || engine;
    const configuredGroups = resolveConfiguredCountGroups(activeStoreScope, baseEngine);
    const usesCountGroups = configuredGroups.some((group) => selected.some((name) => String(group?.name || '').trim().toLowerCase() === name.toLowerCase()));
    return {
      categoryScopeType: selected.length ? 'selected' : 'all',
      categoryNames: selected,
      countGroupNames: selected,
      categoryIds: selected.map((item) => item.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')),
      categoryScopeLabel: selected.length ? selected.join(' • ') : 'All Count Groups',
      categoryScopeSource: usesCountGroups ? 'count-groups' : 'normalized-categories',
    };
  };

  const resolveSelectedSectionScope = () => {
    const selected = sectionScopeOptions.find((option) => option.value === selectedSectionScope);
    if (!selected) return null;
    if (selected.fullSite) return { sectionId: '', sectionName: '', fullSite: true };
    return { sectionId: normalizeSectionId(selected.sectionId || selected.value), sectionName: selected.sectionName || selected.label || formatSectionLabel(selected.value), fullSite: false };
  };

  const createSession = (scopeOverride = activeStoreScope, options = {}) => {
    const targetScope = scopeOverride || activeStoreScope || "ALL";
    const cycleScope = options.cycleScope || resolveSelectedCycleScope();
    if (!cycleScope) {
      setPosNotice('Choose your cycle before preparing a new count session.');
      return;
    }
    const sectionScope = options.sectionScope || resolveSelectedSectionScope();
    if (!sectionScope) {
      setPosNotice('Choose your section before preparing a new count session.');
      return;
    }
    const cycleId = normalizeCycleId(cycleScope?.cycleId || cycleScope?.cycleName || '');
    const cycleName = String(cycleScope?.cycleName || formatCycleLabel(cycleId)).trim() || formatCycleLabel(cycleId);
    const sectionId = sectionScope?.fullSite ? '' : normalizeSectionId(sectionScope?.sectionId || sectionScope?.sectionName || '');
    const sectionName = sectionScope?.fullSite ? '' : String(sectionScope?.sectionName || formatSectionLabel(sectionId)).trim();
    const categoryScope = options.categoryScope || resolveSelectedCategoryScope();
    const delayMs = Number.isFinite(options.delayMs) ? options.delayMs : 45;
    setPrepareStage("building");
    setPrepareBusy(true);

    // Let the blur/loader paint before heavier session preparation and route change.
    window.setTimeout(() => {
      const baseEngine = loadEngineState(targetScope) || engine;
      const sessionStoreKey = resolveInventoryHeaderForScope(targetScope, baseEngine.parsedHeaders || [], baseEngine);
      const sessionMapping = { ...(baseEngine.mapping || {}), systemSoh: sessionStoreKey };
      const allPreparedRows = buildPreparedData(baseEngine.rawRows || [], sessionMapping, baseEngine.config, baseEngine.rules);
      const selectedCategorySet = new Set((categoryScope?.categoryNames || []).map((item) => String(item || '').trim().toLowerCase()));
      const configuredGroups = resolveConfiguredCountGroups(targetScope, baseEngine);
      const selectedGroupOutputs = new Set(
        configuredGroups
          .filter((group) => selectedCategorySet.has(String(group?.name || '').trim().toLowerCase()))
          .flatMap((group) => Array.isArray(group?.outputs) ? group.outputs : [])
          .map((output) => String(output || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const scopedPreparedRows = selectedCategorySet.size
        ? allPreparedRows.filter((row) => {
            const normalizedCategory = String(row?.category || 'Other').trim().toLowerCase();
            return selectedGroupOutputs.size ? selectedGroupOutputs.has(normalizedCategory) : selectedCategorySet.has(normalizedCategory);
          })
        : allPreparedRows;
      const engineForSession = {
        ...baseEngine,
        mapping: sessionMapping,
        preparedRows: scopedPreparedRows,
        lastGeneratedAt: new Date().toISOString(),
      };
      setAdminStoreView(targetScope);
      saveEngineState(engineForSession, targetScope);
      const payload = buildSessionPayloadFromEngine(engineForSession, {
        storeKey: sessionStoreKey,
        storeName: String(engineForSession?.config?.dataSourceMode || "csv").toLowerCase() === "pos" ? formatStoreLabel(targetScope || engineForSession?.config?.storeName) : undefined,
        startedAt: new Date().toISOString(),
        startedBy: buildProfileDisplayName(currentProfile) || "User",
        lastEditedBy: buildProfileDisplayName(currentProfile) || "User",
        sectionId,
        sectionName,
        fullSite: sectionScope?.fullSite === true,
        cycleId,
        cycleName,
        cycleKey: cycleId,
        cycle: { cycleId, id: cycleId, name: cycleName },
        ...categoryScope,
      });

      savePreparedRowsToSessionSeed(payload, sessionStoreKey);
      saveSessionDetailFromPayload(payload);

      const resolvedCycleId = normalizeCycleId(payload.cycleId || payload.cycleKey || cycleId || cycleScope?.cycleId || cycleScope?.cycleName || '');
      const resolvedCycleName = String(payload.cycleName || cycleName || cycleScope?.cycleName || formatCycleLabel(resolvedCycleId)).trim();
      const resolvedSectionId = payload.fullSite === true ? '' : normalizeSectionId(payload.sectionId || sectionId || sectionScope?.sectionId || sectionScope?.sectionName || '');
      const resolvedSectionName = payload.fullSite === true ? '' : String(payload.sectionName || sectionName || sectionScope?.sectionName || formatSectionLabel(resolvedSectionId)).trim();

      upsertSessionRecord({
        sessionId: payload.sessionId,
        storeName: payload.storeName,
        storeKey: payload.storeKey,
        sectionId: resolvedSectionId,
        sectionName: resolvedSectionName,
        fullSite: payload.fullSite === true,
        isFullSite: payload.fullSite === true,
        fullCount: payload.fullSite === true,
        sessionType: payload.fullSite === true ? "full-count" : "section",
        countScope: payload.fullSite === true ? "full-site" : "section",
        categoryScopeType: payload.categoryScopeType || categoryScope.categoryScopeType,
        categoryNames: payload.categoryNames || categoryScope.categoryNames,
        countGroupNames: payload.countGroupNames || categoryScope.countGroupNames || payload.categoryNames || categoryScope.categoryNames,
        categoryIds: payload.categoryIds || categoryScope.categoryIds,
        categoryScopeLabel: payload.categoryScopeLabel || categoryScope.categoryScopeLabel,
        categoryScopeSource: payload.categoryScopeSource || categoryScope.categoryScopeSource || 'count-groups',
        cycleId: resolvedCycleId,
        cycleName: resolvedCycleName,
        cycleKey: resolvedCycleId,
        cycle: { cycleId: resolvedCycleId, id: resolvedCycleId, name: resolvedCycleName },
        meta: {
          cycleId: resolvedCycleId,
          cycleName: resolvedCycleName,
          cycleKey: resolvedCycleId,
          cycle: { cycleId: resolvedCycleId, id: resolvedCycleId, name: resolvedCycleName },
          sectionId: resolvedSectionId,
          sectionName: resolvedSectionName,
          fullSite: payload.fullSite === true,
          isFullSite: payload.fullSite === true,
          fullCount: payload.fullSite === true,
          sessionType: payload.fullSite === true ? "full-count" : "section",
          countScope: payload.fullSite === true ? "full-site" : "section",
          categoryScopeType: payload.categoryScopeType || categoryScope.categoryScopeType,
          categoryNames: payload.categoryNames || categoryScope.categoryNames,
          countGroupNames: payload.countGroupNames || categoryScope.countGroupNames || payload.categoryNames || categoryScope.categoryNames,
          categoryIds: payload.categoryIds || categoryScope.categoryIds,
          categoryScopeLabel: payload.categoryScopeLabel || categoryScope.categoryScopeLabel,
          categoryScopeSource: payload.categoryScopeSource || categoryScope.categoryScopeSource || 'count-groups',
        },
        fileName: payload.fileName,
        startedAt: payload.startedAt,
        updatedAt: payload.startedAt,
        createdAt: payload.startedAt,
        startedBy: payload.startedBy,
        lastEditedBy: payload.lastEditedBy || payload.startedBy,
        totalLines: payload.preparedRows?.length || 0,
        completedLines: 0,
        flaggedCount: 0,
        status: "Active",
        costExposure: 0,
      });

      navigate(`/morpheus-count/session/${payload.sessionId}`);
    }, delayMs);
  };

  const prepareSessionNow = (scopeOverride = activeStoreScope) => {
    if ((!scopeOverride || String(scopeOverride).toUpperCase() === "ALL") && siteChoiceOptions.length) {
      requestSiteChoice("prepare");
      return;
    }
    if (isPosMode && !posHasSyncedInventory) {
      setPosNotice("POS mode requires a successful Sync Latest SOH before preparing a new count session.");
      return;
    }
    const targetScope = scopeOverride || activeStoreScope || "ALL";
    const baseEngine = loadEngineState(targetScope) || engine;
    const sessionStoreKey = resolveInventoryHeaderForScope(targetScope, baseEngine.parsedHeaders || [], baseEngine);
    const sessionEngine = { ...baseEngine, mapping: { ...(baseEngine.mapping || {}), systemSoh: sessionStoreKey } };
    const cycleScope = resolveSelectedCycleScope();
    if (!cycleScope) {
      setPosNotice('Choose your cycle before preparing a new count session.');
      return;
    }
    const sectionScope = resolveSelectedSectionScope();
    if (!sectionScope) {
      setPosNotice('Choose your section before preparing a new count session.');
      return;
    }
    const cycleActiveSessions = activeRecordsForSelectedCycle(sessionEngine, cycleScope, targetScope);
    const activeFullSiteSessions = cycleActiveSessions.filter(isFullSiteSessionRecord);
    const activeSectionSessions = cycleActiveSessions.filter((record) => !isFullSiteSessionRecord(record));

    if (!sectionScope.fullSite && activeFullSiteSessions.length) {
      const fullCount = activeFullSiteSessions[0];
      const fullCountLabel = fullCount?.sessionId || 'the active Full Site Count';
      setPosNotice(`A Full Site Count is already active for ${cycleScope.cycleName || 'this cycle'} (${fullCountLabel}). Complete or archive that Full Site Count before preparing a section count because it already includes all sections.`);
      setOverwriteMatches([]);
      setOverwriteScope('');
      setOverwritePrompt(false);
      setCycleBlockPrompt({
        type: 'full-count-blocks-section',
        cycleName: cycleScope.cycleName || 'this cycle',
        sectionName: sectionScope.sectionName || formatSectionLabel(sectionScope.sectionId || 'section'),
        fullCount,
      });
      return;
    }

    if (sectionScope.fullSite && activeSectionSessions.length) {
      const sectionNames = activeSectionSessions
        .map((record) => record?.sectionName || formatSectionLabel(record?.sectionId || 'section'))
        .filter(Boolean)
        .join(', ');
      setPosNotice(`Section count${activeSectionSessions.length > 1 ? 's are' : ' is'} already active for ${cycleScope.cycleName || 'this cycle'}${sectionNames ? `: ${sectionNames}` : ''}. Resume and complete the section count, or replace it before preparing a Full Site Count.`);
      setOverwriteMatches(activeSectionSessions);
      setOverwriteScope(targetScope);
      setOverwritePrompt(true);
      return;
    }

    const existing = cycleActiveSessions.filter((record) => {
      if (sectionScope.fullSite) return isFullSiteSessionRecord(record);
      if (isFullSiteSessionRecord(record)) return false;
      const recordSectionId = normalizeSectionId(record?.sectionId || record?.sectionName || '');
      return recordSectionId === normalizeSectionId(sectionScope.sectionId || sectionScope.sectionName || '');
    });
    if (existing.length) {
      // Unarchived completed sections remain part of the live cycle. Importing the same
      // section again must resume or replace that live record. A fresh section is allowed
      // only after the cycle has been finalized and moved to Archives.
      setOverwriteMatches(existing);
      setOverwriteScope(targetScope);
      setOverwritePrompt(true);
      return;
    }
    createSession(targetScope, { sectionScope, cycleScope });
  };

  const confirmOverwrite = async () => {
    const targetScope = overwriteScope || activeStoreScope || "ALL";
    const matchesToArchive = Array.isArray(overwriteMatches) ? [...overwriteMatches] : [];
    const sectionScope = resolveSelectedSectionScope();
    const cycleScope = resolveSelectedCycleScope();
    setPrepareStage("archiving");
    setPrepareBusy(true);
    setOverwritePrompt(false);
    setOverwriteScope("");
    setOverwriteMatches([]);
    setCycleBlockPrompt(null);

    // Let the archive message paint first, then remove only the active sessions
    // that triggered the prompt. This is important for Full Site Count: replacing
    // a full count must archive the active section sessions in that cycle, not
    // search for another full count match.
    window.setTimeout(async () => {
      try {
        await Promise.all(
          matchesToArchive
            .map((record) => record?.sessionId)
            .filter(Boolean)
            .map((sessionId) => archiveSessionRecord(sessionId, "Overwritten by new session"))
        );
      } catch (err) {
        console.warn("Failed to archive existing MC session before overwrite", err);
      } finally {
        setPrepareStage("building");
        createSession(targetScope, { delayMs: 35, sectionScope, cycleScope });
      }
    }, 80);
  };

  const resumeExistingLiveSession = () => {
    const target = overwriteMatches?.[0]?.sessionId;
    setOverwritePrompt(false);
    setOverwriteScope("");
    setOverwriteMatches([]);
    if (target) navigate(`/morpheus-count/session/${target}`);
  };


  const closeCycleBlockPrompt = () => {
    setCycleBlockPrompt(null);
  };

  const resumeBlockingFullCount = () => {
    const target = cycleBlockPrompt?.fullCount?.sessionId;
    setCycleBlockPrompt(null);
    if (target) navigate(`/morpheus-count/session/${target}`);
  };

  const archiveBlockingFullCount = async () => {
    const target = cycleBlockPrompt?.fullCount?.sessionId;
    const label = target || 'Full Site Count';
    setCycleBlockPrompt(null);
    if (!target) return;
    setPrepareStage("archiving");
    setPrepareBusy(true);
    try {
      await archiveSessionRecord(target, "Archived before section count");
      setPosNotice(`${label} was archived. You can now prepare the section count.`);
    } catch (err) {
      console.warn("Failed to archive blocking full count", err);
      setPosNotice(`Could not archive ${label}. Please try again from Sessions.`);
    } finally {
      setPrepareBusy(false);
      setPrepareStage("building");
    }
  };

  const confirmSiteChoice = async () => {
    const availableOptions = siteChoicePrompt?.distribution ? distributionChoiceOptions : siteChoiceOptions;
    const selected = siteChoiceValue || availableOptions[0] || "ALL";
    const prompt = siteChoicePrompt;
    const nextDistributionContext = {
      mode: String(selected).toUpperCase() === "ALL" ? "ALL_SITES" : "SITE",
      scope: selected,
      selectedAt: new Date().toISOString(),
    };
    setDistributionContext(nextDistributionContext);
    closeSiteChoicePrompt();

    if (prompt?.reason === "import" && prompt.payload) {
      // Do NOT switch the dropdown before the new CSV is saved.
      // Switching first triggers the scope-change effect, which can briefly reload
      // the previous import for that site and then overwrite the fresh upload.
      // updateCsvText saves the chosen site's engine first, locks it to CSV mode,
      // and only then switches the admin dropdown/view.
      const text = await prompt.payload.text();
      updateCsvText(text, prompt.payload.name, selected);
      return;
    }

    setAdminStoreView(selected);

    if (prompt?.reason === "prepare") {
      prepareSessionNow(selected);
      return;
    }

    if (prompt?.reason === "pos-sync") {
      if (String(selected).toUpperCase() === "ALL") {
        setAdminStoreView("ALL");
        setDistributionProgress({ status: "running", current: 0, total: siteChoiceOptions.length, site: "Starting POS distribution", results: [] });
        const results = [];
        for (let index = 0; index < siteChoiceOptions.length; index += 1) {
          const site = siteChoiceOptions[index];
          setDistributionProgress((prev) => ({ ...prev, current: index, site: formatStoreLabel(site), results: [...results] }));
          try {
            await syncLatestSoh(site);
            results.push({ site, label: formatStoreLabel(site), ok: true });
          } catch (error) {
            results.push({ site, label: formatStoreLabel(site), ok: false, reason: String(error?.message || error) });
          }
          setDistributionProgress((prev) => ({ ...prev, current: index + 1, results: [...results] }));
        }
        const succeeded = results.filter((item) => item.ok).length;
        const failed = results.length - succeeded;
        setAdminStoreView("ALL");
        setPosNotice(`Global POS distribution complete: ${succeeded} site${succeeded === 1 ? "" : "s"} updated${failed ? `, ${failed} failed` : ""}.`);
        setDistributionProgress({ status: "complete", current: results.length, total: results.length, site: "Complete", results, succeeded, failed });
        window.setTimeout(() => setDistributionProgress(null), 4500);
        return;
      }
      window.setTimeout(() => syncLatestSoh(selected), 150);
      return;
    }

    // User clicked Choose CSV while All Sites was selected: switch the admin
    // dropdown to the chosen site, then open the native file picker. The ref keeps
    // the chosen site available even if React has not re-rendered before the file is selected.
    forcedImportScopeRef.current = selected;
    window.setTimeout(() => fileInputRef.current?.click(), 150);
  };

  const resolveOperationalValue = (row, column) => {
    const direct = row?.[column.key];
    if (direct !== undefined && direct !== null && `${direct}`.trim() !== "") return direct;
    const fallback = column.fallback ? row?.[column.fallback] : undefined;
    if (fallback !== undefined && fallback !== null && `${fallback}`.trim() !== "") return fallback;
    return "—";
  };

  const hasData = rawRows.length > 0;

  return (
    <div className="mc-enginePage mc-tabPage">
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="imports" />

      <section className="mc-intakeCommand">
        <article className="mc-intakeSourceCard">
          <div className="mc-intakeSourceCard__top">
            <div>
              <span className="mc-intakeLabel">Loaded inventory source</span>
              <span className="mc-intakeBadge" title="Distribution scope selected for this inventory source">
                Distribution · {distributionContext.mode === "ALL_SITES" ? "All Sites" : formatStoreLabel(distributionContext.scope)}
              </span>
              <h2>{hasData ? (fileName || "Inventory snapshot") : "No inventory loaded"}</h2>
            </div>
            <span className={`mc-intakeStatus ${hasData ? "is-ready" : "is-idle"}`}>{hasData ? "Inventory loaded" : "Waiting"}</span>
          </div>
          <p>{hasData
            ? "This is the loaded inventory available for review and counting. Selecting a POS provider does not change the source of this snapshot."
            : "Upload a CSV or sync an available, connected POS to load inventory."}</p>
          <dl className="mc-intakeSourceMeta">
            <div><dt>Site</dt><dd>{formatStoreLabel(activeStoreScope || "ALL")}</dd></div>
            <div><dt>Last intake</dt><dd>{formatUploadTimestamp(uploadedAt)}</dd></div>
            <div><dt>Products</dt><dd>{rawRows.length}</dd></div>
            <div><dt>Source</dt><dd>{fileName || (hasData ? "Source not recorded" : "No file loaded")}</dd></div>
          </dl>
        </article>

        <article className="mc-intakeActiveCard">
          <span className="mc-intakeLabel">Active inventory snapshot</span>
          <h3>{hasData ? `${rawRows.length} products are active` : "No active snapshot yet"}</h3>
          <p>{hasData ? "This is the inventory Morpheus will send into Inventory Review and use to prepare count sessions." : "Sync or upload inventory to create the first active snapshot for this site."}</p>
          <div className="mc-intakeSnapshotFacts">
            <span><b>{detectedColumns}</b> columns</span>
            <span><b>{duplicateSummary.count}</b> duplicates</span>
            <span><b>{detectedSiteColumn || "Not set"}</b> site column</span>
          </div>
        </article>
      </section>

      <section className="mc-enginePage__content">
        {posNotice ? <div className="mc-posNotice">{posNotice}</div> : null}

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>{isPosMode ? "Sync Latest Inventory" : "Import Inventory"}</h3>
              <p>{isPosMode ? "Create a fresh active inventory snapshot from your connected POS." : "Upload a fresh inventory snapshot for the selected site."}</p>
            </div>
            <div className="mc-engineBadge">{isPosMode ? (posReadyToSync ? "Sync Ready" : "Setup Required") : "Live Upload"}</div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="mc-hiddenFileInput"
            onChange={handleFileChange}
          />

          {isPosMode ? (
            <div className="mc-posSyncBox">
              <div className="mc-posSyncBox__main">
                <h4>{providerLabel} stock sync</h4>
                <p>{providerInfo.available ? "Sync inventory from your connected provider, then review it before preparing a count." : providerInfo.setupMessage}</p>
              </div>
              <div className="mc-inlineActions mc-uploadActions">
                <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={testConnection} disabled={posBusy || !providerInfo.available}>{posBusy ? "Working..." : "Test Connection"}</button>
                <button
                  type="button"
                  className="mc-engineBtn mc-engineBtn--primary"
                  onClick={syncLatestSoh}
                  disabled={posBusy || !posReadyToSync}
                  title={!posReadyToSync ? "Test connection first" : "Sync latest SOH"}
                >
                  {posBusy ? "Syncing..." : "Sync Latest SOH"}
                </button>
              </div>
              <div className="mc-miniMeta mc-miniMeta--pos">
                <span>Provider: {providerLabel}</span>
                <span className={`mc-posStatusPill mc-posStatusPill--${posStatus.tone}`}>{posStatus.label}</span>
                <span>Prepared inventory rows: {posInventory.rows?.length || 0}</span>
                <span>Last sync: {formatUploadTimestamp(posSettings?.lastSyncedAt || posInventory.syncedAt)}</span>
              </div>
            </div>
          ) : (
            <div
              className={`mc-uploadZone ${isDragging ? "is-dragging" : ""}`}
              onClick={openPicker}
              style={{ cursor: "pointer" }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <h4>Drop inventory CSV here or click to upload</h4>
              <p>Upload the latest POS export. Morpheus will validate it before it becomes operational.</p>
              <div className="mc-inlineActions mc-uploadActions">
                <button
                  type="button"
                  className="mc-engineBtn mc-engineBtn--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPicker();
                  }}
                >
                  Choose CSV File
                </button>
              </div>
              <div className="mc-miniMeta">
                <span>{fileName}</span>
                <span>Uploaded: {formatUploadTimestamp(uploadedAt)}</span>
                <span>{rawRows.length} rows</span>
                <span>{detectedColumns} columns</span>
                <span>Site column: {detectedSiteColumn || "Not set"}</span>
                {duplicateSummary.count > 0 ? <span>{duplicateSummary.count} duplicates merged</span> : null}
              </div>
            </div>
          )}
        </article>

        <article className="mc-enginePanel mc-storageSyncPanel">
          <div className="mc-enginePanel__header">
            <div>
              <span className="mc-intakeLabel">Sync activity</span>
              <h3>Did inventory sync, and did it change?</h3>
              <p>Every successful CSV import or POS sync is recorded. A storage revision is created only when inventory changed.</p>
            </div>
            <div className="mc-engineBadge">Latest {Math.min(storageSyncActivity.length, 1)} of {storageSyncActivity.length}</div>
          </div>
          {storageSyncActivity.length ? (
            <div className="mc-storageSyncList">
              {storageSyncActivity.slice(0, 1).map((sync) => (
                <div key={sync.id} className={`mc-storageSyncRow mc-storageSyncRow--${sync.outcome || "no_changes"}`}>
                  <div>
                    <strong>{sync.syncLabel || "Inventory Sync"}</strong>
                    <span>{formatUploadTimestamp(sync.timestamp)}</span>
                  </div>
                  <div>
                    <span>{sync.source || "Inventory source"}</span>
                    <small>{sync.productCount || 0} products</small>
                  </div>
                  <div className="mc-storageSyncOutcome">
                    <strong>{sync.outcomeLabel || "No inventory changes"}</strong>
                    <small>{sync.outcome === "revision_created" ? `${sync.changedCount || 0} products changed` : "Snapshot matched latest revision"}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mc-emptyState"><strong>No sync activity yet.</strong><p>The next successful CSV import or POS sync will be recorded here.</p></div>
          )}
        </article>

        {distributionHistory.length ? (
          <article className="mc-enginePanel mc-distributionAuditPanel">
            <div className="mc-enginePanel__header">
              <div>
                <span className="mc-intakeLabel">Global distribution audit</span>
                <h3>Where did the inventory go?</h3>
                <p>Every All Sites import keeps an auditable per-site result. Failed or skipped sites can be retried without uploading the CSV again.</p>
              </div>
              <div className="mc-engineBadge">Latest {Math.min(distributionHistory.length, 5)} of {distributionHistory.length}</div>
            </div>
            <div className="mc-distributionAuditList">
              {distributionHistory.slice(0, 5).map((batch) => {
                const open = expandedDistributionBatch === batch.batchId;
                const retryable = (batch.results || []).some((item) => revisionRetryable(item) || item?.ok === false);
                return (
                  <div key={batch.batchId} className={`mc-distributionAuditRow mc-distributionAuditRow--${batch.status || "queued"}`}>
                    <button type="button" className="mc-distributionAuditRow__summary" onClick={() => setExpandedDistributionBatch(open ? "" : batch.batchId)}>
                      <span>
                        <strong>{batch.fileName || "Global inventory"}</strong>
                        <small>{formatUploadTimestamp(batch.requestedAt || batch.uploadedAt)}</small>
                      </span>
                      <span className="mc-distributionAuditRow__counts">
                        <b>{batch.succeeded || 0} ready</b>
                        {batch.inventoryReady ? <em>{batch.inventoryReady} revisions pending</em> : null}
                        {batch.skipped ? <em>{batch.skipped} skipped</em> : null}
                        {batch.failed ? <i>{batch.failed} failed</i> : null}
                      </span>
                      <span className="mc-distributionAuditRow__status">{String(batch.status || "queued").replaceAll("_", " ")}</span>
                    </button>
                    {open ? (
                      <div className="mc-distributionAuditRow__details">
                        {(batch.results || []).map((item) => (
                          <div key={`${batch.batchId}-${item.site}`} className={`is-${item.status || (item.ok ? "ready" : item.skipped ? "skipped" : "failed")}`}>
                            <span>{item.status === "ready" ? "✓" : revisionPending(item) ? "…" : item.skipped || item.status === "skipped" ? "–" : "!"}</span>
                            <strong>{item.label || formatStoreLabel(item.site)}</strong>
                            <small>{item.reason || (item.status === "ready" ? "Inventory and revision confirmed" : revisionPending(item) ? "Inventory saved · revision pending" : "Pending")}</small>
                          </div>
                        ))}
                        {retryable ? (
                          <div className="mc-inlineActions">
                            <button type="button" className="mc-engineBtn mc-engineBtn--secondary" disabled={Boolean(distributionProgress?.status === "running" || retryingDistributionBatch || activeDistributionJobs.has(getMcActiveOrgId()))} onClick={() => retryDistributionFailures(batch)}>
                              {retryingDistributionBatch === batch.batchId ? "Retrying..." : batch.status === "revisions_processing" && !activeDistributionJobs.has(getMcActiveOrgId()) ? "Resume pending revisions" : "Retry affected sites"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </article>
        ) : null}

        <article className="mc-enginePanel mc-storageIntelligencePanel">
          <div className="mc-enginePanel__header">
            <div>
              <span className="mc-intakeLabel">Storage intelligence</span>
              <h3>What changed in the latest inventory?</h3>
              <p>Deterministic insight generated from the newest storage revision for this site.</p>
            </div>
            <div className="mc-storageRevisionStatus">
              {storageLedgerLoading || storageRevisionProcessing ? (
                <span className="mc-storageRevisionLoader" role="status" aria-live="polite">
                  <i aria-hidden="true" /> {storageRevisionProcessing ? "Creating revision" : "Loading revision"}
                </span>
              ) : null}
              <span className={`mc-storageReadiness mc-storageReadiness--${storageReadiness.tone}`} title={storageMovements[0]?.readiness?.error || "Storage Engine revision lifecycle status"}>
                <i aria-hidden="true" /> {storageReadiness.label}
              </span>
              <div className="mc-engineBadge">{storageIntelligence ? `R${storageIntelligence.movement.revision || 1}` : "Waiting"}</div>
            </div>
          </div>

          {storageIntelligence ? (
            <>
              <div className="mc-storageIntelligenceGrid">
                <section className="mc-storageInsightCard mc-storageInsightCard--revision">
                  <span>Latest revision</span>
                  <strong>R{storageIntelligence.movement.revision || 1}</strong>
                  <dl>
                    <div><dt>Compared to</dt><dd>{storageIntelligence.movement.revision > 1 ? `R${storageIntelligence.movement.revision - 1}` : "Initial snapshot"}</dd></div>
                    <div><dt>Imported</dt><dd>{formatUploadTimestamp(storageIntelligence.movement.timestamp)}</dd></div>
                    <div><dt>Source</dt><dd>{storageIntelligence.movement.source || "Inventory sync"}</dd></div>
                    <div><dt>Readiness</dt><dd className={`mc-storageReadinessText mc-storageReadinessText--${storageReadiness.tone}`}>{storageReadiness.label}</dd></div>
                  </dl>
                </section>

                <section className="mc-storageInsightCard mc-storageInsightCard--movement">
                  <span>Movement summary</span>
                  <div className="mc-storageMovementMetrics">
                    {STORAGE_CHANGE_GROUPS.map((group) => (
                      <div key={group.key} className={`mc-storageMetric mc-storageMetric--${group.key}`}>
                        <small>{group.short}</small>
                        <strong>{storageIntelligence.groups[group.key] || 0}</strong>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mc-storageInsightCard mc-storageInsightCard--pulse">
                  <span>Inventory pulse</span>
                  <div className="mc-storagePulseGrid">
                    <div><small>Stable</small><strong>{storageIntelligence.stablePercent}%</strong><em>{storageIntelligence.stable} products</em></div>
                    <div><small>Changed</small><strong>{storageIntelligence.changedPercent}%</strong><em>{storageIntelligence.changed} products</em></div>
                    <div><small>Net stock</small><strong>{storageIntelligence.netSohChange > 0 ? "+" : ""}{formatStorageValue(storageIntelligence.netSohChange, "SOH")}</strong><em>since previous revision</em></div>
                    <div><small>Largest increase</small><strong>{storageIntelligence.largestIncrease ? `+${formatStorageValue(storageIntelligence.largestIncrease.difference, "SOH")}` : "—"}</strong><em>{storageIntelligence.largestIncrease?.product || "No increase"}</em></div>
                    <div><small>Largest decrease</small><strong>{storageIntelligence.largestDecrease ? formatStorageValue(storageIntelligence.largestDecrease.difference, "SOH") : "—"}</strong><em>{storageIntelligence.largestDecrease?.product || "No decrease"}</em></div>
                  </div>
                </section>

                <section className="mc-storageInsightCard mc-storageInsightCard--highlights">
                  <span>Highlights</span>
                  <ul>
                    {storageIntelligence.highlights.map((highlight, index) => <li key={`${highlight}-${index}`}>{highlight}</li>)}
                  </ul>
                </section>
              </div>
            </>
          ) : (
            <div className="mc-emptyState"><strong>No storage intelligence yet.</strong><p>The first successful inventory import will create the baseline summary.</p></div>
          )}
        </article>

        <article className="mc-enginePanel mc-storagePatternsPanel">
          <div className="mc-enginePanel__header">
            <div>
              <span className="mc-intakeLabel">Movement patterns</span>
              <h3>What is repeating across revisions?</h3>
              <p>Deterministic patterns across the latest {storagePatterns.windowSize || 0} site revisions. Storage describes the pattern; Transaction Integrity will evaluate it.</p>
            </div>
            <div className="mc-engineBadge">Last {storagePatterns.windowSize || 0}</div>
          </div>
          <div className="mc-storagePatternGrid">
            {[
              { key: "repeatedDecrease", label: "Repeated decreases", count: storagePatterns.repeatedDecrease.length, hint: "Consecutive downward stock movement" },
              { key: "repeatedIncrease", label: "Repeated increases", count: storagePatterns.repeatedIncrease.length, hint: "Consecutive upward stock movement" },
              { key: "volatile", label: "Volatile products", count: storagePatterns.volatile.length, hint: "Direction changed repeatedly" },
              { key: "stable", label: "Stable products", count: storagePatterns.stableCount, hint: "No detected movement in this window" },
              { key: "recurringRemoval", label: "Recurring removals", count: storagePatterns.recurringRemoval.length, hint: "Removed repeatedly or reintroduced" },
            ].map((pattern) => (
              <button key={pattern.key} type="button" className={`mc-storagePatternCard mc-storagePatternCard--${pattern.key}`} onClick={() => pattern.key !== "stable" && setSelectedStoragePattern(pattern.key)} disabled={pattern.key !== "stable" && pattern.count === 0}>
                <span>{pattern.label}</span><strong>{pattern.count}</strong><em>{pattern.hint}</em>
              </button>
            ))}
          </div>
        </article>

        <article className="mc-enginePanel mc-deadStockPanel">
          <div className="mc-enginePanel__header">
            <div>
              <span className="mc-intakeLabel">Dead stock intelligence</span>
              <h3>What inventory has stopped moving?</h3>
              <p>Products still in stock with no recorded stock change for at least 7 days at this site.</p>
            </div>
            <div className="mc-deadStockPdfActions">
              <div className="mc-engineBadge">7+ days</div>
              <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={exportDeadStock}
                disabled={deadStockPdfBusy || storageLedgerLoading || storageRevisionProcessing || !deadStock.hasWindow || activeStoreScope === 'ALL'}
                title={activeStoreScope === 'ALL' ? 'Select a site to export its dead stock.' : !deadStock.hasWindow ? 'Wait for a checked stock update and 7 days of history.' : 'Export every dead-stock product at this site, highest cost first.'}>
                {deadStockPdfBusy ? 'Creating PDF…' : 'Export dead stock PDF'}
              </button>
            </div>
          </div>

          {deadStockPdfError ? <p className="mc-deadStockPdfError" role="alert">{deadStockPdfError}</p> : null}

          {distributionProgress?.status === "running" ? (
            <div className="mc-deadStockEmpty">
              <strong>Inventory distribution is in progress</strong>
              <span>Dead-stock details will be available when distribution finishes.</span>
            </div>
          ) : !deadStock.hasWindow ? (
            <div className="mc-deadStockEmpty">
              <strong>{deadStock.latestRevisionTrusted ? 'Collecting stock history' : 'Checking the latest stock update'}</strong>
              <span>{deadStock.latestRevisionTrusted ? `We have ${deadStock.coverageDays} day${deadStock.coverageDays === 1 ? '' : 's'} of stock history. We need 7 days to identify products that have stopped moving.` : 'Dead stock will appear after the latest stock update has been checked.'}</span>
            </div>
          ) : (
            <>
              <div className="mc-deadStockMetrics">
                <div><span>Dead products</span><strong>{deadStock.count}</strong><small>unchanged for 7+ days</small></div>
                <div><span>Units sitting</span><strong>{formatStorageValue(deadStock.totalUnits, 'SOH')}</strong><small>stock still at this site</small></div>
                <div><span>{deadStock.missingCostCount ? 'Known capital tied up' : 'Capital tied up'}</span><strong>{deadStock.count && !deadStock.valuedCount ? 'Cost unavailable' : `R${deadStock.tiedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</strong><small>{deadStock.missingCostCount ? `${deadStock.missingCostCount} product${deadStock.missingCostCount === 1 ? '' : 's'} excluded: cost unavailable` : 'SOH × current unit cost'}</small></div>
              </div>

              {deadStock.products.length ? (
                <div className="mc-tableShell mc-tableShell--deadStock">
                  <table className="mc-table mc-table--deadStock">
                    <thead><tr><th>Product</th><th>SKU</th><th>Stock now</th><th>Days without change</th><th>Last stock change</th><th>Unit cost</th><th>Capital tied up</th><th>Network</th></tr></thead>
                    <tbody>{deadStock.products.map((item, rowIndex) => (
                      <tr key={`${item.key}::${rowIndex}`}>
                        <td className="mc-deadStockProductCell"><strong className="mc-deadStockProductName">{item.product}</strong>{item.category ? <small>{item.category}</small> : null}</td>
                        <td>{item.sku || '—'}</td>
                        <td><strong>{formatStorageValue(item.soh, 'SOH')}</strong></td>
                        <td><span className="mc-deadStockAge">{item.daysStatic} days</span></td>
                        <td>{item.lastMovementAt ? <>{formatUploadTimestamp(item.lastMovementAt)}{item.lastMovementRevision ? <small>R{item.lastMovementRevision}</small> : null}</> : <><span>No movement in tracked window</span>{item.observedSinceAt ? <small>Observed since {formatUploadTimestamp(item.observedSinceAt)}</small> : null}</>}</td>
                        <td>{item.costKnown ? `R${item.cost.toFixed(2)}` : '—'}</td>
                        <td><strong className="mc-deadStockValue">{item.costKnown ? `R${item.tiedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Cost unavailable'}</strong></td>
                        <td><button type="button" className="mc-networkMovementBtn" onClick={() => openNetworkMovement(item)}>View network movement</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : (
                <div className="mc-deadStockEmpty mc-deadStockEmpty--clear">
                  <strong>No dead stock detected</strong>
                  <span>Every product with positive SOH has moved within the latest 7-day evidence window.</span>
                </div>
              )}
            </>
          )}
        </article>

        <article className="mc-enginePanel mc-storageHistoryPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Storage Movement History</h3>
              <p>Latest storage revisions for this site. The full immutable ledger remains preserved by the Storage Engine.</p>
            </div>
            <div className="mc-engineBadge">Latest {Math.min(storageMovements.length, 3)} of {storageMovements.length}</div>
          </div>
          {storageMovements.length ? (
            <div className="mc-tableShell mc-tableShell--storageHistory">
              <table className="mc-table mc-table--storageHistory">
                <thead><tr><th>Date</th><th>Movement</th><th>Revision</th><th>Products</th><th>Classification</th><th>Readiness</th><th>Source</th><th>User</th><th /></tr></thead>
                <tbody>{storageMovements.slice(0, 3).map((movement) => {
                  const delta = movement.delta || {};
                  const hasDetails = STORAGE_CHANGE_GROUPS.some((group) => storageClassificationCount(delta, group.key) > 0);
                  return (
                    <tr key={movement.id}>
                      <td>{formatUploadTimestamp(movement.timestamp)}</td>
                      <td><span className="mc-storageMovementType">{movement.movementLabel || "Inventory Import"}</span></td>
                      <td><strong>R{movement.revision || 1}</strong></td>
                      <td>{movement.productCount || 0}</td>
                      <td>
                        {delta.initial && Number(movement.revision || 1) <= 1 ? (
                          <span className="mc-storageDeltaSummary">Initial snapshot · {movement.productCount || 0} products</span>
                        ) : hasDetails ? (
                          <div className="mc-storageClassificationRow">
                            {STORAGE_CHANGE_GROUPS.map((group) => {
                              const count = storageClassificationCount(delta, group.key);
                              return count ? <span key={group.key} className={`mc-storageClassChip mc-storageClassChip--${group.key}`}>{count} {group.short}</span> : null;
                            })}
                          </div>
                        ) : (
                          <span className="mc-storageDeltaSummary">No inventory changes</span>
                        )}
                      </td>
                      <td><span className={`mc-storageHistoryReadiness mc-storageHistoryReadiness--${storageReadinessView(movement).tone}`}>{storageReadinessView(movement).label}</span></td>
                      <td>{movement.source || "—"}</td>
                      <td>{movement.createdBy || "Morpheus operator"}</td>
                      <td>
                        {!(delta.initial && Number(movement.revision || 1) <= 1) && hasDetails ? (
                          <button type="button" className="mc-engineBtn mc-engineBtn--ghost mc-storageViewBtn" onClick={() => setSelectedStorageMovement(movement)}>
                            View changes
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          ) : (
            <div className="mc-emptyState"><strong>No storage movements yet.</strong><p>The next successful CSV import will create Revision 1.</p></div>
          )}
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Inventory Preview</h3>
              <p>Confirm the active inventory snapshot before moving into Inventory Review.</p>
            </div>
            <div className="mc-engineBadge">Review</div>
          </div>

          {hasData ? (
            <>
              <div className="mc-reviewBanner">
                <div>
                  <strong>Inventory received successfully.</strong>
                  <p>The snapshot is active. Review a sample below, then continue into Inventory Review or prepare a count.</p>
                </div>
              </div>

              <div className="mc-tableShell mc-tableShell--preview">
                <table className="mc-table mc-table--preview">
                  <thead>
                    <tr>
                      {OPERATIONAL_COLUMNS.map((column) => (
                        <th key={column.label}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={`${row?.sku || "row"}-${i}`}>
                        {OPERATIONAL_COLUMNS.map((column) => (
                          <td key={column.label}>{resolveOperationalValue(row, column)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </>
          ) : (
            <div className="mc-emptyState">
              <strong>{isPosMode ? "No POS inventory synced yet." : "No CSV loaded yet."}</strong>
              <p>{isPosMode ? "Sync latest SOH first, then the operational preview will appear here." : "Upload a CSV first and the operational preview will appear here."}</p>
            </div>
          )}
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header mc-enginePanel__header--toggle">
            <div>
              <h3>{isPosMode ? "Normalized POS Data (Advanced)" : "Full CSV Data (Advanced)"}</h3>
              <p>{isPosMode ? "Inspect the normalized POS rows that will feed Prepared Data." : "Open the raw grid only when you want to inspect the full import in more detail."}</p>
            </div>
            <button
              type="button"
              className="mc-engineBadge mc-engineBadge--button"
              onClick={() => setShowAdvancedCsv((prev) => !prev)}
            >
              {showAdvancedCsv ? (isPosMode ? "Hide POS Data" : "Hide Full CSV Data") : (isPosMode ? "View POS Data" : "View Full CSV Data")}
            </button>
          </div>

          {showAdvancedCsv ? (
            <div className="mc-advancedStack">
              <div className="mc-gridMeta">
                <span>{detectedColumns} columns detected</span>
                <span>{csvGridRows.length === rawRows.length ? `${rawRows.length} rows loaded` : `Showing ${csvGridRows.length} of ${rawRows.length} rows`}</span>
                <span>{isPosMode ? "Scroll right and down to inspect normalized POS rows" : "Scroll right and down to inspect the raw file"}</span>
              </div>

              <div className="mc-csvInspectorControls">
                <label className="mc-csvInspectorSearch">
                  <span>Search raw data</span>
                  <input
                    type="search"
                    value={csvSearchTerm}
                    onChange={(event) => setCsvSearchTerm(event.target.value)}
                    placeholder="Product, SKU, ID or any imported value"
                  />
                </label>
                <div className="mc-csvInspectorGoTo">
                  <label>
                    <span>Go to row</span>
                    <input
                      type="number"
                      min="1"
                      max={rawRows.length || 1}
                      value={csvGoToRow}
                      onChange={(event) => setCsvGoToRow(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") handleCsvGoToRow(); }}
                      placeholder={`1-${rawRows.length || 1}`}
                    />
                  </label>
                  <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={handleCsvGoToRow}>Go</button>
                </div>
              </div>

              <div className="mc-tableShell mc-tableShell--csv" ref={csvTableRef}>
                <table className="mc-table mc-table--csvGrid">
                  <thead>
                    <tr>
                      <th>#</th>
                      {headers.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvGridRows.map((row) => {
                      const sourceRowIndex = rawRows.indexOf(row);
                      return (
                      <tr key={`csv-grid-${sourceRowIndex}`} data-source-row={sourceRowIndex + 1}>
                        <td>{sourceRowIndex + 1}</td>
                        {headers.map((header) => (
                          <td key={`${header}-${sourceRowIndex}`}>{row?.[header] ?? "—"}</td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mc-textBlock">
                <label>{isPosMode ? "Normalized POS rows" : "Raw CSV text"}</label>
                <textarea
                  value={isPosMode ? JSON.stringify(posInventory.rows || [], null, 2) : engine.csvText}
                  readOnly={isPosMode}
                  onChange={(e) => updateCsvText(e.target.value, "manual_paste.csv")}
                />
              </div>
            </div>
          ) : (
            <div className="mc-collapsedHint">
              <span>The full raw CSV grid is tucked away to keep daily workflow focused.</span>
            </div>
          )}
        </article>
      </section>



      {networkMovementPanel ? (
        <div className="mc-storageCompareOverlay mc-networkMovementOverlay" role="dialog" aria-modal="true" aria-label={`Network movement for ${networkMovementPanel.item?.product || networkMovementPanel.item?.sku || 'product'}`}>
          <div className="mc-storageCompareBackdrop" onClick={closeNetworkMovement} />
          <div className="mc-storageCompareModal mc-networkMovementModal" ref={networkMovementModalRef}>
            <div className="mc-storageCompareHeader">
              <div>
                <div className="mc-enginePage__eyebrow">Network movement</div>
                <h3>{networkMovementPanel.item?.product || 'Network movement'}</h3>
                <p>SKU {networkMovementPanel.item?.sku || '—'} · Stock at {formatStoreLabel(activeStoreScope)}: {formatStorageValue(networkMovementPanel.item?.soh ?? networkMovementPanel.item?.systemSoh ?? '', 'SOH')}</p>
              </div>
              <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={closeNetworkMovement}>Close</button>
            </div>

            {networkMovementLoading ? (
              <div className="mc-networkMovementState mc-networkMovementState--loading">
                <span className="mc-networkMovementSpinner" aria-hidden="true" />
                <div><strong>Loading movement across sites…</strong><span>Checking current stock and its movement history.</span></div>
              </div>
            ) : networkMovementError ? (
              <div className="mc-networkMovementState mc-networkMovementState--error"><strong>Movement information unavailable</strong><span>Please close this view and try again.</span></div>
            ) : (
              <>
                <div className="mc-networkMovementSummary">
                  {[
                    ['MOVING', 'Moving'],
                    ['SLOW', 'Slow'],
                    ['OBSERVED', 'Building history'],
                    ['DEAD', 'Not moving'],
                    ['OUT_OF_STOCK', 'Out of stock'],
                  ].map(([signal, label]) => {
                    const count = (networkMovementPanel.rows || []).filter((row) => row.networkSignal === signal).length;
                    const active = networkMovementFilter === signal;
                    return (
                      <button
                        key={signal}
                        type="button"
                        className={`mc-networkSummaryCard mc-networkSummaryCard--filter mc-networkSignal--${signal.toLowerCase()}${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => setNetworkMovementFilter((current) => current === signal ? '' : signal)}
                        title={active ? `Show all sites` : `Show ${label.toLowerCase()} sites only`}
                      >
                        <span>{label}</span><strong>{count}</strong>
                      </button>
                    );
                  })}
                </div>

                <section id="mc-networkMovementEvidence" className="mc-networkEvidencePanel" ref={networkEvidenceRef} tabIndex={-1} aria-labelledby="mc-networkMovementEvidenceTitle">
                  <div className="mc-networkEvidenceHeader">
                    <div>
                      <h4 id="mc-networkMovementEvidenceTitle">Movement across sites</h4>
                      <p role="status" aria-live="polite">Showing {networkEvidenceRows.length} of {(networkMovementPanel.rows || []).length} sites · {networkEvidenceFilterLabel}</p>
                    </div>
                    {networkMovementFilter ? <button type="button" className="mc-networkMovementBtn" onClick={() => setNetworkMovementFilter('')}>Show all sites</button> : null}
                  </div>
                  <div className="mc-networkMovementTableShell">
                  <table className="mc-table mc-networkMovementTable">
                    <thead><tr><th scope="col">Site</th><th scope="col">Stock now</th><th scope="col">Movement</th><th scope="col">Last stock change</th></tr></thead>
                    <tbody>{networkEvidenceRows.length ? networkEvidenceRows.map((row) => (
                      <tr key={`${row.scope}-${row.sku}`}>
                        <td><strong>{row.site}</strong>{normalizeStoreValue(row.scope) === normalizeStoreValue(networkMovementPanel.sourceScope || storageAuthorityScope) ? <small>Current site</small> : null}</td>
                        <td><strong>{row.soh === '' ? '—' : formatStorageValue(row.soh, 'SOH')}</strong></td>
                        <td><span className={`mc-networkSignal mc-networkSignal--${String(row.networkSignal || 'no_evidence').toLowerCase()}`}>{movementLabel(row.networkSignal)}</span></td>
                        <td>{row.lastObservedMovement ? formatUploadTimestamp(row.lastObservedMovement) : 'No change recorded'}
                          {row.daysStatic !== '' && row.daysStatic != null ? <small>No change for {row.daysStatic} {Number(row.daysStatic) === 1 ? 'day' : 'days'}</small> : null}
                          <details className="mc-networkRowHistory"><summary>Stock history</summary><p>{row.velocity?.observations > 1 ? `${row.velocity.observations} stock checks over ${formatTransferMetric(row.velocity.observedDays)} days.` : 'More stock checks are needed.'}{row.velocity?.depletionPerDay > 0 ? ` Stock decreased by about ${formatTransferMetric(row.velocity.depletionPerDay)} units per day.` : ''}</p></details>
                        </td>
                      </tr>
                    )) : <tr><td colSpan={4}>{networkMovementFilter ? 'No sites match. Select Show all sites to see the full network.' : 'No movement history is available for this product.'}</td></tr>}</tbody>
                  </table>
                  </div>
                </section>
                <McTransferIntelligence
                  rows={networkMovementPanel.rows || []}
                  sourceScope={networkMovementPanel.sourceScope || storageAuthorityScope}
                  evaluatedAt={networkMovementPanel.summary?.generatedAt || ''}
                  orgId={getMcActiveOrgId()}
                  rulesScope={activeStoreScope}
                  canEditRules={canAccessAdminTabs(currentProfile)}
                  onShowMoving={showMovingSiteEvidence}
                  reportItem={networkMovementPanel.item}
                  reportSite={formatStoreLabel(activeStoreScope)}
                  reportLatestAt={deadStock.latestSnapshotAt}
                />
              </>
            )}
          </div>
        </div>
      ) : null}

      {selectedStorageMovement ? (
        <div className="mc-storageCompareOverlay" role="dialog" aria-modal="true" aria-label={`Revision R${selectedStorageMovement.revision} changes`}>
          <div className="mc-storageCompareBackdrop" onClick={() => setSelectedStorageMovement(null)} />
          <div className="mc-storageCompareModal">
            <div className="mc-storageCompareHeader">
              <div>
                <div className="mc-enginePage__eyebrow">Storage comparison</div>
                <h3>Revision R{selectedStorageMovement.revision} Changes</h3>
                <p>{selectedStorageMovement.source || "Inventory sync"} · {formatUploadTimestamp(selectedStorageMovement.timestamp)}</p>
              </div>
              <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={() => setSelectedStorageMovement(null)}>Close</button>
            </div>
            <div className="mc-storageCompareSummary">
              {STORAGE_CHANGE_GROUPS.map((group) => {
                const count = storageClassificationCount(selectedStorageMovement.delta, group.key);
                return <div key={group.key} className={`mc-storageSummaryCard mc-storageSummaryCard--${group.key}`}><span>{group.short}</span><strong>{count}</strong></div>;
              })}
              <div className="mc-storageSummaryCard"><span>Unchanged</span><strong>{Number(selectedStorageMovement.delta?.unchangedCount || 0)}</strong></div>
            </div>
            <div className="mc-storageCompareBody">
              {STORAGE_CHANGE_GROUPS.map((group) => {
                const rows = selectedStorageMovement.delta?.changes?.[group.key] || [];
                if (!rows.length) return null;
                const isOpen = Boolean(expandedStorageGroups[group.key]);
                return (
                  <section key={group.key} className={`mc-storageChangeGroup ${isOpen ? "is-open" : ""}`}>
                    <button type="button" className="mc-storageChangeGroup__header" onClick={() => setExpandedStorageGroups((current) => ({ ...current, [group.key]: !current[group.key] }))} aria-expanded={isOpen}>
                      <div><h4>{group.label}</h4><small>{isOpen ? "Hide products" : "Show products"}</small></div>
                      <span>{rows.length}</span><i>{isOpen ? "−" : "+"}</i>
                    </button>
                    {isOpen ? <div className="mc-storageChangeList">
                      {rows.map((item, index) => (
                        <article key={`${item.key || item.product}-${index}`} className="mc-storageChangeItem">
                          <div className="mc-storageChangeItem__identity"><strong>{item.product || "Unnamed product"}</strong>{item.sku ? <span>SKU {item.sku}</span> : null}</div>
                          <div className="mc-storageFieldList">
                            {(item.fields || []).map((field, fieldIndex) => (
                              <div key={`${field.field}-${fieldIndex}`} className="mc-storageFieldChange">
                                <span>{field.field}</span>
                                <b>{formatStorageValue(field.before, field.field)}</b>
                                <i>→</i>
                                <b>{formatStorageValue(field.after, field.field)}</b>
                                {Number.isFinite(Number(field.difference)) && Number(field.difference) !== 0 ? <em>{Number(field.difference) > 0 ? "+" : ""}{formatStorageValue(Number(field.difference), field.field)}</em> : null}
                              </div>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div> : null}
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {selectedStoragePattern ? (() => {
        const rows = storagePatterns[selectedStoragePattern] || [];
        const titles = { repeatedDecrease: "Repeated decreases", repeatedIncrease: "Repeated increases", volatile: "Volatile products", recurringRemoval: "Recurring removals" };
        return (
          <div className="mc-storageCompareOverlay" role="dialog" aria-modal="true" aria-label={titles[selectedStoragePattern] || "Movement pattern"}>
            <div className="mc-storageCompareBackdrop" onClick={() => setSelectedStoragePattern(null)} />
            <div className="mc-storageCompareModal mc-storagePatternModal">
              <div className="mc-storageCompareHeader"><div><div className="mc-enginePage__eyebrow">Movement pattern</div><h3>{titles[selectedStoragePattern]}</h3><p>Observed across the latest {storagePatterns.windowSize} revisions for this site.</p></div><button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={() => setSelectedStoragePattern(null)}>Close</button></div>
              <div className="mc-storageCompareBody">
                <div className="mc-storagePatternList">
                  {rows.map((item) => (
                    <article key={item.key} className="mc-storagePatternItem">
                      <div><strong>{item.product}</strong>{item.sku ? <span>SKU {item.sku}</span> : null}</div>
                      {selectedStoragePattern === "volatile" ? <b>{item.signChanges} direction changes</b> : null}
                      {selectedStoragePattern === "recurringRemoval" ? <b>{item.reintroduced ? "Removed and reintroduced" : `${item.removedCount} removals`}</b> : null}
                      {(selectedStoragePattern === "repeatedDecrease" || selectedStoragePattern === "repeatedIncrease") ? <b>{item.run} consecutive revisions · {item.total > 0 ? "+" : ""}{formatStorageValue(item.total, "SOH")}</b> : null}
                      {item.stock?.length ? <div className="mc-storagePatternTrail">{item.stock.map((move) => <span key={`${item.key}-${move.revision}`}>R{move.revision}: {move.before} → {move.after} ({move.difference > 0 ? "+" : ""}{formatStorageValue(move.difference, "SOH")})</span>)}</div> : null}
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })() : null}

      {prepareBusy ? (
        <div className="mc-globalPrepareOverlay" role="status" aria-live="polite" aria-label="Preparing count session">
          <div className="mc-globalPrepareOverlay__backdrop" />
          <div className="mc-globalPrepareOverlay__card">
            <div className="mc-globalPrepareOverlay__orb" />
            <div className="mc-globalPrepareOverlay__eyebrow">{prepareStage === "archiving" ? "Archiving session" : "Preparing session"}</div>
            <h3>{prepareStage === "archiving" ? "Archiving existing live count..." : "Building your live count..."}</h3>
            <p>{prepareStage === "archiving" ? "MC is safely moving the existing live session into Archives before creating the new count." : "MC is saving the latest import and opening the new count workspace."}</p>
            <div className="mc-globalPrepareOverlay__bar"><span /></div>
          </div>
        </div>
      ) : null}

      {distributionProgress ? (
        <div className="mc-distributionOverlay" role="dialog" aria-modal="true" aria-label="Inventory distribution progress">
          <div className="mc-distributionOverlay__backdrop" />
          <div className="mc-distributionOverlay__card">
            <div className="mc-enginePage__eyebrow">Global inventory distribution</div>
            <h3>{distributionProgress.status === "complete"
              ? distributionProgress.failed || distributionProgress.skipped ? "Some sites need attention" : "Distribution complete"
              : distributionProgress.status === "failed"
                ? "Distribution not confirmed"
              : distributionProgress.status === "queued"
                ? "Inventory accepted"
                : "Distributing inventory"}</h3>
            <p>{distributionProgress.status === "complete"
              ? `${distributionProgress.succeeded || 0} sites updated${distributionProgress.skipped ? ` · ${distributionProgress.skipped} skipped` : ""}${distributionProgress.failed ? ` · ${distributionProgress.failed} failed` : ""}.`
              : distributionProgress.status === "failed"
                ? distributionProgress.error || "The inventory save was not confirmed. Check your connection and try again."
              : distributionProgress.status === "queued"
                ? `${distributionProgress.succeeded + (distributionProgress.inventoryReady || 0)} of ${distributionProgress.total || 0} site inventories saved. ${distributionProgress.inventoryReady || 0} revisions continue while you use other tabs. Keep this browser tab open.`
                : `${distributionProgress.site || "Preparing"} · ${distributionProgress.current || 0} of ${distributionProgress.total || 0}`}</p>
            <div className="mc-distributionOverlay__bar"><span style={{ width: `${distributionProgress.total ? Math.round((distributionProgress.current / distributionProgress.total) * 100) : 0}%` }} /></div>
            <div className="mc-distributionOverlay__sites">
              {(distributionProgress.results || []).slice(-8).map((item) => (
                <div key={item.site} className={revisionPending(item) ? "is-pending" : item.ok ? "is-success" : item.skipped ? "is-skipped" : "is-failed"}>
                  <span>{revisionPending(item) ? "…" : item.ok ? "✓" : item.skipped ? "–" : "!"}</span>
                  <strong>{item.label || formatStoreLabel(item.site)}</strong>
                  <small>{revisionPending(item) ? "Inventory saved · revision pending" : item.status === "ready" ? "Inventory and revision saved" : item.reason || "Failed"}</small>
                </div>
              ))}
            </div>
            {["complete", "failed", "queued"].includes(distributionProgress.status) ? (
              <button type="button" className="mc-distributionOverlay__close" onClick={() => setDistributionProgress(null)}>
                {distributionProgress.status === "queued" ? "Continue working" : "Close"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {siteChoicePrompt ? (
        <div className="mc-siteChoiceOverlay" style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div className="mc-siteChoiceBackdrop" onClick={closeSiteChoicePrompt} style={{ position: "absolute", inset: 0, background: "rgba(2,6,23,.62)", backdropFilter: "blur(12px)" }} />
          <div className="mc-siteChoiceCard" style={{ position: "relative", width: "min(560px, 92vw)", borderRadius: 28, padding: 26, border: "1px solid rgba(103,232,249,.16)", background: "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.99))", boxShadow: "0 30px 120px rgba(0,0,0,.55)" }}>
            <div className="mc-enginePage__eyebrow">{siteChoicePrompt?.distribution ? "Choose distribution" : "Select site"}</div>
            <h3 className="mc-siteChoiceTitle" style={{ margin: "0 0 8px", color: "#f8fafc" }}>
              {siteChoicePrompt?.distribution ? "Where should this inventory be distributed?" : "Choose a site first."}
            </h3>
            <p className="mc-siteChoiceCopy" style={{ margin: "0 0 16px", color: "#9ab2ca" }}>
              {siteChoicePrompt?.distribution
                ? "All Sites keeps the workflow globally scoped for distribution and Inventory Review. Choose one site when this source belongs to a single operation."
                : "Select the exact site before preparing operational work. Count sessions always remain site-specific."}
            </p>
            {(siteChoicePrompt?.distribution ? distributionChoiceOptions : siteChoiceOptions).length ? (
              <>
                <select
                  value={siteChoiceValue || (siteChoicePrompt?.distribution ? "ALL" : siteChoiceOptions[0])}
                  onChange={(event) => setSiteChoiceValue(event.target.value)}
                  className="mc-siteChoiceSelect"
                  style={{ width: "100%", border: "1px solid rgba(103,232,249,.22)", background: "rgba(2,10,24,.9)", color: "#f8fafc", borderRadius: 18, padding: "14px 16px", fontWeight: 800, marginBottom: 12 }}
                >
                  {(siteChoicePrompt?.distribution ? distributionChoiceOptions : siteChoiceOptions).map((option) => (
                    <option key={option} value={option}>
                      {String(option).toUpperCase() === "ALL" ? "All Sites" : formatStoreLabel(option)}
                    </option>
                  ))}
                </select>
                {siteChoicePrompt?.distribution ? (
                  <div style={{ margin: "0 0 18px", padding: "12px 14px", borderRadius: 16, border: "1px solid rgba(103,232,249,.12)", background: "rgba(8,32,54,.62)", color: "#9fdcf3" }}>
                    <strong style={{ color: "#e6fbff" }}>Distribution:</strong>{" "}
                    {String(siteChoiceValue || "ALL").toUpperCase() === "ALL"
                      ? "All Sites · Global Review"
                      : `${formatStoreLabel(siteChoiceValue)} · Site Review`}
                  </div>
                ) : null}
              </>
            ) : (
              <p style={{ margin: "0 0 16px", color: "#fbbf24" }}>No active MC sites were found yet. Continue without choosing a site, or add sites later.</p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={closeSiteChoicePrompt}>Cancel</button>
              <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={confirmSiteChoice}>Continue</button>
            </div>
          </div>
        </div>
      ) : null}
      {cycleBlockPrompt ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={closeCycleBlockPrompt} style={{ position: "absolute", inset: 0, background: "rgba(2,6,23,.62)", backdropFilter: "blur(12px)" }} />
          <div className="mc-siteChoiceCard" style={{ position: "relative", width: "min(600px, 92vw)", borderRadius: 28, padding: 26, border: "1px solid rgba(103,232,249,.16)", background: "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.99))", boxShadow: "0 30px 120px rgba(0,0,0,.55)" }}>
            <div className="mc-enginePage__eyebrow">Full Count already active</div>
            <h3 style={{ margin: "0 0 8px", color: "#f8fafc" }}>Complete or archive the Full Count first.</h3>
            <p style={{ margin: "0 0 10px", color: "#9ab2ca" }}>
              {cycleBlockPrompt.sectionName || "This section"} cannot be prepared in {cycleBlockPrompt.cycleName || "this cycle"} because an active Full Count already includes every section.
            </p>
            <p style={{ margin: 0, color: "#9ab2ca" }}>Open the Full Count and complete it, or archive it if it was started by mistake.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
              <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={closeCycleBlockPrompt}>
                Cancel
              </button>
              <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={archiveBlockingFullCount}>
                Archive Full Count
              </button>
              <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={resumeBlockingFullCount}>
                Resume Full Count
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {overwritePrompt ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={() => setOverwritePrompt(false)} style={{ position: "absolute", inset: 0, background: "rgba(2,6,23,.62)", backdropFilter: "blur(12px)" }} />
          <div className="mc-siteChoiceCard" style={{ position: "relative", width: "min(560px, 92vw)", borderRadius: 28, padding: 26, border: "1px solid rgba(103,232,249,.16)", background: "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.99))", boxShadow: "0 30px 120px rgba(0,0,0,.55)" }}>
            <div className="mc-enginePage__eyebrow">Active count detected</div>
            <h3 style={{ margin: "0 0 8px", color: "#f8fafc" }}>This cycle already has an active count.</h3>
            <p style={{ margin: 0, color: "#9ab2ca" }}>Resume the existing count, or replace it for this cycle. Completed sections remain live until the cycle is finalized and moved to Archives.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
              <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={() => { setOverwritePrompt(false); setOverwriteMatches([]); }}>
                Cancel
              </button>
              <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={resumeExistingLiveSession}>
                Resume active count
              </button>
              <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={confirmOverwrite}>
                Replace active count
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <McPageFooter />
    </div>
  );
}
